import { Link, useNavigate } from 'react-router-dom';
import { APP_BASENAME, navigateOutsideApp, isFocusedStudio } from './appNavigation';
// src/Overview.js
import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import { useAuth } from "./AuthContext";
import { supabase } from './supabase';
import { 
  FiUpload, 
  FiFileText, 
  FiDownload, 
  FiTrash2,
  FiCheckCircle,
  FiMic,
  FiLink,
  FiMonitor,
  FiPlay,
  FiSearch,
  FiZap,
  FiLoader,
  FiAlertCircle,
  FiX
} from "react-icons/fi";
import UploadPopup from "./UploadPopup";
import AppErrorBoundary from './AppErrorBoundary';
import { useGuestCopy } from "./guestCopy";
import GuestSavePrompt from './GuestSavePrompt';
import GuestAccountDialog from './GuestAccountDialog';
import LargeFilePreparation from './LargeFilePreparation';
import {readPendingFile,retainPendingFile,clearPendingFile} from './pendingFile.mjs';
import { useConversionCopy } from './conversionCopy';
import SpeakerNamesEditor from './SpeakerNamesEditor';
import { resolvePodcast } from './podcastResolution';
import { setGuestEntryContext } from "./guestAnalytics";
import "./UploadPopup.css";
import "./TranscriptModal.css";
import { useI18n } from "./i18n";
// (no direct imports from ./lib/api here)
import CONFIG from "./config";
import { fetchWithTimeout, responseError, readJsonResponse } from './apiRequest';
import { createExportSession, isExportSessionError } from './exportSession';
import { loadCheckoutReceipt } from './checkoutReceipt';
import { observeHistoryOutcomes, observeJobOutcome, observeResultOutcome, observePendingJob, observeNewResultOutcome } from './jobObservations';
import { createImport, watchImport, listImports, acknowledgeImport, importOutcome, refreshImportOutcome } from './importTasks';
import { uploadRecovery, uploadErrorCode, normalizeUploadFailure, quotaResetLabel } from './uploadRecovery';
import { rememberQuotaBlocked, consumeQuotaRecovery } from './quotaResume';
import { importFailure, isSavedUpgrade } from './importRecovery';
import { rememberPendingUrlImport, pendingUrlImport, clearPendingUrlImport, claimPendingUrlImport } from './pendingUrlImport';
import { ResumableUpload, uniqueUploadFiles } from "./resumableUpload";
import { retryRead } from './retryRead.mjs';
import { summaryAvailability as getSummaryAvailability, showsSummaryTab } from './summaryAvailability';
import { withClientVersion } from './clientVersion';
import { createResultPrefetchQueue } from './resultPrefetch';
import { requestHistoryPage, mergeHistory, allCompletedHistory, changedHistory } from './historyPages';
import { workspaceVisible, observeWorkspaceVisibility } from './workspaceVisibility';
import { setTokenGetter } from "./lib/api";
import {
  classifyError, diagnosticErrorCode, requestMeasurements,
  resolveGoogleAnalyticsAttribution,
  getMetaBrowserAttribution,
  getRedditBrowserAttribution,
  sizeBucket,
  sizeToMb,
  rememberCheckoutUploadedRecord,
  trackEvent,
  trackDiagnostic,
  trackEventWithCta,
  trackMetaInitiateCheckout,
  trackPremiumActivated,
  trackProductIntentEvent,
  trackUpgradeClick,
} from "./analytics";
import {
  buildCheckoutAttribution,
  clearCheckoutAttribution,
  getSavedCheckoutAttribution,
  getAuthAttribution,
  storeCheckoutAttribution,
} from "./authAttribution";
import { soundCloudLinkIssue, importMeasurements, sourceFamily } from "./sourceImport";
import { emptyTranscriptHint } from './resultQuality';
import { loadSignupAttribution } from './signupAttribution';
import WorkspacePlanCard from "./WorkspacePlanCard";
import UpgradeBillingActions from "./UpgradeBillingActions";
import { BILLING_INTERVALS, getBillingPriceSummary } from "./pricing";
import {
  fileExceedsUploadLimit,
  uploadLimitLabel,
} from "./uploadLimits";
import {
  createZipBlob,
  safeBaseFilename,
  formatTranscriptionContent as formatTranscript,
  isTranscriptEmpty,
} from "./transcriptUtils";
// Single source of truth: use CONFIG.API_URL everywhere (matches lib/api)
const API_BASE = (CONFIG.API_URL ?? "").replace(/\/$/, "");
function trackImportRecovery(attempt, outcome, status) {
  if (!attempt) return;
  trackEvent(`url_import_recovery_${outcome}`, { ...attempt,
    ...(Number.isInteger(status) ? { status_code: status } : {}) });
}
function finishUploadImportRecovery(item, outcome, status) {
  trackImportRecovery(item.importRecovery, outcome, status);
  delete item.importRecovery;
}
function trackUploadCompletion(xhr, params) {
  const recovery = xhr.recoveryAnalytics();
  trackEvent('upload_complete', { ...params, ...recovery, ...xhr.transferAnalytics() });
  if (recovery.upload_recovered || recovery.upload_resumed) {
    trackEvent('upload_recovered', { ...params, ...recovery,
      operation: xhr.lastRetryStage || 'resume', error_code: xhr.lastRetryCode || 'upload_resumed' });
  }
}
const PENDING_UPGRADE_TRANSCRIPTION_KEY = "instanttranscriber_pending_upgrade_transcription";
const LONG_TRANSCRIPTION_SECONDS = 30 * 60;
const MAX_PARALLEL_LOCAL_UPLOADS = 5;
const PRODUCT_INTENT_EVENTS = {
  TRANSCRIPTION_COMPLETED: "transcription_completed",
  LIMIT_REACHED: "transcription_limit_reached",
  DAILY_LIMIT_REACHED: "daily_limit_reached",
  CHECKOUT_STARTED: "checkout_started",
  MULTIPLE_TRANSCRIPTIONS_COMPLETED: "multiple_transcriptions_completed",
  LONG_TRANSCRIPTION_COMPLETED: "long_transcription_completed",
};
const norm = (s) => (s || "").toLowerCase();
const isActiveTranscriptionStatus = (s) => (
  ["transcribing", "queued", "starting", "running", "processing"].includes(norm(s))
);
const isCompletedTranscriptionStatus = (s) => ["completed", "succeeded"].includes(norm(s));

const parseDurationSeconds = (value) => {
  if (value == null || value === "") return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  const text = String(value).trim();
  if (!text) return null;
  const numeric = Number(text);
  if (Number.isFinite(numeric)) return numeric;
  const parts = text.split(":").map((part) => Number(part));
  if (parts.length < 2 || parts.length > 3 || parts.some((part) => !Number.isFinite(part))) {
    return null;
  }
  return parts.reduce((total, part) => (total * 60) + part, 0);
};

const durationBucket = (seconds) => {
  if (!Number.isFinite(seconds)) return "unknown";
  if (seconds < 5 * 60) return "lt_5_min";
  if (seconds < 15 * 60) return "5_15_min";
  if (seconds < LONG_TRANSCRIPTION_SECONDS) return "15_30_min";
  if (seconds < 35 * 60) return "30_35_min";
  if (seconds < 60 * 60) return "35_60_min";
  return "gt_60_min";
};

const todayKey = () => new Date().toISOString().slice(0, 10);

const completedTranscriptionIds = (files) => (
  Array.from(files || [])
    .filter(file => isCompletedTranscriptionStatus(file?.status) && file.result_state === 'text' && !file.is_preview)
    .map(file => file.id)
    .filter(Boolean)
);

const isDailyFreeLimitMessage = (msg) => {
  const s = String(msg || "").toLowerCase();
  return (
    s.includes("maximum of 3 transcriptions per day") ||
    s.includes("daily free limit") ||
    /3\s+transcriptions\s+per\s+day/.test(s)
  );
};

const safeSessionGet = (key) => {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
};

const safeSessionSet = (key, value) => {
  try {
    window.sessionStorage.setItem(key, value);
  } catch {}
};

const safeSessionRemove = (key) => {
  try {
    window.sessionStorage.removeItem(key);
  } catch {}
};

const savePendingUpgradeTranscription = (pending) => {
  if (!pending?.id) return;
  safeSessionSet(PENDING_UPGRADE_TRANSCRIPTION_KEY, JSON.stringify({
    ...pending,
    saved_at: new Date().toISOString(),
  }));
};

const getPendingUpgradeTranscription = () => {
  const raw = safeSessionGet(PENDING_UPGRADE_TRANSCRIPTION_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    return parsed?.id ? parsed : null;
  } catch {
    return null;
  }
};

const clearPendingUpgradeTranscription = () => {
  safeSessionRemove(PENDING_UPGRADE_TRANSCRIPTION_KEY);
};

async function freshUploadToken() {
  let { data: { session }, error } = await supabase.auth.getSession();
  if (error || !session) throw Object.assign(new Error("Please sign in again before uploading."), { status: 401 });
  if (session.expires_at && session.expires_at * 1000 < Date.now() + 120000) {
    const refreshed = await supabase.auth.refreshSession();
    session = refreshed.data.session;
    if (refreshed.error || !session) throw Object.assign(new Error("Please sign in again before uploading."), { status: 401 });
  }
  return session.access_token;
}

function Overview() {
  const studio = isFocusedStudio();
  const { currentUser, isPremium: contextIsPremium, checkPremiumStatus, ensureSession, guestConfig, accountReady } = useAuth();
  const currentUserId = currentUser?.id;
  const navigate = useNavigate();
  const guestCopy = useGuestCopy();
  const conversionCopy = useConversionCopy();
  const isGuest = Boolean(currentUser?.is_anonymous);
  const entryHandlers = useRef({});
  const acceptedEntries = useRef(new Set());
  const { t, language: interfaceLanguage } = useI18n();
  const [isPremium, setIsPremium] = useState(false);
  const [quota, setQuota] = useState(null);
  const [quotaError, setQuotaError] = useState(false);
  const [recentFiles, setRecentFiles] = useState([]);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [historyError, setHistoryError] = useState(false);
  const [fileSearch, setFileSearch] = useState('');
  const [fileFilter, setFileFilter] = useState('all');
  const [historyNextCursor, setHistoryNextCursor] = useState(null);
  const [historyLoadingMore, setHistoryLoadingMore] = useState(false);
  const [historyQueryLoading, setHistoryQueryLoading] = useState(false);
  const [historyTotals, setHistoryTotals] = useState({ files: 0, completed: 0 });
  const historyQueryRef = useRef({ search: '', filter: 'all' });
  historyQueryRef.current = { search: fileSearch.trim(), filter: fileFilter };
  const historyPagesRef = useRef({ key: null, cursor: null, pages: 0 });
  const [checkoutPending, setCheckoutPending] = useState(false);
  const checkoutRequestRef = useRef(false);
  const [statusMap, setStatusMap] = useState({}); // id -> /status payload
  const [modalContent, setModalContent] = useState(null);
  const [modalRecovery, setModalRecovery] = useState(null);
  const [largeFile,setLargeFile]=useState(null);
  const [resumedFile,setResumedFile]=useState(null);
  const [retainingFile,setRetainingFile]=useState(false);
  const fileRetentionAttempt=useRef(null);
  const preparedResumeFile=useRef(null);
  const [guestAccountDialog, setGuestAccountDialog] = useState(null);
  const [modalUpgradeSource, setModalUpgradeSource] = useState(null);
  const [modalUpgradeRecord, setModalUpgradeRecord] = useState(null);
  const [modalTitle, setModalTitleValue] = useState("");
  const setModalTitle = useCallback(title => { setModalRecovery(null); setModalUpgradeSource(null); setModalUpgradeRecord(null); setLargeFile(null); fileRetentionAttempt.current=null;setModalTitleValue(title); }, []);
  const [showModal, setShowModal] = useState(false);
  useEffect(()=>{if(!showModal)fileRetentionAttempt.current=null;},[showModal]);
  const [modalShowUpgradeCTA, setModalShowUpgradeCTA] = useState(false);
  const isErrorModal = Boolean(modalRecovery) || modalShowUpgradeCTA || /error|fehler|erreur/i.test(modalTitle)
    || ['Upgrade required', t('overview.copyErrorTitle'), t('overview.exportError'),
      t('overview.uploadError'), t('overview.upgradeRequired')].includes(modalTitle);
  const [toastMessage, setToastMessage] = useState("");
  const [showToast, setShowToast] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [billingInterval, setBillingInterval] = useState(BILLING_INTERVALS.MONTHLY);
  const dragDepthRef = useRef(0); // global DnD depth counter for overlay
  
  // upgrade nudges
  const isFree = !isPremium;
  const nearFreeLimit = isFree && quota && typeof quota.limit === "number" && (quota.used >= Math.max(0, quota.limit - 1));
  const atFreeLimit   = isFree && quota && typeof quota.limit === "number" && (quota.used >= quota.limit);
  
  // Speaker display states
  const [currentTranscriptionData, setCurrentTranscriptionData] = useState(null);
  
  // Persistent preferences for the session
  const [globalShowSpeakers, setGlobalShowSpeakers] = useState(true);
  const [globalShowTime, setGlobalShowTime] = useState(false);
  
  // Current modal states (derived from global preferences)
  const [showSpeakers, setShowSpeakers] = useState(true);
  const [showTime, setShowTime] = useState(false);
  const [useSpeakerPostprocessing, setUseSpeakerPostprocessing] = useState(true);
  const [activeModalTab, setActiveModalTab] = useState("transcript");

  // NEW: dropdown state
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);
  const [menuPlacement, setMenuPlacement] = useState("down"); // "down" | "up"
  const [downloadMenuMaxHeight, setDownloadMenuMaxHeight] = useState(320);
  const [downloadMenuPosition, setDownloadMenuPosition] = useState({ top: 0, left: 0 });
  const [showBulkDownloadModal, setShowBulkDownloadModal] = useState(false);
  const [bulkFormat, setBulkFormat] = useState("txt");
  const [bulkShowSpeakers, setBulkShowSpeakers] = useState(true);
  const [bulkShowTime, setBulkShowTime] = useState(false);
  const [bulkUsePostprocessing, setBulkUsePostprocessing] = useState(true);
  const [bulkIncludeSummaries, setBulkIncludeSummaries] = useState(false);
  const [bulkDownloading, setBulkDownloading] = useState(false);
  const [bulkProgress, setBulkProgress] = useState("");
  const [bulkDownloadError, setBulkDownloadError] = useState("");

  // Cache results once a file completes so opening the modal is instant
  const [resultCache, setResultCache] = useState({}); // { [id]: resultJson }
  const [currentRecordId, setCurrentRecordId] = useState(null);
  const currentRecordIdRef = useRef(null);
  currentRecordIdRef.current = currentRecordId;
  const resultCacheRef = useRef({}); // mirror of resultCache for stable callbacks
  const resultPrefetchRef = useRef(createResultPrefetchQueue());
  const resultPrefetchRunRef = useRef(null);
  const resultRevisionsRef = useRef({});
  const modalViewRef = useRef({});
  modalViewRef.current = { showSpeakers, showTime, useSpeakerPostprocessing, activeModalTab };
  const resultRequestsRef = useRef({});
  const bulkSessionRef = useRef(null);
  const resultUserRef = useRef(currentUser);
  resultUserRef.current = currentUser;
  useEffect(() => () => bulkSessionRef.current?.cancel(), [currentUserId]);
  const historyFilesRef = useRef(recentFiles);
  historyFilesRef.current = recentFiles;
  const metadataAbortRef = useRef(new AbortController());
  const metadataOwnerRef = useRef(null);
  const importObserverRef = useRef(null);
  const importActiveRef = useRef(false);
  const importHandlersRef = useRef({});
  const resultAbortRef = useRef({}); // { [id]: AbortController }
  const completionTrackedRef = useRef(new Set());
  const statusRequestRef = useRef(false);
  const pollFailuresRef = useRef(new Map());
  const downloadMenuRef = useRef(null);
  const downloadTriggerRef = useRef(null);

  // top-level start guards to prevent double auto-start
  const startGuardsRef = useRef(new Set());

  const historyRequestRef = useRef(null);
  const quotaRequestRef = useRef(null);
  const statusIntervalRef = useRef(null);

  // --- upload progress state ---
  const [isUploading, setIsUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [uploadPreparation, setUploadPreparation] = useState(null);
  const [uploadConnection, setUploadConnection] = useState(null);
  // const [uploadBytes, setUploadBytes] = useState({ loaded: 0, total: 0 }); // Moved to popup
  const uploadXhrRef = useRef(null);
  const uploadXhrRefs = useRef(new Map());
  const uploadQueueRef = useRef([]);
  const activeUploadCountRef = useRef(0);
  const uploadRunIdRef = useRef(0);
  const uploadStartingRef = useRef(false);
  
  // --- upload popup state ---
  const [showUploadPopup, setShowUploadPopup] = useState(false);
  const [currentUploadFile, setCurrentUploadFile] = useState(null);
  const [currentJobId, setCurrentJobId] = useState(null);
  const [autoStartRecording, setAutoStartRecording] = useState(false);
  const [uploadItems, setUploadItems] = useState([]);
  const uploadItemsRef = useRef([]);
  const uploadCancelToastRef = useRef(false);
  const upgradeModalShownRef = useRef(false);
  const checkoutActivationTrackedRef = useRef(false);
  const pendingUpgradeStartAttemptedRef = useRef(new Set());
  const urlImportIntentTrackedRef = useRef(false);
  const urlInputMethodRef = useRef(null);
  const [startingIds, setStartingIds] = useState(() => new Set());

  useEffect(()=>{
    let active=true;
    readPendingFile().then(file=>{if(active&&file){setResumedFile(file);trackEvent('large_file_restored',{operation:'upload_resume',event_source:'upgrade'});}});
    return()=>{active=false;};
  },[]);
  useEffect(()=>{
    const used=preparedResumeFile.current||resumedFile;
    if(used&&uploadItems.some(item=>item.file===used&&item.jobId)){
      clearPendingFile();setResumedFile(null);preparedResumeFile.current=null;
    }
  },[uploadItems,resumedFile]);

  // --- recording state ---
  const [isRecording, setIsRecording] = useState(false);
  const [recordingMode, setRecordingMode] = useState('upload'); // 'upload' | 'recording'
  // NEW: which recording input to use in 'recording' mode ('mic' | 'tab' | 'tab-mic')
  const [recordingInput, setRecordingInput] = useState('mic');

  useEffect(() => {
    uploadItemsRef.current = uploadItems;
  }, [uploadItems]);

  useEffect(() => {
    if (recordingMode === "recording") return;
    const uploading = uploadItems.some(item => item.status === "queued" || item.status === "uploading");
    if (uploading !== isUploading) setIsUploading(uploading);
  }, [uploadItems, recordingMode, isUploading]);

  // Handle done callback from podcast import (simple function to avoid early hook deps)
  const handlePodcastDone = async (recordId,details={}) => {
    resetUploadPopupState();
    await fetchHistory();
    if (details.completed && recordId) await handleFileClick(recordId,t('import.importedFile'));
  };

  // --- URL import state ---
  const [urlInput, setUrlInput] = useState("");
  const [urlRestored, setUrlRestored] = useState(false);
  const restoredUrlRef = useRef(null);
  const upgradeUrlImportHandler = useRef(null);
  const [showStudioUrl, setShowStudioUrl] = useState(() => studio && new URLSearchParams(window.location.search).get('intent') === 'url_import');
  const [isImportingUrl, setIsImportingUrl] = useState(false);
  const [importTask, setImportTask] = useState(null);
  const importTaskRef = useRef(null);
  importTaskRef.current = importTask;
  const [urlImportError, setUrlImportError] = useState('');
  const [urlRecovery, setUrlRecovery] = useState(null);
  const urlRecoveryAttemptRef = useRef(null);
  const urlFieldRef = useRef(null);
  const urlPanelRef = useRef(null);
  const urlRecoveryRef = useRef(null);
  const [entryAttribution] = useState(() => getAuthAttribution());
  const isSoundCloudEntry = String(entryAttribution.source_intent || entryAttribution.source || '').includes('soundcloud');

  useEffect(() => {
    if ((!isImportingUrl && !urlImportError) || showModal || showUploadPopup) return;
    const target = urlImportError ? urlRecoveryRef.current : urlPanelRef.current;
    target?.scrollIntoView({ block: 'start', behavior: 'auto' });
    if (urlImportError) target?.focus({ preventScroll: true });
  }, [isImportingUrl, urlImportError, showModal, showUploadPopup, showStudioUrl]);

  useEffect(() => {
    if (new URLSearchParams(window.location.search).get('intent') !== 'url_import') return;
    urlFieldRef.current?.focus({ preventScroll: true });
    urlFieldRef.current?.scrollIntoView({ block: 'center' });
    trackEvent('source_import_entry', { source_intent: entryAttribution.source_intent, event_source: 'source_landing' });
  }, [entryAttribution]);

  // --- queue a start when the job id isn't known yet ---
  const [pendingStartOptions, setPendingStartOptions] = useState(null);
  const pendingStartOptionsRef = useRef(null);
  useEffect(() => { pendingStartOptionsRef.current = pendingStartOptions; }, [pendingStartOptions]);

  // ---------- helpers ----------
  const shouldOfferUpgrade = useCallback((msg) => {
    // Only offer upgrade to Free users
    if (isPremium) return false;
    const s = String(msg || "").toLowerCase();
    // Match common duration/limit messages from backend
    return (
      /duration|minutes|length|too long/.test(s) ||
      s.includes("exceeds allowed limit") ||
      s.includes("requires_upgrade") ||
      s.includes("upgrade to premium") ||
      isDailyFreeLimitMessage(s) ||
      /35\s*min/.test(s) ||
      s.includes("free plan")
    );
  }, [isPremium]);

  const planName = isPremium ? "premium" : "free";
  const isConfirmedFreePlan = !isPremium && !quotaError && quota && typeof quota.limit === "number";

  const trackFreeProductIntent = useCallback((eventName, params = {}, options = {}) => {
    const { allowUnconfirmedFree = false, ...eventOptions } = options;
    if (isPremium || (!isConfirmedFreePlan && !allowUnconfirmedFree)) {
      return { meta: false, reddit: false, gated: true };
    }
    return trackProductIntentEvent(
      eventName,
      {
        plan: planName,
        ...params,
      },
      {
        userId: currentUser?.id,
        ...eventOptions,
      }
    );
  }, [currentUser?.id, isConfirmedFreePlan, isPremium, planName]);

  const getUploadTrackingParams = (files, source) => {
    const list = Array.from(files || []).filter(Boolean);
    const sizes = list.map(f => Number(f.size) || 0);
    const totalBytes = sizes.reduce((sum, size) => sum + size, 0);
    const largestBytes = sizes.length ? Math.max(...sizes) : 0;
    return {
      source,
      plan: planName,
      file_count: list.length,
      total_size_mb: sizeToMb(totalBytes),
      largest_size_mb: sizeToMb(largestBytes),
      largest_size_bucket: sizeBucket(largestBytes),
    };
  };



  const isLikelyPublicHttpUrl = (s) => /^https?:\/\/[^ \s]+$/i.test(String(s || ""));
  const filenameFromUrl = (u) => {
    try {
      const p = new URL(u);
      const last = p.pathname.split("/").filter(Boolean).pop() || "";
      return decodeURIComponent(last || p.hostname);
    } catch { return "remote-file"; }
  };

  const trackUrlImportIntent = (inputMethod) => {
    if (urlImportIntentTrackedRef.current) return;
    urlImportIntentTrackedRef.current = true;
    urlInputMethodRef.current = inputMethod;
    trackEvent("url_import_intent", {
      source: "paste_link_form",
      input_method: inputMethod,
      plan: planName,
    });
  };

  const handleUrlInputFocus = () => {
    trackEvent("url_import_field_focus", {
      source: "paste_link_form",
      plan: planName,
    });
  };

  const handleUrlInputPaste = () => {
    urlInputMethodRef.current = "paste";
    trackUrlImportIntent("paste");
    trackEvent("url_import_paste", {
      source: "paste_link_form",
      plan: planName,
    });
  };

  const handleUrlInputChange = (e) => {
    setUrlImportError('');
    setUrlRecovery(null);
    setUrlRestored(false);
    clearPendingUrlImport();
    const nextValue = e.target.value;
    if (nextValue.trim()) {
      trackUrlImportIntent(urlInputMethodRef.current || "typing");
      if (!urlInputMethodRef.current) urlInputMethodRef.current = "typing";
    } else {
      urlInputMethodRef.current = null;
      urlImportIntentTrackedRef.current = false;
    }
    setUrlInput(nextValue);
  };


  const formatHMS = (sec) => {
    if (sec == null || isNaN(sec)) return "-";
    const s = Math.max(0, Math.floor(sec));
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const r = s % 60;
    if (h > 0) return `${h}:${String(m).padStart(2,"0")}:${String(r).padStart(2,"0")}`;
    return `${m}:${String(r).padStart(2,"0")}`;
  };



  // const formatBytes = (bytes) => {
  //   if (bytes == null) return "-";
  //   const units = ["B","KB","MB","GB","TB"];
  //   let i = 0, v = bytes;
  //   while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  //   const places = v < 10 && i > 0 ? 1 : 0;
  //   return `${v.toFixed(places)} ${units[i]}`;
  // };

  const resetUploadState = useCallback(() => {
    setIsUploading(false);
    setUploadPct(0);
    setUploadPreparation(null);
    setUploadConnection(null);
    // setUploadBytes({ loaded: 0, total: 0 }); // Moved to popup
    uploadXhrRef.current = null;
    uploadXhrRefs.current.clear();
    uploadQueueRef.current = [];
    activeUploadCountRef.current = 0;
    uploadRunIdRef.current += 1;
  }, []);
  
  const resetUploadPopupState = useCallback(() => {
    setShowUploadPopup(false);
    setCurrentUploadFile(null);
    setCurrentJobId(null);
    setUploadItems([]);
    uploadItemsRef.current = [];
    resetUploadState();
  }, [resetUploadState]);

  // Format transcription content based on display preferences
  const getSpeakerPostprocessingMeta = useCallback((data) => (
    data?.speaker_postprocessing ||
    data?.full?.diarization?.high_quality_speaker_annotation ||
    null
  ), []);

  const isSpeakerPostprocessingRunning = useCallback((data) => {
    const meta = getSpeakerPostprocessingMeta(data);
    return meta?.status === "processing";
  }, [getSpeakerPostprocessingMeta]);

  const hasSpeakerPostprocessingResult = (data) => (
    !!data?.speaker_postprocessing_available &&
    !!(data?.light_segments_original?.length || data?.full?.speaker_postprocessing_original_segments?.length)
  );

  const getTranscriptSummary = useCallback((data) => {
    const summary =
      data?.transcript_summary ||
      data?.full?.diarization?.high_quality_speaker_annotation?.transcript_summary ||
      null;
    if (!summary || typeof summary !== "object") return null;

    const shortSummary = String(summary.short_summary || "").trim();
    const longSummary = String(summary.summary || "").trim();
    return {
      short_summary: shortSummary && shortSummary.length <= 60 ? shortSummary : "",
      summary: longSummary
    };
  }, []);

  const hasTranscriptSummary = useCallback((data) => (
    !!getTranscriptSummary(data)?.summary
  ), [getTranscriptSummary]);

  const trackTranscriptOpened = useCallback((source, data, extra = {}) => {
    observeResultOutcome(data, currentUser?.id, source);
    const summaryAvailability = getSummaryAvailability(data);
    const params = {
      source,
      has_speakers: Boolean(data?.has_diarization),
      has_timestamps: Boolean(data?.has_timestamps),
      has_summary: summaryAvailability === "ready",
      summary_availability: summaryAvailability,
      ...extra,
    };

    trackEvent("transcript_opened", params);
    trackEvent(`transcript_open_summary_${summaryAvailability}`, params);
  }, [currentUser?.id]);

  const formatSummaryContent = useCallback((data) => {
    const summary = getTranscriptSummary(data);
    if (summary?.summary) {
      const parts = [];
      if (summary.short_summary) {
        parts.push(`${t("overview.shortSummary")}:\n${summary.short_summary}`);
      }
      parts.push(`${t("overview.summaryTab")}:\n${summary.summary}`);
      return parts.join("\n\n");
    }
    const meta =
      data?.speaker_postprocessing ||
      data?.full?.diarization?.high_quality_speaker_annotation ||
      null;
    if (meta?.status === "processing") {
      return t(meta.retry_count ? "overview.speakerReviewRetrying" : "overview.summaryPending");
    }
    if (meta?.status === 'llm_failed') return t('overview.speakerReviewFailed');
    return t("overview.noSummary");
  }, [getTranscriptSummary, t]);

  const formatTranscriptionContent = useCallback((data, withSpeakers = false, withTime = false, usePostprocessing = true) => (
    formatTranscript(data, {
      withSpeakers, withTime, usePostprocessing,
      labels: { detectedLanguage: t("overview.detectedLanguage"), noTranscription: t("overview.noTranscription") },
    })
  ), [t]);

  // keep ref mirror in sync so prefetchResult can be stable ([] deps)
  useEffect(() => {
    resultCacheRef.current = resultCache;
  }, [resultCache]);

  // Click-outside handler for dropdown menu
  useEffect(() => {
    const onDocClick = (e) => {
      if (!downloadMenuRef.current) return;
      // Guard against closing when clicking the trigger button (using ref for safety)
      if (downloadTriggerRef.current && downloadTriggerRef.current.contains(e.target)) return;
      if (!downloadMenuRef.current.contains(e.target)) {
        setShowDownloadMenu(false);
      }
    };
    if (showDownloadMenu) document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [showDownloadMenu]);

  // Close modal/menu on Escape
  useEffect(() => {
    const onKey = (e) => {
      if (document.querySelector('.guest-account-dialog[open]')) return;
      if (e.key === "Escape") {
        if (showDownloadMenu) setShowDownloadMenu(false);
        if (showModal) { setShowModal(false); setModalShowUpgradeCTA(false); }
        if (showBulkDownloadModal) setShowBulkDownloadModal(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showDownloadMenu, showModal, showBulkDownloadModal]);

  // Opening and prefetch share one request. Known empty results are terminal.
  const readResult = useCallback((recordId, token, purpose = 'open') => {
    if (resultCacheRef.current[recordId]) return Promise.resolve(resultCacheRef.current[recordId]);
    if (resultRequestsRef.current[recordId]) return resultRequestsRef.current[recordId];
    const ctl = new AbortController();
    resultAbortRef.current[recordId] = ctl;
    const owner = resultUserRef.current?.id;
    const request = (async () => {
      try {
        const res = await fetchWithTimeout(`${API_BASE}/result/${recordId}?full=0`, {
          headers: { Authorization: `Bearer ${token}` }, signal: ctl.signal, requestPurpose: purpose,
        });
        if (!res.ok) throw responseError(res, await readJsonResponse(res), 'Result unavailable');
        const json = await res.json();
        const file = historyFilesRef.current.find(item => item.id === recordId);
        const data = { ...file, ...json, id: recordId };
        if (owner !== resultUserRef.current?.id || ctl.signal.aborted) return null;
        resultCacheRef.current[recordId] = data;
        setResultCache(prev => ({ ...prev, [recordId]: data }));
        observeNewResultOutcome(data, owner, `result_${purpose}`);
        if (isCompletedTranscriptionStatus(data.status) && ['text','empty'].includes(data.result_state)) {
          setRecentFiles(prev => prev.map(item => item.id === recordId && item.result_state !== data.result_state
            ? { ...item, result_state: data.result_state } : item));
        }
        return data;
      } finally {
        if (resultAbortRef.current[recordId] === ctl) delete resultAbortRef.current[recordId];
        if (resultRequestsRef.current[recordId] === request) delete resultRequestsRef.current[recordId];
      }
    })();
    resultRequestsRef.current[recordId] = request;
    return request;
  }, []);

  const prefetchResult = useCallback(async (recordId, token) => {
    if (!recordId || resultCacheRef.current[recordId]) return;
    try { await readResult(recordId, token, 'prefetch'); }
    catch (error) { trackDiagnostic('result_prefetch_error', error, { operation: 'result' }); }
  }, [readResult]);

  const prefetchNewResults = useCallback(async () => {
    const queue = resultPrefetchRef.current;
    if (resultPrefetchRunRef.current || !workspaceVisible()) return;
    resultPrefetchRunRef.current = queue;
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token || session.user?.id !== resultUserRef.current?.id) return;
      while (resultPrefetchRunRef.current === queue && resultPrefetchRef.current === queue && workspaceVisible()) {
        const id = queue.next(resultCacheRef.current);
        if (!id) break;
        await prefetchResult(id, session.access_token);
      }
    } finally {
      if (resultPrefetchRunRef.current === queue) resultPrefetchRunRef.current = null;
    }
  }, [prefetchResult]);

  // ---- status helpers (normalize + monotonic display) ----
  const stage = (s) => {
    switch (norm(s)) {
      case "requires_upgrade": return 1;
      case "uploaded": return 1;
      case "transcribing":
      case "queued":
      case "starting": return 2;
      case "running":
      case "processing": return 3;
      case "succeeded":
      case "completed": return 4;
      default: return 0;
    }
  };

  const bestStatus = (file, live) => {
    // A full run after a preview starts a new lifecycle for the same recording.
    if (live && Boolean(file?.is_preview) !== Boolean(live.is_preview)) return norm(file?.status);
    if (file?.is_preview && live?.status === "requires_upgrade") return "requires_upgrade";
    const fs = norm(file?.status);
    const ls = norm(live?.status);
    // pick the more advanced stage; tie-breaker prefers live
    if (stage(ls) > stage(fs)) return ls;
    return fs;
  };

  // Helper: is a file completed based on live or stored status (allow viewing on 'succeeded')
  const isCompletedNow = (file, live) => {
    const s = norm(bestStatus(file, live));
    return s === "completed" || s === "succeeded";
  };

  // Helper: normalize a status label for UI
  const labelFromStatuses = (file, live) => {
    const s = norm(bestStatus(file, live));
    if (typeof s === "string" && s.startsWith("error")) return "error";
    if (s === "completed" || s === "succeeded") return "completed";
    if (s === "running" || s === "processing") return "processing";
    if (s === "queued" || s === "transcribing" || s === "starting") return "queued";
    if (s === "requires_upgrade") return "requires_upgrade";
    if (s === "uploaded") return "uploaded";
    return "unknown";
  };

  // Quick helper to toast
  const toast = useCallback((msg, ms = 3000) => { setToastMessage(msg); setShowToast(true); setTimeout(() => setShowToast(false), ms); }, []);

  // Robust clipboard copy with fallback
  const copyToClipboard = useCallback(async (text) => {
    try {
      await navigator.clipboard.writeText(text ?? "");
      trackEvent("transcript_copy", { content_type: activeModalTab });
      toast(t("overview.copied"), 3000);
    } catch (_) {
      try {
        const ta = document.createElement("textarea");
        ta.value = text ?? "";
        ta.style.position = "fixed";
        ta.style.left = "-9999px";
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        const ok = document.execCommand("copy");
        document.body.removeChild(ta);
        if (ok) {
          trackEvent("transcript_copy", { content_type: activeModalTab, fallback: true });
          toast(t("overview.copied"), 3000);
        }
        else throw new Error("execCommand copy failed");
      } catch {
        trackEvent("transcript_copy_error", { content_type: activeModalTab });
        setModalTitle(t("overview.copyErrorTitle"));
        setModalContent(t("overview.copyErrorBody"));
        setModalShowUpgradeCTA(false);
        setShowModal(true);
      }
    }
  }, [activeModalTab, toast, t, setModalTitle]);

  // NEW: small util to save a Blob
  const saveBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const baseFrom = (name) => (name || "transcript").replace(/\.[^/.]+$/, "");

  const uniqueZipName = (usedNames, desiredName) => {
    const cleaned = String(desiredName || "transcript.txt").replace(/^\/+/, "");
    if (!usedNames.has(cleaned)) {
      usedNames.add(cleaned);
      return cleaned;
    }

    const dot = cleaned.lastIndexOf(".");
    const base = dot > 0 ? cleaned.slice(0, dot) : cleaned;
    const ext = dot > 0 ? cleaned.slice(dot) : "";
    let index = 2;
    let candidate = `${base}-${index}${ext}`;
    while (usedNames.has(candidate)) {
      index += 1;
      candidate = `${base}-${index}${ext}`;
    }
    usedNames.add(candidate);
    return candidate;
  };

  // Inline styles for dropdown menu
  const menuItemStyle = {
    display: "block",
    width: "100%",
    textAlign: "left",
    background: "transparent",
    border: "none",
    padding: "10px 12px",
    cursor: "pointer",
    fontSize: 14
  };

  const menuDividerStyle = { height: 1, background: "#e5e7eb", margin: "4px 6px" };

  // NEW: unified download handler
  const handleDownload = async (kind) => {
    const contentType = activeModalTab === "summary" ? "summary" : "transcript";
    const summaryAvailability = getSummaryAvailability(currentTranscriptionData);

    try {
      if (!currentRecordId) throw new Error("No record selected");
      trackEvent("download_attempt", {
        format: kind,
        content_type: contentType,
        summary_availability: summaryAvailability,
        speakers_enabled: showSpeakers,
        timestamps_enabled: showTime,
      });
      trackEvent(`${contentType}_download_attempt`, {
        format: kind,
        summary_availability: summaryAvailability,
        speakers_enabled: showSpeakers,
        timestamps_enabled: showTime,
      });

      // TXT is still generated client-side from the current modal text
      if (kind === "txt") {
        const file = new Blob([modalContent || ""], { type: "text/plain" });
        const suffix = contentType === "summary" ? "summary" : "transcript";
        saveBlob(file, `${baseFrom(modalTitle)}_${suffix}.txt`);
        setShowDownloadMenu(false);
        trackEvent("file_download", {
          format: "txt",
          content_type: contentType,
          summary_availability: summaryAvailability,
          client_generated: true,
        });
        trackEvent(`${contentType}_download`, {
          format: "txt",
          summary_availability: summaryAvailability,
          client_generated: true,
        });
        toast(t("overview.downloadingTxt"), 2500);
        return;
      }

      if (contentType === "summary") {
        throw new Error(t("overview.summaryTxtOnly"));
      }

      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) throw new Error("No active session");

      // Build the right endpoint + query
      const qs = new URLSearchParams();
      let ext = kind; // 'pdf' | 'docx' | 'srt' | 'vtt'

      if (kind === "srt" || kind === "vtt") {
        // server expects ?speaker= for subtitle formats
        qs.set("speaker", showSpeakers ? "1" : "0");
      } else {
        // server expects ?speakers=&timestamps= for docx/pdf
        qs.set("speakers", showSpeakers ? "1" : "0");
        qs.set("timestamps", showTime ? "1" : "0");
      }

      const url = `${API_BASE}/export/${currentRecordId}.${ext}?${qs.toString()}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });

      if (!res.ok) {
        let msg = `Export failed (${res.status})`;
        try {
          const j = await res.json();
          if (j?.error) msg = j.error;
        } catch {}
        throw new Error(msg);
      }

      const blob = await res.blob();
      saveBlob(blob, `${baseFrom(modalTitle)}.${ext}`);
      setShowDownloadMenu(false);
      trackEvent("file_download", {
        format: ext,
        content_type: "transcript",
        summary_availability: summaryAvailability,
        speakers_enabled: showSpeakers,
        timestamps_enabled: showTime,
      });
      trackEvent("transcript_download", {
        format: ext,
        summary_availability: summaryAvailability,
        speakers_enabled: showSpeakers,
        timestamps_enabled: showTime,
      });
      toast(t("overview.downloadingFormat", { format: ext.toUpperCase() }), 2500);
    } catch (error) {
      trackEvent("download_error", {
        format: kind,
        content_type: contentType,
        summary_availability: summaryAvailability,
        error_type: classifyError(error),
      });
      trackEvent(`${contentType}_download_error`, {
        format: kind,
        summary_availability: summaryAvailability,
        error_type: classifyError(error),
      });
      setModalTitle(t("overview.exportError"));
      setModalContent(error?.message || t("overview.exportFailed"));
      setModalShowUpgradeCTA(false);
      setShowModal(true);
    }
  };

  const getBulkResult = async (recordId, exportSession, { needsFull = false } = {}) => {
    exportSession.assertActive();
    const cached = resultCacheRef.current[recordId];
    if (cached && (!needsFull || cached.full)) return cached;

    const fullParam = needsFull ? "1" : "0";
    const response = await exportSession.request(`${API_BASE}/result/${recordId}?full=${fullParam}`, {
      requestPurpose: 'download',
      method: "GET",
    }, 60000);
    if (!response.ok) {
      let message = `Result failed (${response.status})`;
      try {
        const data = await response.json();
        if (data?.error) message = data.error;
      } catch {}
      throw new Error(message);
    }

    const data = await response.json();
    exportSession.assertActive();
    setResultCache(prev => ({ ...prev, [recordId]: data }));
    resultCacheRef.current = { ...resultCacheRef.current, [recordId]: data };
    return data;
  };

  const fetchBulkExportBlob = async (recordId, format, exportSession) => {
    const qs = new URLSearchParams();
    if (format === "srt" || format === "vtt") {
      qs.set("speaker", bulkShowSpeakers ? "1" : "0");
    } else {
      qs.set("speakers", bulkShowSpeakers ? "1" : "0");
      qs.set("timestamps", bulkShowTime ? "1" : "0");
    }

    const response = await exportSession.request(`${API_BASE}/export/${recordId}.${format}?${qs.toString()}`, {});
    if (!response.ok) {
      let message = `Export failed (${response.status})`;
      try {
        const data = await response.json();
        if (data?.error) message = data.error;
      } catch {}
      throw new Error(message);
    }
    const blob = await response.blob();
    exportSession.assertActive();
    return blob;
  };

  const handleBulkDownload = async () => {
    if (bulkDownloading) return;
    let exportSession;
    try {
      setBulkDownloading(true);
      setBulkDownloadError("");
      setBulkProgress(t('workspace.loadingHistory'));

      const { data: { session } } = await supabase.auth.getSession();
      exportSession = createExportSession(supabase.auth, session, () => resultUserRef.current?.id);
      bulkSessionRef.current = exportSession;
      // An explicit export includes the complete archive, regardless of the
      // currently loaded page, search or filter.
      const files = await allCompletedHistory(API_BASE, session, exportSession.signal, exportSession.request);
      if (!files.length) throw new Error(t('overview.bulkNoFiles'));
      trackEvent('bulk_download_attempt', { format: bulkFormat, file_count: files.length,
        speakers_enabled: bulkShowSpeakers, timestamps_enabled: bulkShowTime,
        summaries_enabled: bulkIncludeSummaries });

      const entries = [];
      const usedNames = new Set();
      const errors = [];
      let successfulFiles = 0;

      for (let index = 0; index < files.length; index += 1) {
        const file = files[index];
        const base = safeBaseFilename(file.original_filename, file.id || `transcript-${index + 1}`);
        setBulkProgress(t("overview.bulkPreparing", { current: index + 1, total: files.length }));

        try {
          const needsFull = bulkFormat === "txt" && (bulkShowSpeakers || bulkShowTime);
          const data = await getBulkResult(file.id, exportSession, { needsFull });

          if (bulkFormat === "txt") {
            const content = formatTranscriptionContent(data, bulkShowSpeakers, bulkShowTime, bulkUsePostprocessing);
            entries.push({
              name: uniqueZipName(usedNames, `${base}.txt`),
              data: content,
            });
          } else {
            const blob = await fetchBulkExportBlob(file.id, bulkFormat, exportSession);
            entries.push({
              name: uniqueZipName(usedNames, `${base}.${bulkFormat}`),
              data: blob,
            });
          }

          successfulFiles += 1;
          if (bulkIncludeSummaries && hasTranscriptSummary(data)) {
            entries.push({
              name: uniqueZipName(usedNames, `${base}_summary.txt`),
              data: formatSummaryContent(data),
            });
          }
        } catch (error) {
          if (isExportSessionError(error)) throw error;
          errors.push(`${file.original_filename || file.id}: ${error?.message || "Export failed"}`);
        }
      }

      if (!successfulFiles) throw new Error(t('overview.bulkAllFailed'));
      if (errors.length) {
        entries.push({
          name: uniqueZipName(usedNames, "_export_errors.txt"),
          data: errors.join("\n"),
        });
      }

      if (!entries.length) {
        throw new Error(t("overview.bulkNoFiles"));
      }

      setBulkProgress(t("overview.bulkZipping"));
      const zipBlob = await createZipBlob(entries);
      await exportSession.getSession();
      exportSession.assertActive();
      saveBlob(zipBlob, `instanttranscriber-transcripts-${new Date().toISOString().slice(0, 10)}.zip`);
      setShowBulkDownloadModal(false);
      toast(
        errors.length
          ? t("overview.bulkDownloadPartial", { count: errors.length })
          : t("overview.bulkDownloadReady"),
        4000
      );
      trackEvent("bulk_download", {
        format: bulkFormat,
        file_count: successfulFiles,
        requested_file_count: files.length,
        zip_entries: entries.length,
        error_count: errors.length,
      });
    } catch (error) {
      const message = ['export_auth_required', 'export_account_changed', 'export_cancelled'].includes(error?.code)
        ? t(`overview.${error.code}`) : error?.message || t("overview.exportFailed");
      setBulkDownloadError(message);
      trackEvent("bulk_download_error", {
        format: bulkFormat,
        error_type: classifyError(error),
      });
    } finally {
      exportSession?.cancel();
      if (bulkSessionRef.current === exportSession) bulkSessionRef.current = null;
      setBulkDownloading(false);
      setBulkProgress("");
    }
  };

  const fetchHistory = useCallback((options = {}) => {
    const append = options.append === true;
    const query = { ...historyQueryRef.current };
    const key = JSON.stringify(query);
    const owner = metadataOwnerRef.current;
    const existing = historyRequestRef.current;
    if (existing?.key === key && existing.owner === owner) return existing.request;
    existing?.controller.abort('replaced');
    const controller = new AbortController();
    const metadataSignal = metadataAbortRef.current.signal;
    const stop = () => controller.abort(metadataSignal.reason || 'account_changed');
    metadataSignal.addEventListener('abort', stop, { once: true });
    if (metadataSignal.aborted) stop();
    const slot = { key, owner, controller };
    const changedQuery = historyPagesRef.current.key !== key;
    if (append) setHistoryLoadingMore(true);
    else if (changedQuery) setHistoryQueryLoading(true);
    const request = (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session || session.user?.id !== owner) return;
        const page = await requestHistoryPage(API_BASE, session, {
          ...query, cursor: append ? historyPagesRef.current.cursor : null,
        }, controller.signal);
        if (controller.signal.aborted || metadataOwnerRef.current !== owner || key !== JSON.stringify(historyQueryRef.current)) return;
        const previous = changedQuery ? [] : historyFilesRef.current;
        const files = mergeHistory(previous, [...page.items, ...page.active]);
        // Loading older pages or changing a search must never download all of
        // those historical results as if they had just completed.
        resultPrefetchRef.current.observe(files, { seed: append || changedQuery });
        observeHistoryOutcomes(files, session.user?.id);
        const outdatedPreviews = files.filter(file => !file.is_preview && resultCacheRef.current[file.id]?.is_preview);
        outdatedPreviews.forEach(file => {
          resultAbortRef.current[file.id]?.abort();
          delete resultCacheRef.current[file.id];
        });
        if (outdatedPreviews.length) setResultCache(prev => {
          const next = { ...prev }; outdatedPreviews.forEach(file => { delete next[file.id]; }); return next;
        });
        const keepCursor = !append && !changedQuery && historyPagesRef.current.pages > 1;
        historyPagesRef.current = { key, cursor: keepCursor ? historyPagesRef.current.cursor : page.next_cursor,
          pages: append ? historyPagesRef.current.pages + 1 : keepCursor ? historyPagesRef.current.pages : 1 };
        setHistoryNextCursor(historyPagesRef.current.cursor);
        const totals = page.totals;
        setHistoryTotals(totals && Number.isInteger(totals.files) && Number.isInteger(totals.completed)
          ? { files: Math.max(0, totals.files), completed: Math.max(0, totals.completed) }
          : { files: files.length, completed: files.filter(file => isCompletedTranscriptionStatus(file.status) && file.result_state !== 'empty').length });
        historyFilesRef.current = files;
        setRecentFiles(files);
        setHistoryError(false);
      } catch (error) {
        if (metadataOwnerRef.current === owner && key === JSON.stringify(historyQueryRef.current)
            && error?.code !== 'request_cancelled') setHistoryError(true);
        trackDiagnostic('history_load_error', error, { operation: 'history' });
      } finally {
        metadataSignal.removeEventListener('abort', stop);
        if (historyRequestRef.current === slot) {
          historyRequestRef.current = null;
          setHistoryLoaded(true); setHistoryLoadingMore(false); setHistoryQueryLoading(false);
        }
      }
    })();
    slot.request = request;
    historyRequestRef.current = slot;
    return request;
  }, []);

  const fetchChangedHistory = useCallback(async records => {
    const owner = metadataOwnerRef.current;
    const { data: { session } } = await supabase.auth.getSession();
    if (!session || session.user?.id !== owner) return;
    const items = await changedHistory(API_BASE, session, records, metadataAbortRef.current.signal);
    if (metadataOwnerRef.current !== owner) return;
    const missing = records.filter(id => !items.some(file => file.id === id));
    const usable = file => file && isCompletedTranscriptionStatus(file.status) && file.result_state !== 'empty';
    const previous = new Map(historyFilesRef.current.map(file => [file.id, file]));
    const completedDelta = items.reduce((count, file) => count + Number(Boolean(usable(file))) - Number(Boolean(usable(previous.get(file.id)))), 0)
      - missing.filter(id => usable(previous.get(id))).length;
    setHistoryTotals(totals => ({ ...totals, completed: Math.max(0, totals.completed + completedDelta) }));
    const files = mergeHistory(historyFilesRef.current, items, missing);
    resultPrefetchRef.current.observe(files);
    observeHistoryOutcomes(items, owner);
    historyFilesRef.current = files;
    setRecentFiles(files);
  }, []);

  const fetchQuota = useCallback(() => {
    const owner = metadataOwnerRef.current, signal = metadataAbortRef.current.signal;
    const existing = quotaRequestRef.current;
    if (existing?.owner === owner && existing.signal === signal) return existing.request;
    const slot = { owner, signal };
    const request = (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const token = session?.access_token;
        if (!token || session.user?.id !== owner || signal.aborted) return;
        const data = await retryRead(async timeoutMs => {
          const response = await fetchWithTimeout(`${API_BASE}/quota`, {
            requestPurpose:'quota_refresh', signal, method: "GET",
            headers: { Authorization: `Bearer ${token}` },
          }, Math.min(15000, timeoutMs));
          if (!response.ok) throw responseError(response, await readJsonResponse(response), "Failed to fetch quota");
          return readJsonResponse(response);
        }, { signal, onRecovered: measurements => trackEvent('api_read_recovered', { operation: 'quota', ...measurements }) });
        if (signal.aborted || metadataOwnerRef.current !== owner) return;
        setQuota(data);
        setQuotaError(false);
      } catch (error) {
        if (error?.code !== "request_cancelled" && metadataOwnerRef.current === owner) setQuotaError(true);
        trackDiagnostic("quota_load_error", error, { operation: "quota" });
        console.error("Error fetching quota:", error);
      } finally {
        if (quotaRequestRef.current === slot) quotaRequestRef.current = null;
      }
    })();
    slot.request = request; quotaRequestRef.current = slot;
    return request;
  }, []);

  // ---------- bootstrap ----------
  useEffect(() => {
    const controller = new AbortController();
    metadataAbortRef.current = controller;
    const resultControllers = resultAbortRef.current;
    if (metadataOwnerRef.current && metadataOwnerRef.current !== currentUserId) {
      setImportTask(null);
      setIsImportingUrl(false);
    }
    metadataOwnerRef.current = currentUserId;
    historyRequestRef.current?.controller.abort('account_changed');
    historyRequestRef.current = null;
    historyPagesRef.current = { key: null, cursor: null, pages: 0 };
    setHistoryTotals({ files: 0, completed: 0 });
    setHistoryNextCursor(null); setHistoryLoadingMore(false); setHistoryQueryLoading(false);
    resultCacheRef.current = {};
    urlRecoveryAttemptRef.current = null;
    resultPrefetchRef.current = createResultPrefetchQueue();
    resultPrefetchRunRef.current = null;
    setResultCache({});
    setRecentFiles([]);
    setStatusMap({}); pollFailuresRef.current.clear();
    setFileSearch(''); setFileFilter('all');
    setHistoryLoaded(false);
    if (currentUserId) fetchQuota();
    return () => {
      controller.abort('account_changed');
      historyRequestRef.current?.controller.abort('account_changed');
      resultPrefetchRunRef.current = null;
      Object.values(resultControllers).forEach(ctl => ctl.abort('account_changed'));
    };
  }, [currentUserId, fetchHistory, fetchQuota]);

  useEffect(() => {
    if (!currentUserId) return undefined;
    setHistoryQueryLoading(true);
    const timer = setTimeout(() => fetchHistory(), fileSearch.trim() ? 250 : 0);
    return () => clearTimeout(timer);
  }, [currentUserId, fileSearch, fileFilter, fetchHistory]);

  // Provide an auth token getter for lib/api (podcast endpoints, etc.)
  useEffect(() => {
    setTokenGetter(async () => {
      const { data: { session } } = await supabase.auth.getSession();
      return session?.access_token ?? null;
    });
  }, []);

  useEffect(() => {
    setIsPremium(contextIsPremium);
  }, [contextIsPremium]);

  // Stripe return flow
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    if (sessionId) {
      const checkoutAttribution = getSavedCheckoutAttribution();
      trackEventWithCta("checkout_return", {
        ...checkoutAttribution,
        source: "stripe",
        billing_provider: "stripe",
        return_surface: "overview",
        has_session_id: true,
      });
      const tries = [1000, 3000, 5000];
      tries.forEach((delay) => {
        setTimeout(async () => {
          try {
            const premiumActive = await checkPremiumStatus(true);
            if (!premiumActive || checkoutActivationTrackedRef.current) return;
            const receipt = await loadCheckoutReceipt(sessionId);
            if (!receipt || checkoutActivationTrackedRef.current) return;
            const didTrack = trackPremiumActivated({
              ...checkoutAttribution,
              ...receipt,
              source: "overview",
              billing_provider: "stripe",
            });
            checkoutActivationTrackedRef.current = true;
            if (didTrack) clearCheckoutAttribution();
          } catch (error) {
            trackDiagnostic('checkout_receipt_error', error, { operation: 'checkout_receipt' });
          }
        }, delay);
      });
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [checkPremiumStatus]);

  // Premium quota handling
  useEffect(() => {
    if (!currentUserId) return;
    if (isPremium) setQuota({ used: 0, remaining: null, limit: "unlimited" });
    else fetchQuota();
  }, [currentUserId, isPremium, fetchQuota]);

  useEffect(() => {
    if (isPremium || !quota || typeof quota.limit !== "number") return;
    if ((Number(quota.used) || 0) < quota.limit) return;
    trackFreeProductIntent(PRODUCT_INTENT_EVENTS.DAILY_LIMIT_REACHED, {
      source: "quota_state",
      limit_type: "daily_free_transcriptions",
      quota_used: Number(quota.used) || 0,
      quota_limit: quota.limit,
    }, {
      dedupeKey: `daily:${todayKey()}`,
    });
  }, [isPremium, quota, trackFreeProductIntent]);

  useEffect(() => {
    if (isPremium || !recentFiles.length) return;

    const completedIds = completedTranscriptionIds(recentFiles);
    if (completedIds.length >= 2) {
      trackFreeProductIntent(PRODUCT_INTENT_EVENTS.MULTIPLE_TRANSCRIPTIONS_COMPLETED, {
        source: "history_state",
        completed_count: Math.min(completedIds.length, 100),
      }, {
        dedupeKey: "multiple_completed",
      });
    }

    recentFiles.forEach((file) => {
      if (!file?.id || !isCompletedTranscriptionStatus(file.status) || file.is_preview || file.result_state !== 'text') return;
      trackFreeProductIntent(PRODUCT_INTENT_EVENTS.TRANSCRIPTION_COMPLETED, {
        source: 'result_quality', result_type: 'full', result_state: 'text',
      }, { dedupeKey: `completed:${file.id}` });
      const durationSeconds = parseDurationSeconds(file.audio_duration_seconds ?? file.duration);
      if (!Number.isFinite(durationSeconds) || durationSeconds <= LONG_TRANSCRIPTION_SECONDS) return;
      trackFreeProductIntent(PRODUCT_INTENT_EVENTS.LONG_TRANSCRIPTION_COMPLETED, {
        source: "history_state",
        duration_bucket: durationBucket(durationSeconds),
        duration_minutes: Math.round(durationSeconds / 60),
      }, {
        dedupeKey: `long_completed:${file.id}`,
      });
    });
  }, [isPremium, recentFiles, trackFreeProductIntent]);

  // ---------- per-file status polling (every 3s for non-completed) ----------
  const fetchStatuses = useCallback(async () => {
    if (statusRequestRef.current || !workspaceVisible()) return;
    const owner = metadataOwnerRef.current;
    statusRequestRef.current = true;
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token || session.user?.id !== owner) return;

      const inProgress = recentFiles.filter(f => isActiveTranscriptionStatus(f.status));

      if (inProgress.length === 0) {
        setStatusMap({});
        return;
      }

      const results = await Promise.allSettled(
        inProgress.map(async f => {
          const res = await fetchWithTimeout(`${API_BASE}/status/${f.id}`, {
            requestPurpose:'job_progress',
            signal: metadataAbortRef.current.signal,
            headers: { Authorization: `Bearer ${token}` },
          });
          if (!res.ok) throw responseError(res, await readJsonResponse(res), "Status unavailable");
          const data = await res.json();
          return { id: f.id, data };
        })
      );

      const next = {};
      if (metadataOwnerRef.current !== owner) return;
      results.forEach((r, index) => {
        const id = inProgress[index].id;
        if (r.status === "rejected") {
          const failures = (pollFailuresRef.current.get(id) || 0) + 1;
          pollFailuresRef.current.set(id, failures);
          if (failures >= 3) {
            next[id] = { statusUnavailable: true };
            trackDiagnostic("status_poll_error", r.reason, { operation: "status_poll" });
          }
          return;
        }
        pollFailuresRef.current.delete(id);
        if (r.status === "fulfilled" && r.value?.id) {
          const { id, data } = r.value;
          next[id] = { ...data, statusUnavailable: false };
          if (norm(data.status).startsWith("error")) observeJobOutcome({ ...data, id }, session.user?.id, "status_poll");
          
          // Clean up startGuards for terminal states
          const status = norm(data?.status);
          if (["completed", "succeeded"].includes(status) || status.startsWith("error")) {
            startGuardsRef.current.delete(id);
          }
        }
      });
      setStatusMap(prev => ({ ...prev, ...Object.fromEntries(Object.entries(next).map(([id, data]) => [id, { ...prev[id], ...data }])) }));

      // Fetch only terminal jobs, rather than rereading every old history row.
      const completedIds = results
        .filter(r =>
          r.status === "fulfilled" &&
          (norm(r.value?.data?.status).startsWith("completed") || norm(r.value?.data?.status).startsWith("error"))
        )
      .map(r => r.value.id);

      if (completedIds.length > 0) {
        completedIds.forEach(id => {
          const live = next[id] || {};
          observeJobOutcome({ ...recentFiles.find(file => file.id === id), ...live, id }, session.user?.id, 'status_poll');
        });
        await fetchChangedHistory(completedIds);
      }
    } catch (e) {
      trackDiagnostic("status_poll_error", e, { operation: "status_poll" });
      console.warn("status poll error:", e);
    } finally {
      statusRequestRef.current = false;
    }
  }, [recentFiles, fetchChangedHistory]);

  // Only new completions are prefetched, sequentially. Old history loads on open.
  useEffect(() => {
    prefetchNewResults();
  }, [recentFiles, prefetchNewResults]);
  useEffect(() => observeWorkspaceVisibility(visible => { if (visible) prefetchNewResults(); }), [prefetchNewResults]);


  useEffect(() => {
    const hasActive = recentFiles.some(f => isActiveTranscriptionStatus(f.status));

    if (hasActive) {
      fetchStatuses(); // immediate
      if (statusIntervalRef.current === null) {
        statusIntervalRef.current = setInterval(fetchStatuses, 3000);
      }
    } else {
      if (statusIntervalRef.current) {
        clearInterval(statusIntervalRef.current);
        statusIntervalRef.current = null;
      }
      setStatusMap({});
    }

    return () => {
      if (statusIntervalRef.current) {
        clearInterval(statusIntervalRef.current);
        statusIntervalRef.current = null;
      }
    };
  }, [recentFiles, fetchStatuses]);

  // Refresh on reopening a landing-page dialog as well as a normal tab.
  useEffect(() => observeWorkspaceVisibility(visible => {
    if (visible) { fetchHistory(); fetchStatuses(); }
  }), [fetchHistory, fetchStatuses]);

  // cleanup on unmount
  useEffect(() => () => {
    // Abort prefetches on unmount to prevent lingering network work
    Object.values(resultAbortRef.current).forEach(c => c?.abort?.('view_closed'));
    importObserverRef.current?.abort('view_closed');
  }, []);

  // ---------- premium purchase ----------
  const handlePremiumSubscription = async (
    ctaLocation = "overview_unknown",
    selectedBillingInterval = billingInterval,
    record = null
  ) => {
    if (!accountReady || contextIsPremium || isPremium || checkoutRequestRef.current) return;
    const priceSummary = getBillingPriceSummary(selectedBillingInterval);
    if (urlRecovery?.upgradeAvailable && urlInput && rememberPendingUrlImport(urlInput, currentUser, { resumeAfterUpgrade: true })) {
      trackEvent('url_import_upgrade_saved', { source: 'url_import_size_limit', url_type: getUrlImportType(urlInput) });
    }
    if (!currentUser || currentUser.is_anonymous) {
      const params = new URLSearchParams({ premium: priceSummary.billingInterval });
      if (record?.id) params.set('record', record.id);
      const lang = new URLSearchParams(window.location.search).get("lang");
      if (lang) params.set("lang", lang);
      const from = `/dashboard?${params}`;
      if (currentUser?.is_anonymous) {
        setGuestAccountDialog({ redirectPath: from, filename: record?.original_filename,
          location: ctaLocation, purpose: 'premium', fileRetained:record?.retained_locally===true });
        return;
      }
      navigate(`/signup?redirect_path=${encodeURIComponent(from)}`, { state: { from } });
      return;
    }
    checkoutRequestRef.current = true;
    setCheckoutPending(true);
    let checkoutParams = {};
    try {
      await loadSignupAttribution(currentUser).catch(error => console.warn('Checkout attribution warning:', error.message));
      checkoutParams = buildCheckoutAttribution({
        checkout_source: "overview",
        source: "overview",
        cta_location: ctaLocation,
        checkout_cta_location: ctaLocation,
        plan: planName,
        billing_interval: priceSummary.billingInterval,
        plan_intent: `premium_${priceSummary.billingInterval}`,
        offer_variant: 'workspace_v1',
      }, contextIsPremium ? null : currentUser);
      trackUpgradeClick(checkoutParams);
      trackEventWithCta("begin_checkout", checkoutParams);
      const { data } = await supabase.auth.getSession();
      const token = data?.session?.access_token;
      if (!token) throw new Error("No active session");

      const response = await fetchWithTimeout(
        CONFIG.CREATE_CHECKOUT_SESSION,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            app_base_path: APP_BASENAME,
            billing_interval: priceSummary.billingInterval,
            attribution: checkoutParams,
            ga: await resolveGoogleAnalyticsAttribution(),
            meta: getMetaBrowserAttribution(),
            reddit: getRedditBrowserAttribution(),
          }),
        },
        30000
      );
      if (!response.ok) throw responseError(response, await readJsonResponse(response), "Subscription error");
      const checkoutData = await response.json();
      trackMetaInitiateCheckout({
        content_category: "subscription",
        source: "overview",
        billing_interval: priceSummary.billingInterval,
      });
      trackFreeProductIntent(PRODUCT_INTENT_EVENTS.CHECKOUT_STARTED, {
        ...checkoutParams,
        content_category: "subscription",
        billing_provider: "stripe",
      });
      storeCheckoutAttribution(checkoutParams, contextIsPremium ? null : currentUser);
      trackEventWithCta("checkout_redirect", checkoutParams);
      navigateOutsideApp(checkoutData.url);
    } catch (error) {
      if (error?.code === 'request_cancelled') {
        trackDiagnostic('checkout_error', error, { operation: 'checkout' });
        return;
      }
      console.error("Premium subscription error:", error);
      trackEventWithCta("checkout_error", {
        ...checkoutParams,
        error_type: classifyError(error),
        error_code: diagnosticErrorCode(error),
        operation: 'checkout', ...requestMeasurements(error),
        ...(error.status ? { status_code: error.status } : {}),
      });
      setModalTitle("Premium Subscription Error");
      setModalContent("Subscription failed: " + error.message);
      setShowModal(true);
    } finally {
      checkoutRequestRef.current = false;
      setCheckoutPending(false);
    }
  };

  // ---------- upload with popup ----------
  const createUploadItem = (file) => ({
    id: `upload-${Date.now()}-${Math.random().toString(16).slice(2)}`,
    file,
    progress: 0,
    status: "queued",
    jobId: null,
    error: null
  });

  const updateUploadItem = (id, updates) => {
    setUploadItems(prev => {
      const next = prev.map(item => (item.id === id ? { ...item, ...updates } : item));
      uploadItemsRef.current = next;
      return next;
    });
  };

  const bumpUploadItemProgress = (id) => {
    setUploadItems(prev => {
      const next = prev.map(item => {
        if (item.id !== id) return item;
        const nextProgress = Math.min(95, (item.progress || 0) + 1);
        return { ...item, progress: nextProgress };
      });
      uploadItemsRef.current = next;
      return next;
    });
  };

  // Resume the tariff explicitly selected before signup, including OAuth/email redirects.
  const premiumResumeHandler = useRef(null);
  premiumResumeHandler.current = handlePremiumSubscription;
  useEffect(() => {
    if (!accountReady || !currentUser || currentUser.is_anonymous || isPremium !== contextIsPremium) return;
    const url = new URL(window.location.href);
    const interval = url.searchParams.get("premium");
    if (![BILLING_INTERVALS.MONTHLY, BILLING_INTERVALS.ANNUAL].includes(interval)) return;
    url.searchParams.delete("premium");
    window.history.replaceState(window.history.state, "", url.pathname + url.search + url.hash);
    setBillingInterval(interval);
    if (!isPremium) premiumResumeHandler.current("guest_upgrade_resume", interval);
  }, [accountReady, currentUser, isPremium, contextIsPremium]);

  useEffect(() => {
    if (!accountReady || !currentUser || isPremium !== contextIsPremium) return;
    const saved = pendingUrlImport(currentUser);
    if (!saved || restoredUrlRef.current === saved.expires) return;
    restoredUrlRef.current = saved.expires;
    setUrlInput(saved.url);
    setUrlRestored(true);
    setShowStudioUrl(true);
    urlInputMethodRef.current = 'upgrade_resume';
    trackEvent('url_import_restored', { source: 'upgrade_resume', url_type: getUrlImportType(saved.url) });
  }, [accountReady, currentUser, isPremium, contextIsPremium]);

  const showUpgradeModalOnce = (content, record = null) => {
    if (upgradeModalShownRef.current) return;
    setModalTitle(t("overview.statusRequiresUpgrade"));
    setModalContent(content);
    setModalUpgradeRecord(record);
    setModalShowUpgradeCTA(true);
    setShowModal(true);
    upgradeModalShownRef.current = true;
  };

  const pendingOptionsFrom = (options = {}, trackingSource = "upgrade_resume") => ({
    diarize: options.diarize !== undefined ? options.diarize : true,
    language: options.language || "auto",
    timestamps: options.timestamps !== undefined ? options.timestamps : true,
    min_speakers: options.min_speakers ?? null,
    max_speakers: options.max_speakers ?? null,
    tracking_source: options.tracking_source || trackingSource,
  });

  const rememberRequiresUpgradeRecord = (payload, fileName, source, options = {}) => {
    if (!payload?.id) return;
    rememberCheckoutUploadedRecord(payload.id);
    const now = new Date().toISOString().slice(0, 19).replace("T", " ");
    const displayName = fileName || payload.original_filename || "Saved recording";
    savePendingUpgradeTranscription({
      id: payload.id,
      filename: displayName,
      duration: payload.duration || null,
      options: pendingOptionsFrom(options, `${source}_upgrade_resume`),
    });
    setRecentFiles(prev => [
      {
        id: payload.id,
        original_filename: displayName,
        new_filename: payload.new_filename,
        uploaded_time: now,
        duration: payload.duration || null,
        status: payload.status || "requires_upgrade",
        started_time: payload.started_time || null,
        is_preview: Boolean(payload.is_preview),
        preview_seconds: payload.preview_seconds || 0,
      },
      ...prev.filter(item => item.id !== payload.id),
    ]);
    trackEvent("transcription_requires_upgrade_saved", {
      source,
      plan: planName,
      has_duration: Boolean(payload.duration),
    });
    const durationSeconds = parseDurationSeconds(payload.audio_duration_seconds ?? payload.duration);
    trackFreeProductIntent(PRODUCT_INTENT_EVENTS.LIMIT_REACHED, {
      source,
      limit_type: "free_duration_limit",
      status: "requires_upgrade",
      has_duration: Number.isFinite(durationSeconds) || Boolean(payload.duration),
      duration_bucket: durationBucket(durationSeconds),
      duration_minutes: Number.isFinite(durationSeconds)
        ? Math.round(durationSeconds / 60)
        : undefined,
    }, {
      dedupeKey: `requires_upgrade_saved:${payload.id}`,
      allowUnconfirmedFree: true,
    });
    fetchHistory().catch(() => {});
  };

  const showRequiresUpgradeModal = (fileName, msg, payload) => {
    if (uploadItemsRef.current.length <= 1) setShowUploadPopup(false);
    const record = { ...payload, original_filename: fileName, retained: Boolean(payload?.id) };
    if (payload?.is_preview) {
      showUpgradeModalOnce(t("overview.previewStarted"), record);
      fetchQuota();
      return;
    }
    showUpgradeModalOnce(t("overview.savedRequiresPremium"), record);
  };

  const maybeClearPendingStartOptions = () => {
    if (!pendingStartOptionsRef.current) return;
    const hasPendingUploads = uploadItemsRef.current.some(
      item => item.status === "queued" || item.status === "uploading"
    );
    if (!hasPendingUploads) {
      setPendingStartOptions(null);
    }
  };

  const uploadTooLargeMessage = (file) => {
    const fileName = file?.name;
    const intro = fileName ? `${fileName} is too large.` : "This file is too large.";
    if (!isPremium && fileExceedsUploadLimit(file, true)) {
      return (
        `${intro} The Free plan supports files up to ${uploadLimitLabel(false)}. ` +
        `Premium supports files up to ${uploadLimitLabel(true)}, but this file is larger than that. ` +
        "You can still upgrade for the other Premium benefits, but you must split or compress this file before uploading."
      );
    }
    return (
      `${intro} ${isPremium ? "Premium" : "The Free plan"} supports files up to ${uploadLimitLabel(isPremium)}. ` +
      "Choose a smaller file or split it before uploading."
    );
  };

  const showUploadRecovery = useCallback((payload, status) => {
    const recovery = uploadRecovery(payload, status);
    if (!recovery) return false;
    if (recovery.quota) setQuota(recovery.quota);
    fetchQuota();
    setModalTitle(t('upload.limitTitle'));
    setModalContent(t(recovery.kind === 'daily_limit' && !isGuest ? 'upload.accountDailyLimit' : recovery.messageKey));
    setModalRecovery(recovery);
    setModalShowUpgradeCTA(recovery.kind === 'daily_limit' && !isPremium);
    if (recovery.recordId) setModalUpgradeRecord({ id: recovery.recordId, retained: true });
    if (recovery.kind === 'daily_limit') setModalUpgradeSource('daily_limit');
    setShowModal(true);
    trackEvent('upload_recovery_shown', { error_code: recovery.code, operation: payload?.operation || 'upload',
      retry_count: payload?.retry_count || 0, status_code: status });
    return true;
  }, [fetchQuota, isGuest, isPremium, t, setModalTitle]);

  const startLocalUploadItem = async (item, token) => {
    if (fileExceedsUploadLimit(item.file, isPremium || !accountReady)) {
      finishUploadImportRecovery(item, 'failed', 413);
      const msg = uploadTooLargeMessage(item.file);
      updateUploadItem(item.id, { status: "error", progress: 0, error: msg });
      trackEvent("upload_rejected", {
        ...getUploadTrackingParams([item.file], "local_upload"),
        reason: isPremium ? "premium_size_limit" : "free_size_limit",
        error_code: isPremium ? "premium_size_limit" : "free_size_limit",
      });
      setModalTitle("File too large");
      setModalContent(msg);
      setModalUpgradeRecord({ original_filename: item.file.name, size_bytes: item.file.size });
      if(!isPremium)setLargeFile(item.file);
      setModalShowUpgradeCTA(!isPremium);
      setShowModal(true);
      maybeClearPendingStartOptions();
      return;
    }

    updateUploadItem(item.id, { status: "uploading", progress: 0, error: null, preparation: null, connection:null });
    trackEvent("upload_start", {
      ...getUploadTrackingParams([item.file], "local_upload"),
      batch: uploadItemsRef.current.length > 1,
    });

    const xhr = new ResumableUpload({
      baseUrl: API_BASE, file: item.file, userId: (await supabase.auth.getSession()).data.session?.user.id, getToken: freshUploadToken,
    });
    uploadXhrRefs.current.set(item.id, xhr);
    xhr.onconnection = connection => updateUploadItem(item.id,{connection});
    xhr.onpreparation = (preparation) => {
      updateUploadItem(item.id, { preparation, progress: 100, connection:null });
    };

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) {
        const pct = Math.min(100, Math.round((e.loaded / e.total) * 100));
        updateUploadItem(item.id, { progress: pct });
        if (uploadItemsRef.current.length === 1) {
          setUploadPct(pct);
        }
      } else {
        bumpUploadItemProgress(item.id);
        if (uploadItemsRef.current.length === 1) {
          setUploadPct((p) => (p < 95 ? p + 1 : p));
        }
      }
    };

    return await new Promise((resolve) => {
      let resolved = false;
      const releaseUploadSlot = () => {
        if (resolved) return;
        resolved = true;
        resolve();
      };
      const handleUnexpectedUploadError = (error, stage = "response") => {
        finishUploadImportRecovery(item, 'failed');
        console.error("Upload handling failed:", error);
        uploadXhrRefs.current.delete(item.id);
        const msg = error?.message || "Upload failed";
        trackEvent("upload_error", {
          source: "local_upload",
          error_type: classifyError(error),
          error_code: diagnosticErrorCode(error),
          operation: error?.stage || stage,
          retry_count: error?.retry_count || 0,
          stage,
          batch: uploadItemsRef.current.length > 1,
        });
        updateUploadItem(item.id, { status: "error", error: msg });
        maybeClearPendingStartOptions();
      };

      xhr.onload = async () => {
        try {
          const ok = xhr.status >= 200 && xhr.status < 300;
          let payload = null;
          try { payload = JSON.parse(xhr.responseText || "{}"); } catch (_) {}
          uploadXhrRefs.current.delete(item.id);
          finishUploadImportRecovery(item, (ok && payload?.id) || isSavedUpgrade(payload, xhr.status)
            ? 'succeeded' : 'failed', xhr.status);

          if (!ok) {
            const msg = xhr.status === 413
              ? uploadTooLargeMessage(item.file)
              : payload?.resume_available ? t('upload.selectSameToResume')
                : ((payload && payload.error) ? payload.error : `Upload error (${xhr.status})`);
            trackEvent(isSavedUpgrade(payload, xhr.status) ? "upload_requires_upgrade" : "upload_error", {
              source: "local_upload",
              ...xhr.transferAnalytics(),
              status_code: xhr.status,
              error_code: isSavedUpgrade(payload, xhr.status) ? 'free_duration_limit' : uploadErrorCode(payload, xhr.status),
              operation: payload?.operation || 'upload',
              retry_count: payload?.retry_count || 0,
              error_type: isSavedUpgrade(payload, xhr.status) ? 'plan_limit' : classifyError(msg),
              batch: uploadItemsRef.current.length > 1,
            });
            if (payload?.requires_upgrade && payload?.id) {
              const pendingOptions = pendingStartOptionsRef.current || {};
              updateUploadItem(item.id, {
                status: "requires_upgrade",
                progress: 100,
                jobId: payload.id,
                error: msg,
              });
              rememberRequiresUpgradeRecord(payload, item.file.name, "local_upload", pendingOptions);
              showRequiresUpgradeModal(item.file.name, msg, payload);
              maybeClearPendingStartOptions();
              return;
            }
            updateUploadItem(item.id, { status: "error", error: msg });
            if (showUploadRecovery(payload, xhr.status)) { maybeClearPendingStartOptions(); return; }
            if (shouldOfferUpgrade(msg)) {
              showUpgradeModalOnce(
                `Upload failed for ${item.file.name}: ${msg}\n\nFree plan supports files up to 35 minutes. Upgrade to transcribe up to 10 hours per file and 3 GB uploads.`
              );
            } else if (uploadItemsRef.current.length === 1) {
              setModalTitle("Upload Error");
              setModalContent(`Upload failed: ${msg}`);
              setModalShowUpgradeCTA(false);
              setShowModal(true);
            } else {
              toast(`Upload failed for ${item.file.name}: ${msg}`, 4000);
            }
            maybeClearPendingStartOptions();
            return;
          }

          updateUploadItem(item.id, {
            status: "uploaded",
            progress: 100,
            jobId: payload?.id || null,
            error: null
          });
          rememberCheckoutUploadedRecord(payload?.id);
          trackUploadCompletion(xhr, {
            source: "local_upload",
            status_code: xhr.status,
            batch: uploadItemsRef.current.length > 1,
            has_duration: Boolean(payload?.duration),
          });

          if (uploadItemsRef.current.length === 1) {
            toast("Upload complete - ready to start.", 3500);
          }

          // If the user already pressed Start while we were uploading, start now
          if (pendingStartOptionsRef.current && payload?.id) {
            try {
              await startTranscription(payload.id, token, pendingStartOptionsRef.current);
            } catch (transcriptionError) {
              const msg = transcriptionError?.message || "Unknown error";
              updateUploadItem(item.id, { status: "start_error", error: msg });
              if (transcriptionError.recoveryShown) return;
              if (shouldOfferUpgrade(msg)) {
                showUpgradeModalOnce(
                  `Failed to start transcription for ${item.file.name}: ${msg}\n\nFree plan supports files up to 35 minutes. Upgrade to transcribe up to 10 hours per file and 3 GB uploads.`
                );
              } else if (uploadItemsRef.current.length === 1) {
                setModalTitle("Transcription Start Error");
                setModalContent(`Failed to start transcription: ${msg}`);
                setModalShowUpgradeCTA(false);
                setShowModal(true);
              } else {
                toast(`Failed to start ${item.file.name}: ${msg}`, 4000);
              }
            } finally {
              maybeClearPendingStartOptions();
            }
          }

          if (!pendingStartOptionsRef.current && payload?.id) {
            // (Optional) Optimistic list insert so the card shows up instantly
            setRecentFiles(prev => [
              {
                id: payload.id,
                original_filename: item.file.name,
                new_filename: payload.new_filename,
                uploaded_time: new Date().toISOString().slice(0,19).replace('T',' '),
                duration: payload.duration || null,
                status: "uploaded",
                started_time: null,
              },
              ...prev
            ]);

            // Make the new item appear immediately / as fast as possible
            try { await fetchHistory(); } catch (_) {}
          }
        } catch (error) {
          handleUnexpectedUploadError(error);
        } finally {
          releaseUploadSlot();
        }
      };

      xhr.onerror = () => {
        try {
          finishUploadImportRecovery(item, 'failed', xhr.status);
          uploadXhrRefs.current.delete(item.id);
          const msg = xhr.error?.canResume ? t('upload.selectSameToResume') : xhr.error?.message || t('upload.selectSameToResume');
          trackEvent("upload_error", {
            source: "local_upload",
            ...xhr.transferAnalytics(),
            error_type: classifyError(xhr.error),
            error_code: uploadErrorCode(xhr.error),
            operation: xhr.error?.stage || 'upload',
            retry_count: xhr.error?.retry_count || 0,
            batch: uploadItemsRef.current.length > 1,
          });
          updateUploadItem(item.id, { status: "error", error: msg });
          if (uploadItemsRef.current.length === 1) {
            setModalTitle("Upload Error");
            setModalContent(`Upload failed: ${msg}`);
            setModalShowUpgradeCTA(false);
            setShowModal(true);
          } else {
            toast(`Upload failed for ${item.file.name}: ${msg}`, 4000);
          }
          maybeClearPendingStartOptions();
        } finally {
          releaseUploadSlot();
        }
      };

      xhr.onabort = () => {
        try {
          finishUploadImportRecovery(item, 'cancelled');
          uploadXhrRefs.current.delete(item.id);
          trackEvent("upload_canceled", {
            source: "local_upload",
            ...xhr.transferAnalytics(),
            error_code: 'upload_cancelled',
            operation: xhr.operation,
            retry_count: xhr.retryCount || 0,
            batch: uploadItemsRef.current.length > 1,
          });
          updateUploadItem(item.id, { status: "canceled", error: "canceled" });
          if (uploadCancelToastRef.current) return;
          if (uploadItemsRef.current.length === 1) {
            setToastMessage("Upload canceled.");
            setShowToast(true);
            setTimeout(() => setShowToast(false), 3000);
          }
          maybeClearPendingStartOptions();
        } finally {
          releaseUploadSlot();
        }
      };

      try {
        xhr.send();
      } catch (error) {
        handleUnexpectedUploadError(error, "send");
        releaseUploadSlot();
      }
    });
  };

  const pumpLocalUploadQueue = (token, runId) => {
    if (uploadRunIdRef.current !== runId) return;

    while (
      activeUploadCountRef.current < MAX_PARALLEL_LOCAL_UPLOADS &&
      uploadQueueRef.current.length > 0
    ) {
      const item = uploadQueueRef.current.shift();
      if (!item || !uploadItemsRef.current.some(current => current.id === item.id)) {
        continue;
      }

      activeUploadCountRef.current += 1;
      startLocalUploadItem(item, token)
        .catch((error) => {
          finishUploadImportRecovery(item, 'failed');
          console.error("Upload worker failed:", error);
          updateUploadItem(item.id, {
            status: "error",
            error: error?.message || "Upload failed",
          });
          maybeClearPendingStartOptions();
        })
        .finally(() => {
          if (uploadRunIdRef.current !== runId) return;
          activeUploadCountRef.current = Math.max(0, activeUploadCountRef.current - 1);
          pumpLocalUploadQueue(token, runId);
        });
    }
  };

  const uploadFiles = async (files, importRecoveryAttempt) => {
    if (uploadStartingRef.current) return;
    uploadStartingRef.current = true;
    try {
      const originalCount = files.length;
      files = await uniqueUploadFiles(files);
      if (!files.length) return;
      if (files.length !== originalCount) trackEvent('upload_duplicate_prevented', { operation: 'batch', duplicate_count: originalCount - files.length });
      const session = await ensureSession();
      if (!session) throw new Error("No active session");
      const token = session.access_token;

      const items = files.map(createUploadItem);
      if (importRecoveryAttempt) items[0].importRecovery = importRecoveryAttempt;
      uploadItemsRef.current = items;
      upgradeModalShownRef.current = false;
      uploadCancelToastRef.current = false;
      setUploadItems(items);
      setCurrentUploadFile(items.length === 1 ? items[0].file : null);
      setCurrentJobId(null);
      setUploadPct(0);
      setShowUploadPopup(true);
      setRecordingMode("upload");
      setAutoStartRecording(false);
      setPendingStartOptions(null);
      trackEvent("upload_batch_start", getUploadTrackingParams(files, "local_upload"));

      const runId = uploadRunIdRef.current + 1;
      uploadRunIdRef.current = runId;
      activeUploadCountRef.current = 0;
      uploadQueueRef.current = [...items];
      pumpLocalUploadQueue(token, runId);
    } catch (error) {
      resetUploadPopupState();
      console.error("Upload failed:", error);
      const msg = error?.message || "Unknown error";
      trackEvent("upload_error", {
        source: "local_upload",
        error_type: classifyError(error),
        stage: "prepare",
      });
      if (shouldOfferUpgrade(msg)) {
        showUpgradeModalOnce(
          `Upload failed: ${msg}\n\nFree plan supports files up to 35 minutes. Upgrade to transcribe up to 10 hours per file and 3 GB uploads.`
        );
      } else {
        setModalTitle("Upload Error");
        setModalContent("Upload failed: " + msg);
        setModalShowUpgradeCTA(false);
        setShowModal(true);
      }
    } finally { uploadStartingRef.current = false; }
  };

  // ---------- start transcription ----------
  const startTranscription = useCallback(async (jobId, token, options = {}) => {
    // Guard against double starts
    if (startGuardsRef.current.has(jobId)) {
      return { ok: true, deduped: true };
    }
    startGuardsRef.current.add(jobId);

    const startData = {
      id: jobId,
      diarize: options.diarize !== undefined ? options.diarize : true,
      language: options.language || "auto",
      diar_method: "fast",
      timestamps: options.timestamps !== undefined ? options.timestamps : true
    };
    
    // Add speaker count if specified
    if (options.min_speakers) {
      startData.min_speakers = options.min_speakers;
    }
    if (options.max_speakers) {
      startData.max_speakers = options.max_speakers;
    }

    trackEvent("transcription_start_attempt", {
      source: options.tracking_source || "unknown",
      auto_started: Boolean(options.auto_started),
      language: startData.language,
      diarize: startData.diarize,
      timestamps: startData.timestamps,
      speaker_count_mode: options.min_speakers || options.max_speakers ? "specified" : "auto",
    });

    let response;
    try {
      token = await freshUploadToken();
      response = await fetchWithTimeout(withClientVersion(`${API_BASE}/start`), {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify(startData)
      });
    } catch (error) {
      startGuardsRef.current.delete(jobId);
      trackEvent("transcription_start_error", {
        source: options.tracking_source || "unknown",
        error_type: classifyError(error),
        error_code: diagnosticErrorCode(error),
        operation: 'transcription_start',
        ...requestMeasurements(error),
      });
      throw error;
    }

    if (!response.ok) {
      startGuardsRef.current.delete(jobId); // cleanup on failure
      const errorData = normalizeUploadFailure(await readJsonResponse(response), response.status);
      const errorMessage = typeof errorData?.error === "string"
        ? errorData.error
        : (errorData?.error?.message || `Start transcription failed: ${response.status}`);
      trackEvent(isSavedUpgrade(errorData, response.status) ? "transcription_start_requires_upgrade" : "transcription_start_error", {
        source: options.tracking_source || "unknown",
        status_code: response.status,
        error_type: isSavedUpgrade(errorData, response.status) ? 'plan_limit' : classifyError(errorMessage),
        error_code: isSavedUpgrade(errorData, response.status) ? 'free_duration_limit' : uploadErrorCode(errorData, response.status),
        operation: 'transcription_start',
      });
      if (errorData?.requires_upgrade) {
        const durationSeconds = parseDurationSeconds(errorData.audio_duration_seconds ?? errorData.duration);
        trackFreeProductIntent(PRODUCT_INTENT_EVENTS.LIMIT_REACHED, {
          source: options.tracking_source || "unknown",
          limit_type: "free_duration_limit",
          status_code: response.status,
          duration_bucket: durationBucket(durationSeconds),
          duration_minutes: Number.isFinite(durationSeconds)
            ? Math.round(durationSeconds / 60)
            : undefined,
        }, {
          dedupeKey: errorData.id ? `requires_upgrade_start:${errorData.id}` : undefined,
          allowUnconfirmedFree: true,
        });
      }
      if (errorData.code === 'daily_limit_reached' || isDailyFreeLimitMessage(errorMessage)) {
        trackFreeProductIntent(PRODUCT_INTENT_EVENTS.DAILY_LIMIT_REACHED, {
          source: options.tracking_source || "unknown",
          limit_type: "daily_free_transcriptions",
          status_code: response.status,
          quota_limit: 3,
        }, {
          dedupeKey: `daily:${todayKey()}`,
          allowUnconfirmedFree: true,
        });
      }
      if (errorData.code === 'daily_limit_reached') {
        rememberQuotaBlocked(currentUserId, jobId);
        savePendingUpgradeTranscription({ id: jobId, options: pendingOptionsFrom(options, 'daily_limit_resume') });
        trackEvent('transcription_daily_limit_reached', { error_code: 'daily_limit_reached',
          operation: 'transcription_start', status_code: response.status, source: options.tracking_source || 'unknown' });
      }
      const startError = new Error(errorMessage);
      startError.code = errorData?.code;
      startError.status = response.status;
      startError.recoveryShown = showUploadRecovery({ ...errorData, id: errorData.id || jobId, operation: 'transcription_start' }, response.status);
      throw startError;
    }

    let result;
    try { result = await response.json(); }
    catch (error) { startGuardsRef.current.delete(jobId); trackDiagnostic('transcription_start_error', error, { operation: 'transcription_start' }); throw error; }
    if (consumeQuotaRecovery(currentUserId, jobId)) {
      trackEvent('transcription_quota_recovered', { operation: 'transcription_start',
        source: options.tracking_source || 'unknown', error_code: 'daily_limit_reached' });
    }
    if (!result.is_preview) {
      resultAbortRef.current[jobId]?.abort();
      delete resultCacheRef.current[jobId];
      setResultCache(prev => { const next = { ...prev }; delete next[jobId]; return next; });
      setStatusMap(prev => ({ ...prev, [jobId]: result }));
      setRecentFiles(prev => prev.map(file => file.id === jobId
        ? { ...file, status: result.status, is_preview: false, preview_seconds: 0 } : file));
      completionTrackedRef.current.delete(jobId);
    }
    observePendingJob({ ...result, id: jobId }, currentUserId);
    trackEvent("transcription_started", {
      source: options.tracking_source || "unknown",
      auto_started: Boolean(options.auto_started),
      language: startData.language,
      diarize: startData.diarize,
      timestamps: startData.timestamps,
    });
    
    // Refresh history and show success message
    fetchHistory();
    toast("File uploaded! Transcription in progress…", 5000);

    return result;
  }, [fetchHistory, toast, trackFreeProductIntent, showUploadRecovery, currentUserId]);

  useEffect(() => {
    if (!currentUser || !isPremium) return;
    const pending = getPendingUpgradeTranscription();
    if (!pending?.id) return;
    if (pendingUpgradeStartAttemptedRef.current.has(pending.id)) return;
    pendingUpgradeStartAttemptedRef.current.add(pending.id);

    let canceled = false;
    const tryResume = async () => {
      const delays = [0, 3000, 7000, 10000, 10000, 10000, 10000, 10000];
      let lastError = null;
      for (const delay of delays) {
        if (delay > 0) {
          await new Promise(resolve => setTimeout(resolve, delay));
        }
        if (canceled) return;
        try {
          const { data: { session } } = await supabase.auth.getSession();
          const token = session?.access_token;
          if (!token) throw new Error("No active session");
          await startTranscription(pending.id, token, {
            ...(pending.options || {}),
            tracking_source: pending.options?.tracking_source || "checkout_upgrade_resume",
            auto_started: true,
          });
          clearPendingUpgradeTranscription();
          toast("Saved recording started after upgrade.", 5000);
          fetchHistory();
          return;
        } catch (error) {
          lastError = error;
          if (error.recoveryShown) return;
        }
      }
      if (!canceled) {
        trackEvent("pending_upgrade_resume_error", {
          error_type: classifyError(lastError),
        });
        toast("Your saved recording is ready. Click Start Transcription to continue.", 7000);
        fetchHistory();
      }
    };

    tryResume();
    return () => {
      canceled = true;
    };
  }, [currentUser, isPremium, startTranscription, fetchHistory, toast]);

  const handleStartUploadedFile = useCallback(async (file) => {
    if (!file?.id) return;

    setStartingIds(prev => {
      const next = new Set(prev);
      next.add(file.id);
      return next;
    });

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) throw new Error("No active session");

      const result = await startTranscription(file.id, token, {
        diarize: true,
        language: "auto",
        timestamps: true,
        tracking_source: "dashboard_manual",
      });
      const nextStatus = result?.status || "transcribing";
      const now = new Date().toISOString().slice(0, 19).replace("T", " ");

      setRecentFiles(prev => prev.map(item => (
        item.id === file.id
          ? { ...item, status: nextStatus, started_time: item.started_time || now }
          : item
      )));
      setStatusMap(prev => ({
        ...prev,
        [file.id]: {
          ...(prev[file.id] || {}),
          id: file.id,
          status: nextStatus,
          duration: result?.duration || file.duration || null,
          progress: nextStatus === "completed" ? 100 : 0,
          started_time: now,
        },
      }));
    } catch (error) {
      if (error.recoveryShown) return;
      const msg = error?.message || "Unknown error";
      if (shouldOfferUpgrade(msg)) {
        setModalTitle("Upgrade required");
        setModalContent(
          `Failed to start transcription: ${msg}\n\nFree plan supports files up to 35 minutes. Upgrade to transcribe up to 10 hours per file and 3 GB uploads.`
        );
        setModalShowUpgradeCTA(true);
      } else {
        setModalTitle("Transcription Start Error");
        setModalContent(`Failed to start transcription: ${msg}`);
        setModalShowUpgradeCTA(false);
      }
      setShowModal(true);
    } finally {
      setStartingIds(prev => {
        const next = new Set(prev);
        next.delete(file.id);
        return next;
      });
    }
  }, [shouldOfferUpgrade, startTranscription, setModalTitle]);

  const cancelUpload = () => {
    try {
      const isBatch = uploadItemsRef.current.length > 1;
      trackEvent("upload_cancel_requested", {
        source: recordingMode === "recording" ? `recording_${recordingInput}` : "local_upload",
        batch: isBatch,
      });
      uploadCancelToastRef.current = true;
      setToastMessage(isBatch ? "Uploads canceled." : "Upload canceled.");
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3000);
      setTimeout(() => { uploadCancelToastRef.current = false; }, 500);
      uploadRunIdRef.current += 1;
      uploadQueueRef.current = [];
      activeUploadCountRef.current = 0;
      uploadXhrRefs.current.forEach((xhr) => xhr?.abort?.());
      uploadXhrRefs.current.clear();
      uploadXhrRef.current?.abort?.();
    } catch (_) {}
    setPendingStartOptions(null);
    pendingStartOptionsRef.current = null;
  };

  // ---------- upload recording ----------
  const uploadRecording = async (recordingFile, options) => {
    const source = options?.tracking_source || `recording_${recordingInput}`;
    try {
      if (fileExceedsUploadLimit(recordingFile, isPremium || !accountReady)) {
        const msg = uploadTooLargeMessage(recordingFile);
        trackEvent("upload_rejected", {
          ...getUploadTrackingParams([recordingFile], source),
          reason: isPremium ? "premium_size_limit" : "free_size_limit",
          error_code: isPremium ? "premium_size_limit" : "free_size_limit",
        });
        resetUploadPopupState();
        setModalTitle("File too large");
        setModalContent(msg);
        setModalUpgradeRecord({ original_filename: recordingFile.name, size_bytes: recordingFile.size });
        if(!isPremium)setLargeFile(recordingFile);
        setModalShowUpgradeCTA(!isPremium);
        setShowModal(true);
        return;
      }

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("No active session");
      const token = session.access_token;

      // Show upload progress
      setIsUploading(true);
      setUploadPct(0);
      setUploadPreparation(null);
      setUploadConnection(null);
      upgradeModalShownRef.current = false;
      trackEvent("upload_start", {
        ...getUploadTrackingParams([recordingFile], source),
        recording_input: recordingInput,
      });

      const xhr = new ResumableUpload({
        baseUrl: API_BASE, file: recordingFile, userId: (await supabase.auth.getSession()).data.session?.user.id, getToken: freshUploadToken,
        recordingDiagnostics: options?.recording_diagnostics,
      });
      uploadXhrRef.current = xhr;
      xhr.onconnection = setUploadConnection;
      xhr.onpreparation = preparation => { setUploadPreparation(preparation); setUploadConnection(null); };

      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) {
          const pct = Math.min(100, Math.round((e.loaded / e.total) * 100));
          setUploadPct(pct);
        } else {
          setUploadPct((p) => (p < 95 ? p + 1 : p));
        }
      };

      xhr.onload = async () => {
        const ok = xhr.status >= 200 && xhr.status < 300;
        let payload = null;
        try { payload = JSON.parse(xhr.responseText || "{}"); } catch (_) {}
        setIsUploading(false);

        if (!ok) {
          const msg = xhr.status === 413
            ? uploadTooLargeMessage(recordingFile)
            : payload?.resume_available ? t('upload.selectSameToResume')
              : ((payload && payload.error) ? payload.error : `Upload error (${xhr.status})`);
          trackEvent(isSavedUpgrade(payload, xhr.status) ? "upload_requires_upgrade" : "upload_error", {
            source,
            ...xhr.transferAnalytics(),
            recording_input: recordingInput,
            status_code: xhr.status,
            error_type: isSavedUpgrade(payload, xhr.status) ? 'plan_limit' : classifyError(msg),
            error_code: isSavedUpgrade(payload, xhr.status) ? 'free_duration_limit' : uploadErrorCode(payload, xhr.status),
            operation: payload?.operation || 'upload',
            retry_count: payload?.retry_count || 0,
          });
          if (payload?.requires_upgrade && payload?.id) {
            rememberRequiresUpgradeRecord(payload, recordingFile.name, source, options);
            resetUploadPopupState();
            showRequiresUpgradeModal(recordingFile.name, msg, payload);
            return;
          }
          // Handle here (don't throw inside async handler)
          resetUploadPopupState();
          if (showUploadRecovery(payload, xhr.status)) return;
          if (shouldOfferUpgrade(msg)) {
            setModalTitle("Upgrade required");
            setModalContent(
              `Failed to upload recording: ${msg}\n\nFree plan supports files up to 35 minutes. Upgrade to transcribe up to 10 hours per file and 3 GB uploads.`
            );
            setModalShowUpgradeCTA(true);
          } else {
            setModalTitle("Recording Upload Error");
            setModalContent(`Failed to upload recording: ${msg}`);
            setModalShowUpgradeCTA(false);
          }
          setShowModal(true);
          return;
        }

        try {
          rememberCheckoutUploadedRecord(payload?.id);
          trackUploadCompletion(xhr, {
            source,
            recording_input: recordingInput,
            status_code: xhr.status,
            has_duration: Boolean(payload?.duration),
          });
          // Start transcription immediately after successful upload
          await startTranscription(payload.id, token, options);
          
          // Update UI
          setRecentFiles(prev => [
            {
              id: payload.id,
              original_filename: recordingFile.name,
              new_filename: payload.new_filename,
              uploaded_time: new Date().toISOString().slice(0,19).replace('T',' '),
              duration: payload.duration || null,
              status: "transcribing",
              started_time: new Date().toISOString().slice(0,19).replace('T',' '),
            },
            ...prev
          ]);

          // Refresh history and show success
          fetchHistory();
          toast("Recording uploaded! Transcription in progress…", 5000);
          
          // Close the popup after successful transcription start
          resetUploadPopupState();
        } catch (transcriptionError) {
          // If transcription fails, still close popup but show error
          resetUploadPopupState();
          if (transcriptionError.recoveryShown) return;
          const msg = transcriptionError?.message || "Unknown error";
          if (shouldOfferUpgrade(msg)) {
            setModalTitle("Upgrade required");
            setModalContent(
              `Failed to start transcription: ${msg}\n\nFree plan supports files up to 35 minutes. Upgrade to transcribe up to 10 hours per file and 3 GB uploads.`
            );
            setModalShowUpgradeCTA(true);
          } else {
            setModalTitle("Transcription Start Error");
            setModalContent(`Failed to start transcription: ${msg}`);
            setModalShowUpgradeCTA(false);
          }
          setShowModal(true);
        }
      };

      xhr.onerror = () => {
        setIsUploading(false);
        trackEvent("upload_error", {
          source,
          ...xhr.transferAnalytics(),
          recording_input: recordingInput,
          error_type: classifyError(xhr.error),
          error_code: uploadErrorCode(xhr.error),
          operation: xhr.error?.stage || 'upload',
          retry_count: xhr.error?.retry_count || 0,
        });
        resetUploadPopupState();
        setModalTitle("Recording Upload Error");
        setModalContent(xhr.error?.canResume ? t('upload.selectSameToResume') : xhr.error?.message || t('upload.selectSameToResume'));
        setModalShowUpgradeCTA(false);
        setShowModal(true);
      };

      xhr.onabort = () => {
        setIsUploading(false);
        trackEvent("upload_canceled", {
          source,
          ...xhr.transferAnalytics(),
          recording_input: recordingInput,
          error_code: 'upload_cancelled',
          operation: xhr.operation,
          retry_count: xhr.retryCount || 0,
        });
        resetUploadPopupState();
        if (!uploadCancelToastRef.current) {
          setToastMessage("Upload canceled.");
          setShowToast(true);
          setTimeout(() => setShowToast(false), 3000);
        }
      };

      xhr.send();

    } catch (error) {
      setIsUploading(false);
      // Synchronous failure before XHR started (e.g., no session)
      const msg = error?.message || "Unknown error";
      trackEvent("upload_error", {
        source,
        recording_input: recordingInput,
        error_type: classifyError(error),
        stage: "prepare",
      });
      resetUploadPopupState();
      if (shouldOfferUpgrade(msg)) {
        setModalTitle("Upgrade required");
        setModalContent(
          `Failed to upload recording: ${msg}\n\nFree plan supports files up to 35 minutes. Upgrade to transcribe up to 10 hours per file and 3 GB uploads.`
        );
        setModalShowUpgradeCTA(true);
      } else {
        setModalTitle("Recording Upload Error");
        setModalContent(`Failed to upload recording: ${msg}`);
        setModalShowUpgradeCTA(false);
      }
      setShowModal(true);
    }
  };
  
  // Handle popup submission
  const handleUploadPopupSubmit = async (options, recordedBlob = null) => {
    if (recordingMode === 'recording' && recordedBlob) {
      const recordingSource = recordingInput === 'tab-mic'
        ? 'recording_tab_mic'
        : (recordingInput === 'tab' ? 'recording_tab' : 'recording_mic');
      const optionsWithTracking = {
        ...options,
        tracking_source: recordingSource,
      };
      trackEvent("recording_submit", {
        source: recordingSource,
        size_mb: sizeToMb(recordedBlob.size),
        size_bucket: sizeBucket(recordedBlob.size),
      });
      // Handle recording submission
      try {
        // Convert blob to file with appropriate extension based on MIME type
        const getExtensionFromMimeType = (mimeType) => {
          if (mimeType.includes('opus')) return '.opus';
          if (mimeType.includes('webm')) return '.webm';
          if (mimeType.includes('ogg')) return '.ogg';
          if (mimeType.includes('mp4')) return '.mp4';
          return '.webm'; // fallback
        };
        
        const extension = getExtensionFromMimeType(recordedBlob.type);
        // DIFFERENTIATE CAPTURE SOURCE for server-side usage analytics
        const base = recordingInput === 'tab-mic'
          ? 'recording-tab-mic'
          : (recordingInput === 'tab' ? 'recording-tab' : 'recording');
        const recordingFile = new File([recordedBlob], `${base}${extension}`, { type: recordedBlob.type });
        
        // Upload the recording (this will handle closing the popup)
        await uploadRecording(recordingFile, optionsWithTracking);
      } catch (error) {
        console.error("Failed to upload recording:", error);
        trackEvent("recording_submit_error", {
          source: recordingSource,
          error_type: classifyError(error),
        });
        resetUploadPopupState();
        const msg = error?.message || "Unknown error";
        if (shouldOfferUpgrade(msg)) {
          setModalTitle("Upgrade required");
          setModalContent(
            `Failed to upload recording: ${msg}\n\nFree plan supports files up to 35 minutes. Upgrade to transcribe up to 10 hours per file and 3 GB uploads.`
          );
          setModalShowUpgradeCTA(true);
        } else {
          setModalTitle("Recording Upload Error");
          setModalContent(`Failed to upload recording: ${msg}`);
          setModalShowUpgradeCTA(false);
        }
        setShowModal(true);
      }
    } else if (recordingMode === 'upload') {
      const localItems = uploadItemsRef.current || [];
      const jobIdToStart = currentJobId;
      const uploadFile = currentUploadFile;

      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) throw new Error("No active session");
        const token = session.access_token;

        const source = localItems.length > 1
          ? "local_batch"
          : (uploadFile?.size === 0 ? "url_import" : "local_upload");
        const optionsWithTracking = {
          ...options,
          tracking_source: source,
        };
        trackEvent("transcription_options_submit", {
          source,
          file_count: localItems.length || 1,
          language: optionsWithTracking.language || "auto",
          diarize: optionsWithTracking.diarize,
          timestamps: optionsWithTracking.timestamps,
          speaker_count_mode: optionsWithTracking.min_speakers || optionsWithTracking.max_speakers ? "specified" : "auto",
        });

        if (localItems.length > 0) {
          const readyItems = localItems.filter(item => item.jobId && item.status !== "requires_upgrade");

          if (readyItems.length > 0) {
            await Promise.all(readyItems.map(async (item) => {
              try {
                await startTranscription(item.jobId, token, optionsWithTracking);
              } catch (transcriptionError) {
                const msg = transcriptionError?.message || "Unknown error";
                updateUploadItem(item.id, { status: "start_error", error: msg });
                if (transcriptionError.recoveryShown) return;
                if (shouldOfferUpgrade(msg)) {
                  showUpgradeModalOnce(
                    `Failed to start transcription for ${item.file.name}: ${msg}\n\nFree plan supports files up to 35 minutes. Upgrade to transcribe up to 10 hours per file and 3 GB uploads.`
                  );
                } else {
                  toast(`Failed to start ${item.file.name}: ${msg}`, 4000);
                }
              }
            }));
          }

          const hasPendingUploads = localItems.some(
            item => item.status === "queued" || item.status === "uploading"
          );
          if (hasPendingUploads) {
            setPendingStartOptions(optionsWithTracking);
            toast(
              readyItems.length
                ? "Starting available files - I'll start the rest as they finish uploading."
                : "Got it - I'll start as soon as the uploads finish.",
              4000
            );
          } else {
            setPendingStartOptions(null);
            resetUploadPopupState();
          }
          return;
        }

        if (jobIdToStart) {
          // normal path: we already have an id
          await startTranscription(jobIdToStart, token, optionsWithTracking);
          resetUploadPopupState();
        } else if (isUploading) {
          // queue start-until-id
          setPendingStartOptions(optionsWithTracking);
          toast("Got it - I'll start as soon as the upload finishes.", 4000);
          // you *may* close the popup here; queued start will still happen
          resetUploadPopupState();
        } else {
          // "Finalizing" window: upload finished but server hasn't replied with id yet
          setPendingStartOptions(optionsWithTracking);
          toast(t("overview.finalizingUpload"), 4000);
          resetUploadPopupState();
        }
      } catch (error) {
        console.error("Failed to start transcription:", error);
        trackEvent("transcription_options_error", {
          source: "upload_popup",
          error_type: classifyError(error),
        });
        resetUploadPopupState();
        if (error.recoveryShown) return;
        const msg = error?.message || "Unknown error";
        if (shouldOfferUpgrade(msg)) {
          setModalTitle("Upgrade required");
          setModalContent(
            `Failed to start transcription: ${msg}\n\nFree plan supports files up to 35 minutes. Upgrade to transcribe up to 10 hours per file and 3 GB uploads.`
          );
          setModalShowUpgradeCTA(true);
        } else {
          setModalTitle("Transcription Start Error");
          setModalContent(`Failed to start transcription: ${msg}`);
          setModalShowUpgradeCTA(false);
        }
        setShowModal(true);
      }
    }
  };
  
  // Handle popup close
  const handleUploadPopupClose = () => {
    const hasJobIds = uploadItemsRef.current.some(item => item.jobId) || !!currentJobId;
    trackEvent("upload_popup_close", {
      mode: recordingMode,
      recording_input: recordingMode === "recording" ? recordingInput : undefined,
      upload_in_progress: isUploading,
      has_job_id: hasJobIds,
    });
    // If upload is in progress, cancel it
    if (isUploading) {
      cancelUpload();
    }
    // Clear any pending start options
    setPendingStartOptions(null);
    // Reset recording mode
    setRecordingMode('upload');
    setIsRecording(false);
    setAutoStartRecording(false);
    setUrlInput("");
    resetUploadPopupState();
  };

  const deleteFile = async (recordId) => {
    try {
      trackEvent("transcription_delete_attempt");
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("No active session");
      const token = session.access_token;
      const response = await fetch(`${API_BASE}/delete/${recordId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Deletion error");
      }
      historyFilesRef.current = historyFilesRef.current.filter(file => file.id !== recordId);
      setRecentFiles(historyFilesRef.current);
      fetchHistory();
      trackEvent("transcription_deleted");
      setToastMessage("File deleted.");
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3000);
    } catch (error) {
      console.error("Deletion failed:", error);
      trackEvent("transcription_delete_error", { error_type: classifyError(error) });
      setModalTitle("Deletion Error");
      setModalContent("Deletion failed: " + error.message);
      setShowModal(true);
    }
  };

  // ---------- DnD & file input ----------
  const handleFilesSelected = (fileList, source = "file_picker") => {
    const files = Array.from(fileList || []);
    setAutoStartRecording(false);
    if (!files.length) return;
    trackEvent("upload_intent", {
      ...getUploadTrackingParams(files, source),
      ui_surface: "dashboard_upload",
    });
    trackEvent("upload_select", getUploadTrackingParams(files, source));
    const replaceSavedUpload = source === "landing_drop" && !isUploading && !isRecording && !isImportingUrl &&
      uploadItemsRef.current.length > 0 && uploadItemsRef.current.every(item =>
        item.jobId && !["queued", "uploading"].includes(item.status));
    if (isUploading || isRecording || isImportingUrl || (showUploadPopup && !replaceSavedUpload)) {
      trackEvent("upload_select_blocked", {
        source,
        reason: isUploading ? "upload_in_progress" : "popup_open",
      });
      toast(guestCopy.busy, 5000);
      return;
    }
    if (showUploadPopup && replaceSavedUpload) handleUploadPopupClose();

    const oversize = files.filter(file => fileExceedsUploadLimit(file, isPremium || !accountReady));
    const allowed = files.filter(file => !fileExceedsUploadLimit(file, isPremium || !accountReady));

    if (oversize.length > 0) {
      trackEvent("upload_rejected", {
        ...getUploadTrackingParams(oversize, source),
        reason: isPremium ? "premium_size_limit" : "free_size_limit",
        error_code: isPremium ? "premium_size_limit" : "free_size_limit",
      });
      if (!isPremium) {
        trackFreeProductIntent(PRODUCT_INTENT_EVENTS.LIMIT_REACHED, {
          ...getUploadTrackingParams(oversize, source),
          limit_type: "free_size_limit",
          reason: "free_size_limit",
        }, {
          dedupeKey: `free_size:${todayKey()}:${source}`,
        });
      }
      const shown = oversize.slice(0, 3).map(f => f.name).join(", ");
      const extra = oversize.length > 3 ? `, and ${oversize.length - 3} more` : "";
      const premiumOversize = !isPremium
        ? oversize.filter(file => fileExceedsUploadLimit(file, true))
        : [];
      const shownPremiumOversize = premiumOversize.slice(0, 3).map(file => file.name).join(", ");
      const premiumOversizeExtra = premiumOversize.length > 3
        ? `, and ${premiumOversize.length - 3} more`
        : "";
      const premiumOversizeSubject = premiumOversize.length === 1
        ? "This selected file is"
        : "These selected files are";
      setModalTitle(isPremium ? "File too large" : "Upgrade required");
      setModalContent(isPremium
        ? `These files exceed the Premium plan limit (${uploadLimitLabel(true)}) and were skipped: ${shown}${extra}.\n\nChoose smaller files or split them before uploading.`
        : premiumOversize.length > 0
          ? `These files exceed the Free plan limit (${uploadLimitLabel(false)}) and were skipped: ${shown}${extra}.\n\nPremium supports files up to ${uploadLimitLabel(true)}. ${premiumOversizeSubject} larger than that: ${shownPremiumOversize}${premiumOversizeExtra}. You can still upgrade for the other Premium benefits, but the oversized files must be split or compressed before uploading.`
          : `These files exceed the Free plan limit (${uploadLimitLabel(false)}) and were skipped: ${shown}${extra}.\n\nUpgrade to upload up to ${uploadLimitLabel(true)} and 10 h per file.`
      );
      if (!isPremium && oversize.length === 1 && premiumOversize.length === 0) {
        setModalUpgradeRecord({ original_filename: oversize[0].name, size_bytes: oversize[0].size });
        setLargeFile(oversize[0]);
      }
      setModalShowUpgradeCTA(!isPremium);
      setShowModal(true);
    }

    if (allowed.length > 0) {
      const attempt = urlRecoveryAttemptRef.current;
      const recovery = attempt?.action === 'upload_file' && attempt.expires > Date.now() ? attempt.params : undefined;
      if (attempt?.action === 'upload_file') urlRecoveryAttemptRef.current = null;
      uploadFiles(allowed, recovery);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    handleFilesSelected(e.dataTransfer.files, "drag_drop");
  };
  const handleDragOver = (e) => { e.preventDefault(); setIsDragging(true); };
  const handleDragLeave = (e) => { e.preventDefault(); setIsDragging(false); };
  const handleClickUpload = () => {
    trackEvent("upload_picker_open");
    document.getElementById("fileInput").click();
  };
  const handleFileChange = (e) => {
    handleFilesSelected(e.target.files, "file_picker");
    // Reset the input so selecting the same file again re-triggers change
    e.target.value = "";
  };

  // Optional: quick "aha!" demo if you configure CONFIG.SAMPLE_AUDIO_URL
  const handleUseSample = async () => {
    try {
      if (!CONFIG.SAMPLE_AUDIO_URL) return;
      trackEvent("sample_upload_start");
      const res = await fetch(CONFIG.SAMPLE_AUDIO_URL);
      if (!res.ok) throw new Error("Failed to load sample");
      const blob = await res.blob();
      const ext = (blob.type && blob.type.split("/")[1]) ? `.${blob.type.split("/")[1]}` : ".mp3";
      const file = new File([blob], `sample${ext}`, { type: blob.type || "audio/mpeg" });
      await uploadFiles([file]);
      trackEvent("sample_upload_loaded", {
        size_mb: sizeToMb(file.size),
        size_bucket: sizeBucket(file.size),
      });
    } catch (e) {
      trackEvent("sample_upload_error", { error_type: classifyError(e) });
      setModalTitle("Sample Error");
      setModalContent(e.message || "Could not load the sample file.");
      setShowModal(true);
    }
  };

  // Helper function to detect if URL is likely a podcast link
  const isPodcastUrl = (url) => {
    const u = url.toLowerCase();
    return (
      // Apple Podcasts
      u.includes('podcasts.apple.com') ||
      u.includes('/id') ||
      // RSS feeds
      u.includes('.rss') ||
      u.includes('.xml') ||
      u.includes('/feed') ||
      u.includes('/rss') ||
      // Common podcast platforms (excluding Spotify episodes which are handled separately)
      u.includes('anchor.fm') ||
      u.includes('buzzsprout.com') ||
      u.includes('libsyn.com') ||
      u.includes('stitcher.com')
    );
  };

  const getUrlImportType = (url) => {
    let host = "";
    try { host = new URL(url).hostname.toLowerCase(); } catch (_) {}
    if (host === 'youtu.be' || host.endsWith('.youtu.be') || host === 'youtube.com' || host.endsWith('.youtube.com') || host === 'youtube-nocookie.com' || host.endsWith('.youtube-nocookie.com')) return 'youtube';
    if (host === 'drive.google.com' || host === 'docs.google.com') return 'google_drive';
    if (host === 'soundcloud.com' || host.endsWith('.soundcloud.com') || host === 'snd.sc') return 'soundcloud';
    return 'media_file';
  };

  // Helper function to detect Spotify episode URLs (which can't be transcribed)
  const isSpotifyEpisodeUrl = (url) => {
    const u = url.toLowerCase();
    return u.includes('open.spotify.com/show/');
  };

  const applyImportResult = async ({ response: res, payload, task }, { url = '', inputMethod = 'reconnect', session }) => {
    const family = payload?.source_family || task?.source_family;
    const urlType = task?.kind === 'youtube' ? 'youtube' : ['youtube','soundcloud','google_drive','podcast'].includes(family) ? family : getUrlImportType(url);
    const token = session.access_token;
    const attempt = urlRecoveryAttemptRef.current;
    if (attempt?.action === 'retry') {
      if (attempt.expires > Date.now()) trackImportRecovery(attempt.params,
        res.ok || isSavedUpgrade(payload, res.status) ? 'succeeded' : 'failed', res.status);
      urlRecoveryAttemptRef.current = null;
    }
    if (payload?.id && isActiveTranscriptionStatus(payload.status)) observePendingJob(payload, session.user?.id);
      if (!res.ok) {
        const msg = payload?.error || `Import failed (${res.status})`;
        const failure = importFailure(payload, res.status, urlType);
        setUrlRecovery(failure);
        if (!payload?.requires_upgrade) setUrlImportError(t(failure.messageKey === 'import.fileTooLarge'
          ? isPremium ? 'import.premiumFileTooLarge' : 'import.freeFileTooLarge' : failure.messageKey));
        trackEvent(isSavedUpgrade(payload, res.status) ? "url_import_requires_upgrade" : "url_import_error", {
          source: "paste_link_form",
          input_method: inputMethod,
          url_type: urlType,
          status_code: res.status,
          error_type: isSavedUpgrade(payload, res.status) ? 'plan_limit' : failure.kind,
          error_code: isSavedUpgrade(payload, res.status) ? 'free_duration_limit' : failure.code,
          ...importMeasurements(payload),
        });
        if (failure.kind !== 'auth_temporary' && showUploadRecovery(payload, res.status)) return;
        if (payload?.requires_upgrade && payload?.id) {
          const fileName = payload.original_filename || (url ? filenameFromUrl(url) : t('import.importedFile'));
          rememberRequiresUpgradeRecord(payload, fileName, "url_import", pendingStartOptionsRef.current || {});
          showRequiresUpgradeModal(fileName, msg, payload);
          setUrlInput("");
          urlInputMethodRef.current = null;
          urlImportIntentTrackedRef.current = false;
          return;
        }
        if (!isPremium && shouldOfferUpgrade(msg)) {
          setModalTitle("Upgrade required");
          setModalContent(
            `Import failed: ${msg}\n\nFree plan supports files up to 35 minutes and 50 MB. Upgrade to transcribe up to 10 hours and 3 GB per file.`
          );
          setModalShowUpgradeCTA(true);
          setShowModal(true);
        }

        return;
      }

      if (!payload?.id) throw new Error('The import did not return a transcription.');
      clearPendingUrlImport();
      setUrlRestored(false);
      if (isActiveTranscriptionStatus(payload.status)) {
        await Promise.all([fetchHistory(),fetchQuota()]);
        toast(t('import.alreadyRunning'),4000);
        return;
      }
      if (isCompletedTranscriptionStatus(payload?.status)) {
        observeJobOutcome({ ...payload, source: urlType === "youtube" ? "youtube" : "web" }, session.user?.id, "url_import");
        trackEvent("url_import_completed", {
          source: "paste_link_form",
          input_method: inputMethod,
          url_type: urlType,
          caption_type: payload.caption_type,
          caption_language: payload.caption_language_code,
        });
        await Promise.all([fetchHistory(), fetchQuota()]);
        if (payload.id) await handleFileClick(payload.id, payload.title || 'YouTube transcript');
        toast(t(urlType === "youtube" ? "import.captionsImported" : "import.backgroundReady"), 4000);
        setUrlInput("");
        urlInputMethodRef.current = null;
        urlImportIntentTrackedRef.current = false;
        return;
      }

      // Open the same options popup flow you already use for local uploads:
      setRecordingMode("upload");
      setAutoStartRecording(false);
      setCurrentJobId(payload.id);
      setCurrentUploadFile({ name: payload.original_filename || (url ? filenameFromUrl(url) : t('import.importedFile')), size: 0 }); // pseudo file for display
      rememberCheckoutUploadedRecord(payload.id);
      trackEvent("url_import_uploaded", {
        source: "paste_link_form",
        input_method: inputMethod,
        url_type: urlType,
        has_duration: Boolean(payload.duration),
      });

      // If there was a pending start queued, execute it now since we have the jobId
      if (pendingStartOptionsRef.current) {
        try {
          await startTranscription(payload.id, token, {
            ...pendingStartOptionsRef.current,
            tracking_source: pendingStartOptionsRef.current.tracking_source || "url_import",
          });
          setPendingStartOptions(null);
          toast("Link imported and transcription started!", 4000);
          setUrlInput("");
          urlInputMethodRef.current = null;
          urlImportIntentTrackedRef.current = false;
          // Don't show popup since transcription already started
          return;
        } catch (transcriptionError) {
          // If start fails, still show popup for manual retry
          if (transcriptionError.recoveryShown) return;
          const msg = transcriptionError?.message || "Unknown error";
          if (shouldOfferUpgrade(msg)) {
            setModalTitle("Upgrade required");
            setModalContent(
              `Link imported but transcription failed: ${msg}\n\nFree plan supports files up to 35 minutes and 50 MB. Upgrade to transcribe up to 10 hours and 3 GB per file.`
            );
            setModalShowUpgradeCTA(true);
            setShowModal(true);
          } else {
            toast(`Link imported but start failed: ${msg}`, 4000);
          }
          setPendingStartOptions(null);
        }
      }

      setShowUploadPopup(true);
      // IMPORTANT: Let the popup own the countdown for URL imports.
      // Removing the headless auto-start avoids a double start race.

      // (Optional) Optimistic list insert so the card shows up instantly
      setRecentFiles(prev => [
        {
          id: payload.id,
          original_filename: payload.original_filename || (url ? filenameFromUrl(url) : t('import.importedFile')),
          new_filename: payload.new_filename,
          uploaded_time: new Date().toISOString().slice(0,19).replace('T',' '),
          duration: payload.duration || null,
          status: "uploaded",
          started_time: null,
        },
        ...prev.filter(file => file.id !== payload.id)
      ]);

      // Refresh list so the new item appears with the real original filename from DB
      await fetchHistory();
      toast("Link imported - ready to start.", 4000);
      setUrlInput("");
      urlInputMethodRef.current = null;
      urlImportIntentTrackedRef.current = false;
  };

  const finishImportResult = async (result, context, force = false) => {
    if (result.task && !force && !workspaceVisible()) {
      setImportTask(result.task);
      fetchHistory();
      return;
    }
    if (force && result.task) {
      result = await refreshImportOutcome(API_BASE,result.task,context.session);
      if (metadataOwnerRef.current!==context.session.user?.id) return;
    }
    await applyImportResult(result, context);
    trackEvent(force ? 'source_import_resumed' : 'source_import_result_shown', {
      operation:'import_result',event_source:force ? 'explicit_resume' : 'live_result',
      url_type:result.payload?.source_family || result.task?.source_family || 'other',
      status_code:result.response.status, result_type:result.payload?.is_preview ? 'preview' : 'full',
      result_state:['text','empty'].includes(result.payload?.result_state) ? result.payload.result_state : 'unknown',
    });
    if (result.task) {
      try { await acknowledgeImport(API_BASE, result.task, context.session); }
      catch (error) { trackDiagnostic('import_acknowledge_error', error, { operation: 'import_acknowledge' }); }
    }
    setImportTask(null);
  };

  const handleImportFromUrl = async (entryUrl, entryMethod) => {
    if (importActiveRef.current || isImportingUrl || isUploading || showUploadPopup) return;
    if (entryMethod !== 'retry') urlRecoveryAttemptRef.current = null;
    setUrlImportError('');
    const url = typeof entryUrl === 'string' ? entryUrl.trim() : urlInput.trim();
    const inputMethod = entryMethod || urlInputMethodRef.current || "unknown";
    if (typeof entryUrl === 'string') setUrlInput(url);
    if (soundCloudLinkIssue(url)) {
      setUrlImportError(t('overview.soundCloudSingleTrack'));
      trackEvent('url_import_error', { source: 'paste_link_form', url_type: 'soundcloud', error_type: 'input',
        error_code: 'source_folder_or_playlist', operation: 'url_validation' });
      return;
    }
    if (!isLikelyPublicHttpUrl(url)) {
      trackEvent("url_import_error", {
        source: "paste_link_form",
        input_method: inputMethod,
        error_type: "invalid_url",
        error_code: 'source_invalid_url', operation: 'url_validation',
      });
      toast("Please paste a valid http(s) URL.", 3000);
      return;
    }

    // Check if this is a Spotify episode URL (which can't be transcribed)
    if (isSpotifyEpisodeUrl(url)) {
      trackEvent("url_import_error", {
        source: "paste_link_form",
        input_method: inputMethod,
        url_type: "spotify_show",
        error_type: "unsupported_source",
        error_code: 'source_unsupported_media', operation: 'url_validation',
      });
      setModalTitle("Spotify Episodes Not Supported");
      setModalContent(
        "Spotify episodes cannot be transcribed directly because Spotify doesn't provide public audio URLs. " +
        "Try these alternatives:\n\n" +
        "• Find the episode on Apple Podcasts\n" +
        "• Check the show's official website\n" +
        "• Download the audio and upload it directly"
      );
      setModalShowUpgradeCTA(false);
      setShowModal(true);
      return;
    }

    // Check if this is a podcast URL
    if (isPodcastUrl(url)) {
      trackEvent("url_import_attempt", {
        source: urlRestored ? "upgrade_resume" : "paste_link_form",
        input_method: inputMethod,
        url_type: "podcast",
      });
      // Resolve with the same bounded request/cancellation behavior as imports.
      const controller = new AbortController();
      importActiveRef.current = true;
      try {
        setIsImportingUrl(true);
        toast("Resolving podcast...", 3000);

        const session = await ensureSession();
        const token = session?.access_token;
        if (!token) throw new Error("No active session");

        importObserverRef.current = controller;
        const resolveData = await resolvePodcast(API_BASE, url, session, controller.signal);
        const retry = urlRecoveryAttemptRef.current;
        if (retry?.action === 'retry') {
          if (retry.expires > Date.now()) trackImportRecovery(retry.params, 'succeeded', 200);
          urlRecoveryAttemptRef.current = null;
        }
        trackEvent("podcast_resolve_success", {
          source: "paste_link_form",
          input_method: inputMethod,
          has_episode_match: Boolean(resolveData.episode),
          episode_count: Array.isArray(resolveData.episodes) ? resolveData.episodes.length : undefined,
        });
        
        // Show podcast selection popup with resolved data
        setRecordingMode("upload");
        setAutoStartRecording(false);
        setCurrentUploadFile(null);
        setShowUploadPopup(true);
        setUrlInput("");
        urlInputMethodRef.current = null;
        urlImportIntentTrackedRef.current = false;
        
        // Pre-populate the podcast URL in the popup
        // We'll need to pass this data to the popup somehow
        // For now, we'll store it in a way the popup can access it
        window.podcastResolveData = { url, resolveData };
        
      } catch (err) {
        if (err?.code === 'request_cancelled') return;
        const retry = urlRecoveryAttemptRef.current;
        if (retry?.action === 'retry') {
          if (retry.expires > Date.now()) trackImportRecovery(retry.params, 'failed', err?.status);
          urlRecoveryAttemptRef.current = null;
        }
        const failure = importFailure({}, err?.status, 'podcast', err);
        setUrlRecovery(failure);
        setUrlImportError(t(failure.messageKey));
        trackDiagnostic('podcast_resolve_error', err, {
          source: 'paste_link_form', input_method: inputMethod, url_type: 'podcast',
          operation: 'podcast_resolve', error_type: failure.kind,
          error_code: err?.code === 'request_timeout' ? 'request_timeout' : failure.code,
        });
        if (failure.kind !== 'auth_temporary') showUploadRecovery({ code: failure.code }, err?.status);
      } finally {
        if (importObserverRef.current === controller) importObserverRef.current = null;
        importActiveRef.current = false;
        setIsImportingUrl(false);
      }
      return;
    }

    // Regular URL import for audio/video files
    try {
      importActiveRef.current = true;
      const urlType = getUrlImportType(url);
      trackEvent("url_import_attempt", {
        source: urlRestored ? "upgrade_resume" : "paste_link_form",
        input_method: inputMethod,
        url_type: urlType,
        ...importMeasurements({ source_family: sourceFamily(url) }),
      });
      setIsImportingUrl(true);
      upgradeModalShownRef.current = false;

      const session = await ensureSession();
      const token = session?.access_token;
      if (!token) throw new Error("No active session");

      const controller = new AbortController();
      importObserverRef.current = controller;
      importActiveRef.current = true;
      const getSession = async () => (await supabase.auth.getSession()).data.session;
      const result = await createImport(API_BASE, url, urlType, session, getSession, {
        signal: controller.signal, onState: setImportTask,
        onReconnect: (error, stage) => trackDiagnostic('import_status_error', error, stage),
        onRecovered: params => trackEvent('import_recovery_success', params),
      });
      await finishImportResult(result, { url, inputMethod, session });
    } catch (err) {
      if (err?.code === "request_cancelled") { setImportTask(null); return; }
      const attempt = urlRecoveryAttemptRef.current;
      if (attempt?.action === 'retry') {
        if (attempt.expires > Date.now()) trackImportRecovery(attempt.params, 'failed', err?.status);
        urlRecoveryAttemptRef.current = null;
      }
      setImportTask(null);
      const failure = importFailure({}, err?.status, getUrlImportType(url), err);
      setUrlRecovery(failure);
      setUrlImportError(t(failure.messageKey));
      trackEvent("url_import_error", {
        source: "paste_link_form", input_method: inputMethod,
        url_type: getUrlImportType(url), error_type: failure.kind, error_code: failure.code,
        operation: 'import_submit', ...requestMeasurements(err),
        ...importMeasurements({ source_family: sourceFamily(url) }),
      });
    } finally {
      importActiveRef.current = false;
      setIsImportingUrl(false);
    }
  };

  upgradeUrlImportHandler.current = handleImportFromUrl;
  useEffect(() => {
    if (!accountReady || !currentUser || currentUser.is_anonymous || !isPremium || !contextIsPremium
        || !urlRestored || isUploading || isImportingUrl || showUploadPopup || importActiveRef.current || importTask) return;
    const saved = pendingUrlImport(currentUser);
    if (!saved?.resumeAfterUpgrade || urlInput.trim() !== saved.url) return;
    const claimed = claimPendingUrlImport(currentUser);
    if (!claimed) return;
    trackEvent('url_import_upgrade_resumed', { source: 'upgrade_resume', url_type: getUrlImportType(claimed.url) });
    upgradeUrlImportHandler.current(claimed.url, 'upgrade_resume');
  }, [accountReady, currentUser, isPremium, contextIsPremium, urlRestored, urlInput,
      isUploading, isImportingUrl, showUploadPopup, importTask]);

  // ====== TAB AUDIO CAPTURE SUPPORT (Chromium) ======
  // Conservative detection: require getDisplayMedia + a Chromium-like UA hint.
  const isTabAudioSupported = useMemo(() => {
    try {
      const hasGDM = !!navigator?.mediaDevices?.getDisplayMedia;
      if (!hasGDM) return false;
      // UA hints (Chromium/Edge/Opera). This avoids showing on Safari/Firefox,
      // which don't reliably expose tab/system audio to the web.
      const ua = navigator.userAgent || "";
      const brands = (navigator.userAgentData?.brands || []).map(b => b.brand).join(" ");
      const chromiumish = /Chrome|Chromium|Edg|OPR/i.test(ua) || /Chromium|Google Chrome|Microsoft Edge|Opera/i.test(brands);
      return chromiumish;
    } catch {
      return false;
    }
  }, []);

  // Start the "record another tab" flow: open the existing popup in recording mode,
  // but instruct it to use getDisplayMedia instead of the microphone.
  const handleTabCaptureClick = async () => {
    try {
      await ensureSession();
      trackEvent("recording_intent", {
        source: "recording_tab",
        supported: isTabAudioSupported,
        plan: planName,
      });
      trackEvent("recording_open", {
        source: "recording_tab",
        supported: isTabAudioSupported,
      });
      // Do NOT call getDisplayMedia here (avoid permission prompt before UI appears).
      // We just open the popup and let it request on "Start Recording" (or auto-start).
      setRecordingMode('recording');
      setRecordingInput('tab');
      setAutoStartRecording(false);      // Keep the browser permission request on an explicit gesture.
      setCurrentUploadFile(null);        // no file yet
      setShowUploadPopup(true);
    } catch (error) {
      console.error('Tab capture init failed:', error);
      trackEvent("recording_open_error", {
        source: "recording_tab",
        error_type: classifyError(error),
      });
      setModalTitle("Tab Capture Not Available");
      setModalContent("Your browser does not allow capturing another tab's audio. Please use Chrome/Edge or record with a microphone.");
      setShowModal(true);
    }
  };

  const handleTabMicCaptureClick = async () => {
    try {
      await ensureSession();
      trackEvent("recording_intent", {
        source: "recording_tab_mic",
        supported: isTabAudioSupported,
        plan: planName,
      });
      trackEvent("recording_open", {
        source: "recording_tab_mic",
        supported: isTabAudioSupported,
      });
      // The popup requests tab audio and microphone together, then mixes them client-side.
      setRecordingMode('recording');
      setRecordingInput('tab-mic');
      setAutoStartRecording(false);
      setCurrentUploadFile(null);
      setShowUploadPopup(true);
    } catch (error) {
      console.error('Tab + mic capture init failed:', error);
      trackEvent("recording_open_error", {
        source: "recording_tab_mic",
        error_type: classifyError(error),
      });
      setModalTitle("Tab + Microphone Capture Not Available");
      setModalContent("Your browser does not allow capturing another tab and microphone together. Please use Chrome/Edge or record with a microphone.");
      setShowModal(true);
    }
  };

  // ---------- recording functionality ----------
  const handleMicrophoneClick = async () => {
    try {
      await ensureSession();
      trackEvent("recording_intent", {
        source: "recording_mic",
        supported: typeof navigator !== "undefined" && Boolean(navigator.mediaDevices?.getUserMedia),
        plan: planName,
      });
      // Continue the user's recording action after guest verification finishes.
      trackEvent("recording_open", { source: "recording_mic", supported: true });
      
      // Set recording mode and show popup
      setRecordingMode('recording');
      setRecordingInput('mic');
      setAutoStartRecording(true);
      setCurrentUploadFile(null);
      setShowUploadPopup(true);
    } catch (error) {
      console.error('Microphone access denied:', error);
      trackEvent("recording_open_error", {
        source: "recording_mic",
        error_type: classifyError(error),
      });
      setModalTitle("Microphone Access Required");
      setModalContent("Please allow microphone access to use the recording feature. Check your browser permissions and try again.");
      setShowModal(true);
    }
  };

  // ---------- file details modal ----------
  const handleFileClick = async (recordId, filename) => {
    try {
      trackEvent("transcript_open_attempt");
      setCurrentRecordId(recordId);
      setModalShowUpgradeCTA(false);
      setModalTitle(filename);
      setActiveModalTab("transcript");
      // If cached, show immediately (then we're done)
      if (resultCache[recordId]) {
        const data = resultCache[recordId];
        trackTranscriptOpened("cache", data);
        setCurrentTranscriptionData(data);
        const hasSpeakers = !!data.has_diarization;
        const hasTimestamps = !!data.has_timestamps;
        const useShowSpeakers = hasSpeakers ? globalShowSpeakers : false;
        const useShowTime = hasTimestamps ? globalShowTime : false;
        setShowSpeakers(useShowSpeakers);
        setShowTime(useShowTime);
        setUseSpeakerPostprocessing(true);
        setModalContent(formatTranscriptionContent(data, useShowSpeakers, useShowTime, true));
        setShowModal(true);
        return;
      }

      setModalContent("Loading transcription...");
      setCurrentTranscriptionData(null);
      setShowModal(true);

      const { data: { session } } = await supabase.auth.getSession();
      const token = session.access_token;

      // Retry mechanism for race condition: if result isn't available yet for completed jobs
      let data = null;
      let retryCount = 0;
      const maxRetries = 5;
      
      while (retryCount < maxRetries) {
        try {
          data = await readResult(recordId, token);
          if (!data) return;
          if (['text','empty'].includes(data.result_state)
              || (data.transcription_text && data.transcription_text !== 'No transcription available')) break;
          delete resultCacheRef.current[recordId];
          // If we get here, the result might not be ready yet (race condition)
          if (retryCount < maxRetries - 1) {
            setModalContent(`Loading transcription... (retry ${retryCount + 1}/${maxRetries})`);
            await new Promise(resolve => setTimeout(resolve, 1000)); // Wait 1 second
            retryCount++;
            continue;
          }
          
          // Last retry failed, use whatever we got
          break;
          
        } catch (error) {
          if (retryCount < maxRetries - 1) {
            setModalContent(`Loading transcription... (retry ${retryCount + 1}/${maxRetries})`);
            await new Promise(resolve => setTimeout(resolve, 1000)); // Wait 1 second
            retryCount++;
            continue;
          }
          throw error; // Re-throw on final retry
        }
      }
      
      if (!data) {
        throw new Error("Failed to fetch transcription data after retries");
      }
      
      setResultCache(prev => ({ ...prev, [recordId]: data }));

      // Store the full data for toggle functionality
      setCurrentTranscriptionData(data);
      
      // Capabilities reported by server without sending heavy `full`
      const hasSpeakers = !!data.has_diarization;
      const hasTimestamps = !!data.has_timestamps;

      // Use global preferences, but only show speakers if this transcription has them
      const useShowSpeakers = hasSpeakers ? globalShowSpeakers : false;
      const useShowTime = hasTimestamps ? globalShowTime : false;
      
      setShowSpeakers(useShowSpeakers);
      setShowTime(useShowTime);
      setUseSpeakerPostprocessing(true);
      
      // Format and display content
      const formattedContent = formatTranscriptionContent(data, useShowSpeakers, useShowTime, true);
      setModalContent(formattedContent);
      trackTranscriptOpened("api", data, { retry_count: retryCount });
      
    } catch (error) {
      console.error("Error fetching file details:", error);
      trackEvent("transcript_open_error", { error_type: classifyError(error) });
      setModalTitle("Error");
      setModalContent(`Error fetching file details: ${error.message}`);
      setCurrentTranscriptionData(null);
    }
  };

  // Returning from the landing page opens a completed result, or focuses the
  // saved job's progress/start controls. A link never starts a paid/free job.
  const landingJobOpenRef = useRef(handleFileClick);
  landingJobOpenRef.current = handleFileClick;
  const landingJobHandledRef = useRef(null);
  useEffect(() => {
    const recordId = new URLSearchParams(window.location.search).get('record');
    if (!recordId || !currentUser || landingJobHandledRef.current === recordId) return;
    const file = recentFiles.find(item => item.id === recordId);
    if (!file) return;
    landingJobHandledRef.current = recordId;
    const url = new URL(window.location.href);
    url.searchParams.delete('record');
    window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
    if (['completed', 'succeeded'].includes(String(file.status).toLowerCase())) {
      landingJobOpenRef.current(file.id, file.original_filename);
    } else {
      const card = document.getElementById(`file-card-${file.id}`);
      card?.scrollIntoView({ block: 'center' });
      card?.focus({ preventScroll: true });
    }
  }, [currentUser, recentFiles]);

  // Handle speaker display toggle
  const handleSpeakerToggle = async () => {
    if (!currentTranscriptionData) return;

    let updatedData = currentTranscriptionData;

    const needFull =
      !updatedData.full &&
      updatedData.full_available &&
      !(updatedData.light_segments && updatedData.light_segments.length);

    if (needFull && currentRecordId) {
      try {
        setModalContent("Loading detailed view...");
        const { data: { session } } = await supabase.auth.getSession();
        const token = session?.access_token;
        const resp = await fetchWithTimeout(`${API_BASE}/result/${currentRecordId}?full=1`, {
          requestPurpose: 'open',
          headers: { Authorization: `Bearer ${token}` },
        }, 60000);
        if (resp.ok) {
          const fullData = await resp.json();
          updatedData = fullData; // use local for immediate render
          setResultCache(prev => ({ ...prev, [currentRecordId]: fullData }));
          setCurrentTranscriptionData(fullData);
        }
      } catch (_) {}
    }

    const newShowSpeakers = !showSpeakers;
    setShowSpeakers(newShowSpeakers);
    setGlobalShowSpeakers(newShowSpeakers);
    trackEvent("transcript_view_option_change", {
      option: "speakers",
      enabled: newShowSpeakers,
    });

    setModalContent(
      formatTranscriptionContent(updatedData, newShowSpeakers, showTime, useSpeakerPostprocessing)
    );
  };

  // Handle time display toggle
  const handleTimeToggle = async () => {
    if (!currentTranscriptionData) return;

    // We'll render using this variable so we don't rely on async state updates.
    let updatedData = currentTranscriptionData;

    // Lazy-load heavy `full` when needed (only if we don't already have light_segments)
    const needFull =
      !currentTranscriptionData.full &&
      currentTranscriptionData.full_available &&
      !(
        currentTranscriptionData.light_segments &&
        currentTranscriptionData.light_segments.length
      );

    if (needFull && currentRecordId) {
      try {
        setModalContent("Loading detailed view...");
        const { data: { session } } = await supabase.auth.getSession();
        const token = session.access_token;
        const resp = await fetchWithTimeout(`${API_BASE}/result/${currentRecordId}?full=1`, {
          requestPurpose: 'open',
          headers: { Authorization: `Bearer ${token}` },
        }, 60000);
        if (resp.ok) {
          const fullData = await resp.json();
          updatedData = fullData; // use the freshly fetched data for rendering below
          setResultCache(prev => ({ ...prev, [currentRecordId]: fullData }));
          setCurrentTranscriptionData(fullData);
        }
      } catch (_) {}
    }

    const newShowTime = !showTime;
    setShowTime(newShowTime);
    setGlobalShowTime(newShowTime); // Update global preference
    trackEvent("transcript_view_option_change", {
      option: "timestamps",
      enabled: newShowTime,
    });

    // Re-format content with new settings using the most up-to-date data
    const formattedContent = formatTranscriptionContent(updatedData, showSpeakers, newShowTime, useSpeakerPostprocessing);
    setModalContent(formattedContent);
  };

  const handleSpeakerPostprocessingToggle = () => {
    if (!currentTranscriptionData) return;
    const nextValue = !useSpeakerPostprocessing;
    setUseSpeakerPostprocessing(nextValue);
    trackEvent("transcript_view_option_change", {
      option: "speaker_postprocessing",
      enabled: nextValue,
    });
    setModalContent(formatTranscriptionContent(currentTranscriptionData, showSpeakers, showTime, nextValue));
  };

  const handleSaveSpeakerNames = async speakerNames => {
    try {
      const recordId = currentRecordId;
      resultRevisionsRef.current[recordId] = (resultRevisionsRef.current[recordId] || 0) + 1;
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw Object.assign(new Error('No active session'), { code: 'session_not_found' });
      const response = await fetchWithTimeout(`${API_BASE}/result/${recordId}/speakers`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ speaker_names: speakerNames }),
      });
      const data = await readJsonResponse(response);
      if (!response.ok) throw responseError(response, data);
      resultCacheRef.current = { ...resultCacheRef.current, [recordId]: data };
      resultRevisionsRef.current[recordId] += 1;
      setResultCache(previous => ({ ...previous, [recordId]: data }));
      if (currentRecordIdRef.current === recordId) {
        setCurrentTranscriptionData(data);
        const view = modalViewRef.current;
        setModalContent(view.activeModalTab === 'summary' ? formatSummaryContent(data)
          : formatTranscriptionContent(data, view.showSpeakers, view.showTime, view.useSpeakerPostprocessing));
      }
      trackEvent('speaker_names_saved', { speaker_count: Object.keys(speakerNames).length });
    } catch (error) {
      trackDiagnostic('speaker_names_save_error', error, { operation: 'speaker_names_save', error_component: 'SpeakerNamesEditor' });
      throw error;
    }
  };

  const handleModalTabChange = (tab) => {
    const summaryAvailability = getSummaryAvailability(currentTranscriptionData);
    if (tab === 'summary' && !showsSummaryTab(currentTranscriptionData, activeModalTab)) return;
    setActiveModalTab(tab);
    trackEvent("transcript_modal_tab", {
      tab,
      has_summary: summaryAvailability === "ready",
      summary_availability: summaryAvailability,
    });
    if (tab === "summary") {
      trackEvent(`summary_open_${summaryAvailability}`, {
        has_speakers: Boolean(currentTranscriptionData?.has_diarization),
        has_timestamps: Boolean(currentTranscriptionData?.has_timestamps),
      });
      setModalContent(formatSummaryContent(currentTranscriptionData));
      return;
    }
    setModalContent(formatTranscriptionContent(currentTranscriptionData, showSpeakers, showTime, useSpeakerPostprocessing));
  };

  const speakerPostprocessingStatus = getSpeakerPostprocessingMeta(currentTranscriptionData)?.status;
  const speakerReviewWaiting = Boolean(getSpeakerPostprocessingMeta(currentTranscriptionData)?.retry_scheduled);

  useEffect(() => {
    if (!showModal || !currentRecordId || isErrorModal) return;
    if (speakerPostprocessingStatus !== "processing") return;

    let cancelled = false;
    const refreshPostprocessingStatus = async () => {
      const revision = resultRevisionsRef.current[currentRecordId] || 0;
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const token = session?.access_token;
        if (!token) return;

        const response = await fetchWithTimeout(`${API_BASE}/result/${currentRecordId}?full=0`, {
          requestPurpose: 'refresh',
          method: "GET",
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok || cancelled) return;

        const data = await response.json();
        if (cancelled || revision !== (resultRevisionsRef.current[currentRecordId] || 0)) return;
        observeResultOutcome(data, currentUser?.id, 'speaker_review_poll');
        setResultCache(prev => ({ ...prev, [currentRecordId]: data }));
        setCurrentTranscriptionData(data);
        if (data.short_summary) {
          setRecentFiles(prev => prev.map(file => (
            file.id === currentRecordId ? { ...file, short_summary: data.short_summary } : file
          )));
        }
        setModalContent(
          activeModalTab === "summary"
            ? formatSummaryContent(data)
            : formatTranscriptionContent(data, showSpeakers, showTime, useSpeakerPostprocessing)
        );
      } catch (_) {}
    };

    const interval = setInterval(refreshPostprocessingStatus, speakerReviewWaiting ? 30000 : 5000);
    refreshPostprocessingStatus();
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [showModal, currentRecordId, isErrorModal, speakerPostprocessingStatus, speakerReviewWaiting, activeModalTab, showSpeakers, showTime, useSpeakerPostprocessing, formatTranscriptionContent, formatSummaryContent, currentUser?.id]);
  
  const showFreePlan = accountReady && !contextIsPremium && !isPremium && (isGuest || !currentUser || isConfirmedFreePlan);
  const search = fileSearch.trim().toLocaleLowerCase();
  const visibleFiles = recentFiles.filter(file => {
    const matchesSearch = !search || `${file.original_filename || ''} ${file.short_summary || ''}`.toLocaleLowerCase().includes(search);
    const matchesFilter = fileFilter === 'all' || (fileFilter === 'completed' && isCompletedNow(file, statusMap[file.id]))
      || (fileFilter === 'processing' && isActiveTranscriptionStatus(bestStatus(file, statusMap[file.id])));
    return matchesSearch && matchesFilter;
  });

  // Free-usage left (for the small pill)
  const defaultFreeLimit = 3;
  const quotaLimitNum =
    typeof quota?.limit === "number" ? quota.limit : defaultFreeLimit;
  const quotaUsedNum = quota?.used ?? 0;
  const freeLeft = !isPremium && quota && !quotaError
    ? Math.max(0, quotaLimitNum - quotaUsedNum)
    : null;
  const completedBulkCount = historyTotals.completed;
  const bulkFormatUsesTimeOption = bulkFormat === "txt" || bulkFormat === "docx" || bulkFormat === "pdf";

  // ---------- Page-wide "drag anywhere" overlay listeners ----------
  useEffect(() => {
    const hasFiles = (e) =>
      Array.from(e?.dataTransfer?.types || []).includes("Files");
    const onDragEnter = (e) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      dragDepthRef.current += 1;
      setIsDragging(true);
    };
    const onDragOver = (e) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
    };
    const onDragLeave = (e) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      dragDepthRef.current -= 1;
      if (dragDepthRef.current <= 0) {
        dragDepthRef.current = 0;
        setIsDragging(false);
      }
    };
    const onDrop = (e) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      dragDepthRef.current = 0;
      setIsDragging(false);
      // Note: actual upload is handled by the overlay element's onDrop
    };
    window.addEventListener("dragenter", onDragEnter);
    window.addEventListener("dragover", onDragOver);
    window.addEventListener("dragleave", onDragLeave);
    window.addEventListener("drop", onDrop);
    return () => {
      window.removeEventListener("dragenter", onDragEnter);
      window.removeEventListener("dragover", onDragOver);
      window.removeEventListener("dragleave", onDragLeave);
      window.removeEventListener("drop", onDrop);
    };
  }, []);

  entryHandlers.current = { accountType: currentUser ? isGuest ? "guest" : "registered" : "visitor", upload: handleFilesSelected, record: handleMicrophoneClick, importUrl: handleImportFromUrl, refresh: fetchHistory };
  importHandlersRef.current = { finish: finishImportResult, busy: isUploading || isRecording || showUploadPopup,
    fail: error => {
      setImportTask(null);
      const failure = importFailure({}, error?.status, 'media_file', error);
      setUrlRecovery(failure);
      setUrlImportError(t(failure.messageKey));
    } };
  useEffect(() => {
    if (window.parent !== window) window.parent.postMessage({ type: 'instanttranscriber:workspace-account',
      account_type: currentUserId ? isGuest ? 'guest' : 'registered' : 'visitor' }, window.location.origin);
  }, [currentUserId, isGuest]);

  useEffect(() => {
    if (!currentUserId) return undefined;
    const uid = currentUserId;
    const controller = new AbortController();
    let disposed = false;
    let checking = false;
    let lookupFailures = 0, emptyLookups = 0, nextLookup = 0, lookupInterruptedAt = 0, timer;
    const schedule = () => {
      clearTimeout(timer);
      if (!disposed && workspaceVisible()) timer = setTimeout(recover, Math.max(1000, nextLookup - Date.now()));
    };
    const recover = async () => {
      if (disposed || checking || !workspaceVisible()) return;
      if (Date.now() < nextLookup) { schedule(); return; }
      if (importActiveRef.current || importHandlersRef.current.busy || importTaskRef.current) {
        nextLookup = Date.now() + 10000; schedule(); return;
      }
      checking = true;
      nextLookup = Date.now() + 10000;
      let observing = false;
      try {
        const getSession = async () => (await supabase.auth.getSession()).data.session;
        const session = await getSession();
        if (!session || session.user?.id !== uid) return;
        const tasks = await listImports(API_BASE, session, controller.signal);
        if (disposed || !workspaceVisible()) return;
        if (lookupFailures) trackEvent('import_recovery_success', { operation: 'import_list',
          event_source: 'passive_lookup', retry_count: lookupFailures, elapsed_ms: Date.now() - lookupInterruptedAt });
        lookupFailures = 0; nextLookup = 0;
        // A manual submission may have started while this passive read was in flight.
        if (!tasks.length) {
          emptyLookups++;
          nextLookup = Date.now() + Math.min(120000, 30000 * 2 ** Math.min(emptyLookups - 1, 2));
          return;
        }
        emptyLookups = 0;
        if (importActiveRef.current || importHandlersRef.current.busy || importTaskRef.current) return;
        importActiveRef.current = true;
        observing = true;
        importObserverRef.current = controller;
        setShowStudioUrl(true);
        setIsImportingUrl(true);
        const result = await watchImport(API_BASE, tasks[0], uid, getSession, {
          signal: controller.signal, onState: task => { if (!disposed) setImportTask(task); },
          onReconnect: (error, stage) => trackDiagnostic('import_status_error', error, stage),
          onRecovered: params => trackEvent('import_recovery_success', params),
        });
        if (disposed) return;
        if (result.task.state === 'failed') {
          await importHandlersRef.current.finish(result, { session, inputMethod: 'reconnect' });
        } else {
          // A recovered import needs an explicit action before starting a transcription.
          setImportTask(result.task);
          await fetchHistory();
        }
      } catch (error) {
        if (!disposed && observing && error?.code === 'request_cancelled') setImportTask(null);
        if (!disposed && error?.code !== 'request_cancelled') {
          if (observing) importHandlersRef.current.fail(error);
          else {
            if (!lookupFailures) lookupInterruptedAt = Date.now();
            lookupFailures++;
            nextLookup = Date.now() + Math.min(60000, 10000 * 2 ** Math.min(lookupFailures - 1, 3));
          }
          trackDiagnostic('import_recovery_error', error, { operation: observing ? 'import_watch' : 'import_list',
            event_source: observing ? 'known_import' : 'passive_lookup' });
        }
      } finally {
        checking = false;
        if (!disposed && observing && importObserverRef.current === controller) { importActiveRef.current = false; setIsImportingUrl(false); }
        schedule();
      }
    };
    const wake = () => {
      if (!workspaceVisible()) return;
      nextLookup = 0; emptyLookups = 0;
      recover();
    };
    timer = setTimeout(recover, 1000);
    const stopVisibility = observeWorkspaceVisibility(visible => {
      if (visible) wake(); else clearTimeout(timer);
    });
    window.addEventListener('focus', wake);
    window.addEventListener('pageshow', wake);
    return () => {
      disposed = true;
      controller.abort('account_changed');
      importObserverRef.current?.abort('account_changed');
      importActiveRef.current = false;
      clearTimeout(timer); stopVisibility();
      window.removeEventListener('focus', wake); window.removeEventListener('pageshow', wake);
    };
  }, [currentUserId, fetchHistory]);
  useEffect(() => {
    const saved = () => entryHandlers.current.refresh().catch(() => {});
    window.addEventListener('instanttranscriber:guest-saved', saved);
    if (window.parent === window) return () => window.removeEventListener('instanttranscriber:guest-saved', saved);
    const receive = (event) => {
      if (event.origin !== window.location.origin || event.source !== window.parent) return;
      const data = event.data;
      if (data?.type !== 'instanttranscriber:guest-entry' || typeof data.id !== 'string') return;
      if (!guestConfig || !accountReady || isPremium !== contextIsPremium || (!currentUser && !guestConfig.enabled)) return;
      if (!['upload', 'record', 'url', 'open'].includes(data.action)) return;
      if (data.action === 'url' && (typeof data.url !== 'string' || !isLikelyPublicHttpUrl(data.url))) return;
      window.parent.postMessage({ type: 'instanttranscriber:guest-entry-accepted', id: data.id, account_type: entryHandlers.current.accountType }, window.location.origin);
      if (acceptedEntries.current.has(data.id)) return;
      acceptedEntries.current.add(data.id);
      setGuestEntryContext(data.action, data.method);
      if (data.action === 'upload' && Array.isArray(data.files)) entryHandlers.current.upload(data.files, data.method === 'drop' ? 'landing_drop' : 'landing_file_picker');
      if (data.action === 'record') entryHandlers.current.record();
      if (data.action === 'url') entryHandlers.current.importUrl(data.url, data.method || 'landing_url');
      if (data.action === 'url') setShowStudioUrl(true);
    };
    window.addEventListener('message', receive);
    window.parent.postMessage({ type: 'instanttranscriber:workspace-ready' }, window.location.origin);
    return () => { window.removeEventListener('message', receive); window.removeEventListener('instanttranscriber:guest-saved', saved); };
  }, [guestConfig, currentUser, accountReady, isPremium, contextIsPremium]);

  useEffect(() => {
    const acceptedPreparation = uploadItems.length ? uploadItems.every(item => item.jobId
      || ['cancelled','canceled','error','uploaded'].includes(item.status) || item.preparation?.continuesInBackground)
      : uploadPreparation?.continuesInBackground;
    const busy = (isUploading && !acceptedPreparation) || isRecording || (isImportingUrl && !importTask?.id);
    if (window.parent !== window) window.parent.postMessage({ type: 'instanttranscriber:workspace-busy', busy }, window.location.origin);
    if (!busy) return undefined;
    const unload = (event) => { event.preventDefault(); event.returnValue = ''; };
    const navigateAway = (event) => {
      const anchor = event.target.closest?.('a[href]');
      if (!anchor || anchor.target === '_blank' || anchor.hasAttribute('download') || anchor.getAttribute('href').startsWith('#')) return;
      event.preventDefault(); event.stopPropagation();
      toast(guestCopy.busy, 5000);
    };
    window.addEventListener('beforeunload', unload);
    document.addEventListener('click', navigateAway, true);
    return () => {
      window.removeEventListener('beforeunload', unload);
      document.removeEventListener('click', navigateAway, true);
      if (window.parent !== window) window.parent.postMessage({ type: 'instanttranscriber:workspace-busy', busy: false }, window.location.origin);
    };
    // The notice only depends on the current operation and selected language.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUploading, uploadItems, uploadPreparation, isRecording, isImportingUrl, importTask?.id, guestCopy]);

  if (!currentUser && !guestConfig?.enabled) {
    return (
      <div className="login-prompt">
        <h2>{guestConfig === null ? "Preparing your workspace…" : t("overview.loginTitle")}</h2>
        <p>{t("overview.loginBody")}</p>
        <Link to="/login" className="primary-button">{t("common.signIn")}</Link>
        <Link to="/signup" className="secondary-button">{t("common.signUp")}</Link>
      </div>
    );
  }

  return (
    <AppErrorBoundary component="Overview">
      {resumedFile&&accountReady&&<aside className="large-file-resume" role="status">
        <span><strong>{conversionCopy.fileReady}</strong> {resumedFile.name}</span>
        <button type="button" className="primary-button" disabled={isUploading||showUploadPopup||isImportingUrl||isRecording} onClick={()=>{
          trackEvent('large_file_resume',{operation:'upload_resume'});handleFilesSelected([resumedFile],'upgrade_file_resume');
        }}>{conversionCopy.useSelectedFile}</button>
        <button type="button" className="secondary-button" onClick={()=>{clearPendingFile();setResumedFile(null);}}>{conversionCopy.discardSelectedFile}</button>
      </aside>}
      {studio && <section className="studio-toolbar" aria-label={guestCopy.studioTools}>
        <div className="studio-toolbar__actions">
          <button type="button" className="secondary-button" onClick={() => { setShowStudioUrl(false); handleClickUpload(); }} disabled={isUploading || showUploadPopup || isImportingUrl}><FiUpload aria-hidden="true" /> {guestCopy.newFile}</button>
          <button type="button" className="secondary-button" onClick={() => { setShowStudioUrl(false); handleMicrophoneClick(); }} disabled={isUploading || showUploadPopup || isImportingUrl}><FiMic aria-hidden="true" /> {guestCopy.record}</button>
          <button type="button" className="secondary-button" onClick={() => {
            setShowStudioUrl(!showStudioUrl);
            if (!showStudioUrl) requestAnimationFrame(() => { urlFieldRef.current?.focus({ preventScroll: true }); urlPanelRef.current?.scrollIntoView({ block: 'start' }); });
          }} aria-expanded={showStudioUrl} aria-controls="url-import-panel" disabled={isUploading || showUploadPopup || isImportingUrl}><FiLink aria-hidden="true" /> {guestCopy.url}</button>
        </div>
        <div className="studio-toolbar__account">
          {!isPremium && freeLeft !== null && <span>{freeLeft} / {quota?.limit || 3} {guestCopy.remaining}</span>}
          <Link to={currentUser && !isGuest ? '/dashboard/profile' : '/signup'} onClick={() => {
            if (isGuest) trackEvent('guest_upgrade_click', { cta_location: 'studio_toolbar' });
          }}>{currentUser && !isGuest ? t('common.profile') : guestCopy.save}</Link>
        </div>
      </section>}
      {!studio && (isGuest || !currentUser) && <aside className="guest-notice workspace-guest">
        <p>{guestCopy.resultBody}</p>
        <Link to="/signup" onClick={() => trackEvent('guest_upgrade_click', { cta_location: 'guest_banner' })}>{guestCopy.create}</Link>
      </aside>}
      {/* WELCOME */}
      {!studio && <section className={`welcome-section workspace-heading ${isPremium ? "welcome-section--premium" : ""}`}>
        <div>
          <h1>{t('workspace.title')}</h1>
          <p>{t(recentFiles.length ? 'workspace.returningSubtitle' : 'workspace.firstSubtitle')}</p>
        </div>
        {isPremium ? <span className="workspace-heading__plan is-premium"><FiCheckCircle aria-hidden="true" />{t('workspace.premiumActive')}</span>
          : showFreePlan ? <a href="#workspace-plan" className="workspace-heading__plan is-free" onClick={() => trackEvent('premium_plan_compare', { cta_location: 'workspace_header', offer_variant: 'workspace_v1' })}><FiZap aria-hidden="true" />{t('workspace.seePremium')}</a>
          : accountReady ? <Link to="/dashboard/profile" className="workspace-heading__plan">{t('workspace.yourPlan')}</Link>
          : <span className="workspace-heading__plan">{t('common.loading')}</span>}
      </section>}

      <div className={`workspace-body--dashboard${studio ? ' workspace-body--studio' : ''}${!showFreePlan ? ' is-premium' : ''}`}>
      <div hidden={studio && !showStudioUrl} className={`dashboard-focus-grid ${!showFreePlan ? "is-premium" : "has-conversion-panel"}`}>
      {/* UPLOAD */}
      <section className={`upload-section ${isPremium ? "upload-section--premium" : ""}`}>
        {!studio && showFreePlan && (nearFreeLimit || atFreeLimit) && <div className="workspace-limit-notice" role="status">
          <span>{t(atFreeLimit ? 'workspace.limitReached' : 'workspace.nearLimit')}</span>
          <a href="#workspace-plan" onClick={() => trackEvent('premium_plan_compare', { cta_location: atFreeLimit ? 'quota_bar_limit_reached' : 'quota_bar_near_limit', offer_variant: 'workspace_v1' })}>{t('workspace.seePremium')}</a>
        </div>}
        <h2>{studio ? <><FiLink aria-hidden="true" /> {t('workspace.urlTitle')}</> : t('workspace.newTranscription')}</h2>
        <div className="upload-options">
          <div 
            className={`upload-area ${isDragging ? 'dragging' : ''}`}
            onClick={handleClickUpload}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            style={{ cursor: isUploading || showUploadPopup ? "not-allowed" : "pointer", opacity: isUploading || showUploadPopup ? 0.8 : 1 }}
          >
            <FiUpload className="upload-icon" />
            <p className="upload-headline">{t("overview.dragDrop")}</p>
            <p className="upload-sub">{t("overview.selectComputer")}</p>
            <div className="upload-cta-row">
              <button
                type="button"
                className="primary-button select-file-btn"
                onClick={(e) => { e.stopPropagation(); handleClickUpload(); }}
                disabled={isUploading || showUploadPopup}
              >
                {t("overview.chooseFiles")}
              </button>
              {CONFIG.SAMPLE_AUDIO_URL && (
                <button
                  type="button"
                  className="secondary-button sample-btn"
                  onClick={(e) => { e.stopPropagation(); handleUseSample(); }}
                  disabled={isUploading || showUploadPopup}
                >
                  {t("overview.sample")}
                </button>
              )}
            </div>
            <p className="upload-microcopy">{studio ? t('overview.supports') : t('workspace.formats')}</p>
            <input
              type="file"
              id="fileInput"
              style={{ display: "none" }}
              onChange={handleFileChange}
              accept="audio/*,video/*"
              multiple
              disabled={isUploading || showUploadPopup}
            />
            {studio && isFree && <p className="upload-reassure">{t('overview.freeReassure')}</p>}
          </div>
          
          <div className="upload-divider">
            <span>{t("overview.or")}</span>
          </div>

          {/* Right column with two nice tiles */}
          <div className="upload-right">
            <button type="button"
              className="microphone-area"
              onClick={handleMicrophoneClick}
              disabled={isUploading || showUploadPopup || isImportingUrl}
              title={t('overview.recordMicInfo')}
              style={{ cursor: isUploading || showUploadPopup ? "not-allowed" : "pointer", opacity: isUploading || showUploadPopup ? 0.8 : 1 }}
            >
              <FiMic className="microphone-icon" />
              <span className="record-label">{t('workspace.recordAudio')}</span>
              <span className="record-info">{t("overview.recordMicInfo")}</span>
            </button>

            {/* New: Record another tab + microphone (Chromium only) */}
            {isTabAudioSupported && (
              <button type="button"
                className="microphone-area"
                onClick={handleTabMicCaptureClick}
                disabled={isUploading || showUploadPopup || isImportingUrl}
                style={{
                  cursor: isUploading || showUploadPopup ? "not-allowed" : "pointer",
                  opacity: isUploading || showUploadPopup ? 0.8 : 1
                }}
                aria-label={t("overview.recordTabMicTitle")}
                title={t("overview.recordTabMicTitle")}
              >
                <span className="recording-source-icons" aria-hidden="true">
                  <FiMonitor className="recording-source-screen" />
                  <FiMic className="recording-source-mic" />
                </span>
                <span className="record-label">{t('workspace.recordTabMic')}</span>
                <span className="record-info">
                  {t("overview.recordTabMicInfo")}
                </span>
              </button>
            )}

            {/* New: Record another tab (Chromium only) */}
            {isTabAudioSupported && (
              <button type="button"
                className="microphone-area"
                onClick={handleTabCaptureClick}
                disabled={isUploading || showUploadPopup || isImportingUrl}
                style={{ 
                  cursor: isUploading || showUploadPopup ? "not-allowed" : "pointer", 
                  opacity: isUploading || showUploadPopup ? 0.8 : 1
                }}
                aria-label={t("overview.recordTabTitle")}
                title={t("overview.recordTabTitle")}
              >
                <FiMonitor className="microphone-icon" />
                <span className="record-label">{t('workspace.recordTab')}</span>
                <span className="record-info">
                  {t("overview.recordTabInfo")}
                </span>
              </button>
            )}

            {/* New: Paste link tile */}
            <div className="url-area" id="url-import-panel" ref={urlPanelRef} onClick={(e) => e.stopPropagation()}>
              {!studio && <div className="url-area-header">
                <FiLink size={20} />
                <strong>{t('workspace.urlTitle')}</strong>
              </div>}
              <div className="url-import-row">
                <input
                  type="url"
                  ref={urlFieldRef}
                  aria-describedby="url-import-help"
                  aria-invalid={Boolean(urlImportError)}
                  value={urlInput}
                  onFocus={handleUrlInputFocus}
                  onPaste={handleUrlInputPaste}
                  onChange={handleUrlInputChange}
                  onKeyDown={(e) => (e.key === "Enter" ? handleImportFromUrl() : null)}
                  placeholder={t('workspace.urlPlaceholder')}
                  aria-label={t("overview.urlLabel")}
                  disabled={isImportingUrl || isUploading || showUploadPopup}
                />
                <button
                  type="button"
                  className="primary-button"
                  onClick={isImportingUrl ? undefined : handleImportFromUrl}
                  disabled={
                    !isLikelyPublicHttpUrl(urlInput) ||
                    isImportingUrl || isUploading || showUploadPopup
                  }
                >
                  {isImportingUrl ? t("overview.importing") : t("overview.import")}
                </button>
              </div>
              {urlRestored && !isImportingUrl && <div className="url-import-progress" role="status" aria-live="polite">
                <FiLink aria-hidden="true" /><p>{t('import.linkRestored')}</p>
              </div>}
              {isImportingUrl && <div className="url-import-progress" role="status" aria-live="polite">
                <FiLoader className="url-import-spinner" aria-hidden="true" />
                <div><strong>{t(importTask?.reconnecting ? 'import.backgroundReconnecting'
                  : ['captions_lookup','captions_fetch','captions_save'].includes(importTask?.import_stage) ? `import.${importTask.import_stage}`
                  : importTask?.state === 'preparing' ? 'import.backgroundPreparing'
                  : importTask?.state === 'downloading' ? 'import.backgroundDownloading'
                  : importTask?.id ? 'import.backgroundWaiting'
                  : getUrlImportType(urlInput) === 'youtube' ? 'import.loadingCaptions' : 'import.loadingLink')}</strong>
                  <p>{t(importTask?.kind==='youtube' ? 'import.youtubeWaitingHint' : importTask?.id ? 'import.backgroundHint' : 'import.loadingDetail')}</p>
                  {importTask?.state === 'downloading' && importTask.downloaded_bytes > 0 && <p className="import-download-detail">
                    {t(importTask.total_bytes > 0 ? 'import.downloadProgressKnown' : 'import.downloadProgress', {
                      received: (importTask.downloaded_bytes / 1048576).toFixed(1),
                      total: (Number(importTask.total_bytes || 0) / 1048576).toFixed(1),
                    })}
                  </p>}
                  {!isPremium && importTask?.source_duration_seconds > 35 * 60 && <p>{t('import.longSourceHint')}</p>}
                </div>
              </div>}
              {!isImportingUrl && importTask && ['ready','failed'].includes(importTask.state) && <div className="url-import-progress" role="status">
                {importTask.state === 'failed' ? <FiAlertCircle aria-hidden="true" /> : <FiCheckCircle aria-hidden="true" />}
                <div><strong>{t(importTask.state === 'failed' ? 'import.failedTitle' : 'import.backgroundReady')}</strong>
                  <button type="button" className="secondary-button" onClick={async () => {
                    const { data: { session } } = await supabase.auth.getSession();
                    if (!session) return;
                    try { await finishImportResult(importOutcome(importTask), { session, inputMethod: 'reconnect' }, true); }
                    catch (error) { trackDiagnostic('import_result_error', error, { operation: 'import_result' }); }
                  }}>{t('import.backgroundContinue')}</button></div>
              </div>}
              {urlImportError && (
                <div role="alert" className="url-import-recovery" ref={urlRecoveryRef} tabIndex={-1} aria-labelledby="url-import-error-title">
                  <h3 id="url-import-error-title"><FiAlertCircle aria-hidden="true" /> {t('import.failedTitle')}</h3>
                  <p>{urlImportError}</p>
                  {urlRecovery?.driveHelp && <p>{t('import.driveAccessHelp')}</p>}
                  {urlRecovery?.upgradeAvailable && !isPremium && <p>{t('import.upgradeResumeHint')}</p>}
                  {urlRecovery?.code === 'source_access_denied' && <p>{t('import.sourceAccessHelp')}</p>}
                  <div className="url-import-recovery__actions">
                  {urlRecovery?.upgradeAvailable && !isPremium && <button type="button" className="upgrade-button" disabled={isImportingUrl || isUploading} onClick={() => {
                    trackEvent('premium_plan_compare', { cta_location: 'url_import_size_limit', error_code: 'file_too_large' });
                    setModalTitle(t('overview.statusRequiresUpgrade'));
                    setModalContent(t('import.freeFileTooLarge'));
                    setModalUpgradeSource('url_import_size_limit');
                    setModalShowUpgradeCTA(true);
                    setShowModal(true);
                  }}>{t('import.upgradeContinue')}</button>}
                  {urlRecovery?.retryable && <button type="button" className="secondary-button" disabled={isImportingUrl || isUploading} onClick={() => {
                    const params = { ...urlRecovery.analytics, error_code: urlRecovery.code, event_source: 'retry' };
                    urlRecoveryAttemptRef.current = { action: 'retry', params, expires: Date.now() + 15 * 60 * 1000 };
                    trackEvent('url_import_retry', { ...params, source: 'paste_link_form' });
                    handleImportFromUrl(undefined, 'retry');
                  }}>{t('workspace.retry')}</button>}
                  <button type="button" className={urlRecovery?.code === 'source_access_denied' ? 'url-import-file-primary' : 'secondary-button'} disabled={isImportingUrl || isUploading || showUploadPopup} onClick={() => {
                    const params = { ...urlRecovery.analytics, error_code: urlRecovery.code, event_source: 'upload_file' };
                    urlRecoveryAttemptRef.current = { action: 'upload_file', params, expires: Date.now() + 15 * 60 * 1000 };
                    trackEvent('url_import_upload_fallback', { ...params, source: 'paste_link_form' });
                    handleClickUpload();
                  }}>{t('overview.uploadOriginal')}</button>
                  </div>
                </div>
              )}
              <p className="upload-microcopy" id="url-import-help">
                {t('workspace.urlHint')}
                {isSoundCloudEntry && <> {t('overview.soundCloudSingleTrack')}</>}
              </p>
            </div>
          </div>
        </div>
        {!studio && <p className="workspace-upload-limits">{t(isPremium ? 'workspace.premiumLimits' : showFreePlan ? 'workspace.freeLimits' : 'common.loading')}</p>}
      </section>

      {/* QUOTA */}
      {!studio && showFreePlan && <WorkspacePlanCard quota={quota} billingInterval={billingInterval} onBillingChange={setBillingInterval}
        onUpgrade={interval => handlePremiumSubscription('dashboard_premium_card', interval)} pending={checkoutPending}
        signupFirst={!currentUser || isGuest} hasHistory={recentFiles.length > 0} historyReady={!currentUser || (historyLoaded && !historyError)} />}
      </div>

      {/* FILES */}
      <section className="files-section" id="transcription-history">
        <div className="files-section-header">
          <h2>{studio ? guestCopy.studioFiles : t('workspace.library')}</h2>
          {completedBulkCount > 1 && (
            <button
              type="button"
              className="secondary-button bulk-download-button"
              onClick={() => {
                setBulkDownloadError("");
                setBulkProgress("");
                setShowBulkDownloadModal(true);
              }}
            >
              <FiDownload />
              {studio ? t('overview.bulkDownload') : t('workspace.exportAll')}
            </button>
          )}
        </div>

        {quotaError && <div className="history-error" role="alert">
          <span>{t('workspace.quotaUnavailable')}</span>
          <button type="button" className="secondary-button" onClick={fetchQuota}>{t('workspace.retry')}</button>
        </div>}
        {historyError && <div className="history-error" role="alert">
          <span>{t('workspace.historyError')}</span>
          <button type="button" className="secondary-button" onClick={fetchHistory}>{t('workspace.retry')}</button>
        </div>}
        {!studio && (recentFiles.length > 0 || fileSearch || fileFilter !== 'all') && <div className="files-toolbar">
          <label className="files-search"><FiSearch aria-hidden="true" /><input type="search" value={fileSearch} onChange={event => setFileSearch(event.target.value)} placeholder={t('workspace.search')} aria-label={t('workspace.search')} /></label>
          <select aria-label={t('workspace.filter')} value={fileFilter} onChange={event => setFileFilter(event.target.value)}>
            <option value="all">{t('workspace.allFiles')}</option><option value="completed">{t('workspace.readyFiles')}</option><option value="processing">{t('workspace.activeFiles')}</option>
          </select>
        </div>}
        {historyQueryLoading && <p className="history-query-status" role="status">{t('workspace.searchingHistory')}</p>}
        {recentFiles.length === 0 && !fileSearch.trim() && fileFilter === 'all' ? (
          <div className="no-files-message" role="status">
            <FiFileText aria-hidden="true" />
            <h3>{t(currentUser && !historyLoaded ? 'workspace.loadingHistory' : historyError ? 'workspace.historyUnavailable' : 'workspace.emptyTitle')}</h3>
            {!historyError && (historyLoaded || !currentUser) && <p>{t('workspace.emptyBody')}</p>}
          </div>
        ) : visibleFiles.length === 0 && !historyQueryLoading ? (
          <div className="no-files-message" role="status"><FiSearch aria-hidden="true" /><h3>{t('workspace.noResults')}</h3><p>{t('workspace.noResultsBody')}</p>
            <button type="button" className="secondary-button" onClick={() => { setFileSearch(''); setFileFilter('all'); }}>{t('workspace.clearFilters')}</button>
          </div>
        ) : (
          <div className="files-list">
            {visibleFiles.map((file) => {
              const live = statusMap[file.id]; // /status payload if present
              const liveStatus = norm(live?.status);
              const running = live && (liveStatus === "processing" || liveStatus === "running");
              const queued  = live && (liveStatus === "queued" || liveStatus === "transcribing" || liveStatus === "starting");
              const showProgress = (running || queued) && file.status !== "completed";

              const pct = live?.progress ?? 0;
              const eta = live?.eta_display || t("overview.etaEstimating");
              const waitingForWorker = live?.waiting_for_worker === true || live?.phase === 'queued';
              const displayPhase = waitingForWorker ? 'queued' : live?.phase;
              const lang = live?.language || "?";
              const speed = live?.speed_factor > 0 ? `x${live.speed_factor}` : "";
              const proc = live?.processed_seconds != null ? formatHMS(live.processed_seconds) : null;
              const total = live?.duration || file.duration || null;

              const statusLabel = labelFromStatuses(file, live);
              const completedClickable = isCompletedNow(file, live);
              const preview = Boolean(file.is_preview);
              const previewFinished = preview && !isActiveTranscriptionStatus(statusLabel);
              const canStart = statusLabel === "uploaded" || (isPremium && (statusLabel === "requires_upgrade" || previewFinished));
              const canUpgradeSaved = !isPremium && (statusLabel === "requires_upgrade" || preview);
              const isStarting = startingIds.has(file.id);
              const triggeredByApi = file.triggered_by_api || file.source === "api" || !!file.api_version;

              return (
                <div 
                  key={file.id} 
                  id={`file-card-${file.id}`}
                  tabIndex={-1}
                  className={`file-card ${statusLabel === 'completed' ? 'completed' : 'processing'}`}
                >
                  <div 
                    className="file-info"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (completedClickable) {
                        handleFileClick(file.id, file.original_filename);
                      }
                    }}
                    style={{ cursor: completedClickable ? 'pointer' : 'default' }}
                  >
                    <div className="file-header">
                      <span className="file-name" title={file.original_filename}>{file.original_filename}</span>
                      <span className="file-badges">
                        {triggeredByApi && (
                          <span className="file-source-badge">API</span>
                        )}
                        <span className={`file-status status-${statusLabel}`}>
                          {statusLabel === "processing" && t("status.processing")}
                          {statusLabel === "queued" && t("overview.statusQueuedWarming")}
                          {statusLabel === "completed" && t(file.result_state === 'empty' ? 'overview.noSpeechTitle' : preview ? "overview.previewReady" : "status.completed")}
                          {statusLabel === "uploaded" && t("overview.statusReady")}
                          {statusLabel === "requires_upgrade" && t("overview.statusRequiresUpgrade")}
                          {statusLabel === "error" && t("status.error")}
                        </span>
                      </span>
                    </div>

                    {preview && <p className="preview-notice">{t(isPremium ? "overview.previewNoticePremium" : "overview.previewNotice")}</p>}
                    {file.short_summary && String(file.short_summary).length <= 60 && (
                      <div className="file-short-summary">
                        <span>{t("overview.shortSummary")}:</span> {file.short_summary}
                      </div>
                    )}

                    <div className="file-meta">
                      <span>{t("overview.uploaded")} {file.uploaded_time}</span>
                      {file.duration && (
                        <span>
                          {t("overview.duration")} {
                            typeof file.duration === "number"
                              ? formatHMS(file.duration)
                              : file.duration
                          }
                        </span>
                      )}
                    </div>

                    {live?.statusUnavailable && isActiveTranscriptionStatus(file.status) &&
                      <p role="status" className="job-progress-stage">{t("workspace.progressUnavailable")}</p>}
                    {showProgress && (
                      <div className="job-progress" style={{ marginTop: 8 }}>
                        {displayPhase && <div className="job-progress-stage" style={{ marginBottom: 6, fontSize: 13 }}>
                          {t(`overview.phase.${displayPhase}`)}
                        </div>}
                        <div className="progress-container" style={{ height: 8, background: "#eee", borderRadius: 6 }}>
                          <div
                            className="progress-bar"
                            style={{
                              width: `${pct}%`,
                              height: 8,
                              borderRadius: 6,
                              transition: "width 0.3s ease",
                              backgroundColor: "#4f46e5"
                            }}
                          />
                        </div>
                        <div className="job-progress-line" style={{ marginTop: 6, fontSize: 12, color: "#444" }}>
                          <span>{Number.isFinite(Number(pct)) ? Math.round(Number(pct)) : 0}%</span>
                          <span>{waitingForWorker ? t('overview.etaAfterStart') : <>{t("overview.eta")} {eta}</>}</span>
                          {lang !== '?' && <span>{t('overview.lang')} {lang}</span>}
                          {speed && <span>{speed}</span>}
                          {(proc && total) && <span>{proc} / {total}</span>}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="file-actions">
                    {canUpgradeSaved && (
                      <div className="saved-upgrade-actions">
                        <UpgradeBillingActions
                          signupFirst={!currentUser || isGuest}
                          record={{ ...file, retained: true }}
                          className="upgrade-billing-actions--saved"
                          monthlyLabel={t("billing.transcribeFileNow")}
                          onUpgrade={(interval) => {
                            savePendingUpgradeTranscription({
                              id: file.id,
                              filename: file.original_filename,
                              duration: file.duration || null,
                              options: pendingOptionsFrom({}, "saved_upgrade_resume"),
                            });
                            handlePremiumSubscription("saved_requires_upgrade_start", interval, { ...file, retained: true });
                          }}
                        />
                      </div>
                    )}
                    {canStart && (
                      <button
                        className="start-transcription-button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleStartUploadedFile(file);
                        }}
                        disabled={isStarting}
                        title={t("upload.startTranscription")}
                      >
                        <FiPlay />
                        <span>{isStarting ? t("overview.startingTranscription") : t(preview ? "overview.startFull" : "upload.startTranscription")}</span>
                      </button>
                    )}
                    {isCompletedNow(file, live) && (
                      <button 
                        className="icon-button view-button" 
                        aria-label={t('workspace.openFile', { name: file.original_filename })}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleFileClick(file.id, file.original_filename);
                        }}
                        title={t("overview.viewTranscription")}
                      >
                        <FiFileText aria-hidden="true" />
                        <span>{t(preview ? 'workspace.openPreview' : 'workspace.openTranscript')}</span>
                      </button>
                    )}
                    <button 
                      className="icon-button delete-button" 
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteFile(file.id);
                      }}
                      title={t("overview.deleteFile")}
                      aria-label={t('workspace.deleteFile', { name: file.original_filename })}
                    >
                      <FiTrash2 />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {historyNextCursor && <div className="history-pagination">
          <button type="button" className="secondary-button" disabled={historyLoadingMore || historyQueryLoading}
            onClick={() => fetchHistory({ append: true })}>{t(historyLoadingMore ? 'workspace.loadingMore' : 'workspace.loadMore')}</button>
          <span>{t('workspace.historyPageHint')}</span>
        </div>}
      </section>
      </div>

      {/* MODAL */}
      {showModal && (
        <div
          className={`modal-overlay transcript-modal-overlay${!isErrorModal ? " transcript-modal-overlay--reading" : ""}`}
          onClick={() => { setShowModal(false); setModalShowUpgradeCTA(false); }}
        >
          <div className={`modal-container transcript-modal${!isErrorModal ? " transcript-modal--result" : ""}`} role="dialog" aria-modal="true" aria-labelledby="transcript-modal-title" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title-area">
                <h3 id="transcript-modal-title" title={largeFile?conversionCopy.largeFileTitle:modalTitle}>{largeFile?conversionCopy.largeFileTitle:modalTitle}</h3>
              </div>
              <button
                className="icon-button transcript-modal-close"
                onClick={() => { setShowModal(false); setModalShowUpgradeCTA(false); }}
                aria-label={t("common.close")}
              >
                <FiX aria-hidden="true" />
              </button>
              {!isErrorModal && currentTranscriptionData && showsSummaryTab(currentTranscriptionData, activeModalTab) && (
                <div className="modal-tabs" role="tablist" aria-label={t("overview.transcriptViews")}>
                  <button
                    className={`modal-tab ${activeModalTab === "transcript" ? "active" : ""}`}
                    type="button"
                    role="tab"
                    aria-selected={activeModalTab === "transcript"}
                    onClick={() => handleModalTabChange("transcript")}
                  >
                    {t("overview.transcriptTab")}
                  </button>
                  <button
                    className={`modal-tab ${activeModalTab === "summary" ? "active" : ""}`}
                    type="button"
                    role="tab"
                    aria-selected={activeModalTab === "summary"}
                    onClick={() => handleModalTabChange("summary")}
                  >
                    {t("overview.summaryTab")}
                  </button>
                </div>
              )}
              {!isErrorModal && <div className="modal-controls">
                {activeModalTab === "transcript" && currentTranscriptionData?.has_diarization && (
                  <label className="speaker-toggle">
                    <input
                      type="checkbox"
                      checked={showSpeakers}
                      onChange={handleSpeakerToggle}
                      aria-label={t("overview.showSpeakers")}
                    />
                    <span className="transcript-control-label">{t("overview.showSpeakers")}</span>
                    <span className="transcript-control-label--compact">{t("overview.speakersCompact")}</span>
                  </label>
                )}
                {activeModalTab === "transcript" && currentTranscriptionData?.has_timestamps && (
                  <label className="time-toggle">
                    <input
                      type="checkbox"
                      checked={showTime}
                      onChange={handleTimeToggle}
                      aria-label={t("overview.showTimestamps")}
                    />
                    <span className="transcript-control-label">{t("overview.showTimestamps")}</span>
                    <span className="transcript-control-label--compact">{t("overview.timestampsCompact")}</span>
                  </label>
                )}
                {activeModalTab === "transcript" && currentTranscriptionData?.has_diarization && isSpeakerPostprocessingRunning(currentTranscriptionData) && (
                  <span className="speaker-review-status">
                    {t("overview.speakerReviewProcessing")}
                  </span>
                )}
                {activeModalTab === "transcript" && showSpeakers && hasSpeakerPostprocessingResult(currentTranscriptionData) && (
                  <label className="speaker-review-toggle">
                    <input
                      type="checkbox"
                      checked={useSpeakerPostprocessing}
                      onChange={handleSpeakerPostprocessingToggle}
                    />
                    {t("overview.useEnhancedSpeakers")}
                  </label>
                )}
              </div>}
            </div>
            <div className="modal-content">
              {!isErrorModal && currentTranscriptionData?.is_preview && !modalShowUpgradeCTA && (
                <div className="preview-notice" role="note">
                  <p>{t(isPremium ? "overview.previewNoticePremium" : "overview.previewNotice")}</p>
                  {isPremium ? (
                    <button className="start-transcription-button" onClick={() => {
                      setShowModal(false);
                      handleStartUploadedFile({ id: currentRecordId, original_filename: modalTitle });
                    }}>{t("overview.startFull")}</button>
                  ) : (
                    <UpgradeBillingActions signupFirst={!currentUser || isGuest} record={{ ...currentTranscriptionData,
                      ...recentFiles.find(file => file.id === currentRecordId), id: currentRecordId, retained: true }}
                      monthlyLabel={conversionCopy.entireFile} onUpgrade={(interval) => {
                      savePendingUpgradeTranscription({ id: currentRecordId, filename: modalTitle,
                        options: pendingOptionsFrom({}, "preview_upgrade_resume") });
                      handlePremiumSubscription("preview_upgrade", interval, { id: currentRecordId, original_filename: modalTitle });
                    }} />
                  )}
                </div>
              )}
              {!isErrorModal && activeModalTab === "transcript" && isSpeakerPostprocessingRunning(currentTranscriptionData) && (
                <div className="speaker-review-banner">
                  {t(getSpeakerPostprocessingMeta(currentTranscriptionData)?.retry_count ? 'overview.speakerReviewRetrying' : "overview.speakerReviewProcessingDetail")}
                </div>
              )}
              {!isErrorModal && getSpeakerPostprocessingMeta(currentTranscriptionData)?.status === 'llm_failed' && (
                <div className="transcript-recovery-notice" role="note"><strong>{t('overview.speakerReviewFailedTitle')}</strong><p>{t('overview.speakerReviewFailed')}</p></div>
              )}
              {!isErrorModal && isTranscriptEmpty(currentTranscriptionData) && (
                <div className="transcript-recovery-notice" role="note">
                  <strong>{t('overview.noSpeechTitle')}</strong><p>{t(emptyTranscriptHint(currentTranscriptionData))}</p>
                  <button type="button" className="primary-button" onClick={() => {
                    trackEvent('empty_result_upload_retry'); setShowModal(false); handleClickUpload();
                  }}>{t('overview.uploadAnotherRecording')}</button>
                </div>
              )}
              {!isErrorModal && !isTranscriptEmpty(currentTranscriptionData) && activeModalTab === "transcript" && showSpeakers && currentTranscriptionData?.has_diarization && (
                <SpeakerNamesEditor key={currentRecordId} data={currentTranscriptionData}
                  usePostprocessing={useSpeakerPostprocessing} onSave={handleSaveSpeakerNames} />
              )}
              {(!isTranscriptEmpty(currentTranscriptionData) || isErrorModal) && <pre style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{largeFile?conversionCopy.largeFileIntro:modalContent}</pre>}
              {largeFile&&modalShowUpgradeCTA&&<LargeFilePreparation file={largeFile} onPrepared={file=>{
                if(resumedFile)preparedResumeFile.current=file;
                clearPendingFile();
                setShowModal(false);setModalShowUpgradeCTA(false);setLargeFile(null);
                handleFilesSelected([file],'large_file_prepared');
              }}/>}
              {modalRecovery?.kind === 'daily_limit' && quotaResetLabel(modalRecovery.quota, interfaceLanguage) && (
                <p className="upload-recovery-reset">{t('upload.resetAt', { time: quotaResetLabel(modalRecovery.quota, interfaceLanguage) })} (Berlin)</p>
              )}
              {modalRecovery?.kind === 'daily_limit' && modalRecovery.recordId && (
                <p>{t('upload.savedAfterLimit')}</p>
              )}
            </div>
            <div className={`modal-footer${!isErrorModal ? " transcript-actions" : ""}`} style={{ position: "relative", overflow: "visible" }}>
              {isErrorModal ? <button
                className="secondary-button transcript-close-button"
                onClick={() => { setShowModal(false); setModalShowUpgradeCTA(false); }}
              >
                <FiX className="transcript-close-icon" aria-hidden="true" />
                <span>{t("common.close")}</span>
              </button> : null}
              {modalRecovery?.action === 'signin' && (isGuest || modalRecovery.kind === 'session') && (
                <div className="upload-recovery-actions">
                  {isGuest && <Link className="primary-button" to="/signup" onClick={() => trackEvent('upload_recovery_action', { action: 'signup', error_code: modalRecovery.code })}>{t('upload.createAccount')}</Link>}
                  <Link className="secondary-button" to="/login" onClick={() => trackEvent('upload_recovery_action', { action: 'signin', error_code: modalRecovery.code })}>{t('upload.signIn')}</Link>
                </div>
              )}
              {modalRecovery?.action === 'history' && <button type="button" className="primary-button" onClick={() => {
                trackEvent('upload_recovery_action', { action: 'history', error_code: modalRecovery.code });
                setShowModal(false); setShowUploadPopup(false); setFileFilter('all'); setFileSearch('');
                document.getElementById('transcription-history')?.scrollIntoView({ behavior: 'smooth' });
              }}>{t('upload.reviewPending')}</button>}
              {modalRecovery?.kind === 'daily_limit' && modalRecovery.recordId && (
                <button type="button" className="secondary-button" onClick={() => {
                  trackEvent('upload_recovery_action', { action: 'history', error_code: modalRecovery.code });
                  setShowModal(false); setShowUploadPopup(false); setFileFilter('all'); setFileSearch(''); fetchHistory();
                  document.getElementById('transcription-history')?.scrollIntoView({ behavior: 'smooth' });
                }}>{t('upload.savedFiles')}</button>
              )}

              {modalTitle === 'YouTube Import Error' && <button type="button" className="secondary-button" onClick={() => {
                setShowModal(false);
                trackEvent('url_import_upload_fallback', { source: 'youtube_error' });
                handleClickUpload();
              }}>{t('overview.uploadOriginal')}</button>}

              {retainingFile&&<p role="status">{conversionCopy.savingSelectedFile}</p>}
              {modalShowUpgradeCTA ? (
                <UpgradeBillingActions
                  className="upgrade-billing-actions--modal"
                  disabled={retainingFile}
                  signupFirst={!currentUser || isGuest}
                  record={modalUpgradeRecord}
                  limitKind={modalRecovery?.kind === 'daily_limit' ? 'daily_limit' : null}
                  monthlyLabel={modalRecovery?.kind === 'daily_limit' ? t('upload.continueToday') : modalUpgradeRecord?.id ? conversionCopy.entireFile : undefined}
                  onUpgrade={async(interval) => {
                    let record=modalUpgradeRecord;
                    if(largeFile){
                      if(retainingFile)return;
                      setRetainingFile(true);
                      try{
                        if(!fileRetentionAttempt.current)fileRetentionAttempt.current=retainPendingFile(largeFile);
                        const attempt=fileRetentionAttempt.current;
                        const retained=await attempt;
                        if(fileRetentionAttempt.current!==attempt)return;
                        record={...record,retained,retained_locally:retained};
                        setModalUpgradeRecord(record);
                        if(retained){setResumedFile(largeFile);trackEvent('large_file_retained',{operation:'upload_resume'});}
                        else if(modalUpgradeRecord?.retained_locally!==false){
                          setModalUpgradeRecord({...record,retained_locally:false});
                          toast(conversionCopy.localRetentionFailed,7000);return;
                        }
                      }finally{setRetainingFile(false);}
                    }
                    setShowModal(false);
                    setModalShowUpgradeCTA(false);
                    handlePremiumSubscription(modalUpgradeSource || "upgrade_required_modal", interval, record);
                  }}
                />
              ) : (
                !isErrorModal && !isTranscriptEmpty(currentTranscriptionData) && (activeModalTab !== 'summary' || hasTranscriptSummary(currentTranscriptionData)) && (
                  <>
                    <button
                      className="secondary-button transcript-copy-button"
                      onClick={() => copyToClipboard(modalContent)}
                    >
                      <FiFileText aria-hidden="true" />
                      <span className="transcript-action-label">{t("overview.copyText")}</span>
                      <span className="transcript-action-label--compact">{t("common.copy")}</span>
                    </button>

                    {/* NEW: Download as dropdown */}
                    <div
                      className="download-as-wrapper"
                      style={{ position: "relative", display: "inline-block" }}
                    >
                      <button
                        ref={downloadTriggerRef}
                        className="primary-button"
                        onClick={(e) => {
                          // Compute placement + visible height so the menu never clips at viewport edges.
                          const rect = e.currentTarget.getBoundingClientRect();
                          const viewportPadding = 12;
                          const menuGap = 8;
                          const menuWidth = 240;
                          const availableBelow = Math.max(
                            0,
                            window.innerHeight - rect.bottom - viewportPadding - menuGap
                          );
                          const availableAbove = Math.max(
                            0,
                            rect.top - viewportPadding - menuGap
                          );
                          const needed = 260;
                          const openDown =
                            availableBelow >= needed || availableBelow >= availableAbove;
                          const nextPlacement = openDown ? "down" : "up";
                          const nextMaxHeight = Math.max(
                            160,
                            Math.floor(openDown ? availableBelow : availableAbove)
                          );
                          const preferredLeft = rect.right - menuWidth;
                          const clampedLeft = Math.max(
                            viewportPadding,
                            Math.min(preferredLeft, window.innerWidth - menuWidth - viewportPadding)
                          );

                          setMenuPlacement(nextPlacement);
                          setDownloadMenuMaxHeight(nextMaxHeight);
                          setDownloadMenuPosition({
                            top: nextPlacement === "down" ? rect.bottom + menuGap : rect.top - menuGap,
                            left: clampedLeft
                          });
                          setShowDownloadMenu((v) => !v);
                        }}
                        aria-haspopup="menu"
                        aria-expanded={showDownloadMenu}
                      >
                        <FiDownload aria-hidden="true" />
                        <span className="transcript-download-label">{t("overview.downloadAs")}</span>
                        <span className="transcript-download-label--compact">{t("overview.downloadCompact")}</span>
                      </button>

                      {showDownloadMenu && typeof document !== "undefined" && createPortal(
                        <div
                          ref={downloadMenuRef}
                          className="download-as-menu"
                          role="menu"
                          style={{
                            position: "fixed",
                            top: `${downloadMenuPosition.top}px`,
                            left: `${downloadMenuPosition.left}px`,
                            transform: menuPlacement === "up" ? "translateY(-100%)" : "none",
                            background: "#fff",
                            border: "1px solid #e5e7eb",
                            borderRadius: 8,
                            boxShadow: "0 12px 28px rgba(0,0,0,0.18)",
                            zIndex: 4000,
                            width: 240,
                            padding: 6,
                            maxHeight: `${downloadMenuMaxHeight}px`,
                            overflowY: "auto"
                          }}
                        >
                          {activeModalTab === "summary" ? (
                            <button className="menu-item" role="menuitem" onClick={() => handleDownload("txt")} style={menuItemStyle}>
                              {t("overview.txtSummary")}
                            </button>
                          ) : (
                            <>
                              <button className="menu-item" role="menuitem" onClick={() => handleDownload("txt")} style={menuItemStyle}>
                                {t("overview.txtPlain")}
                              </button>
                              <div style={menuDividerStyle} />
                              <button className="menu-item" role="menuitem" onClick={() => handleDownload("srt")} style={menuItemStyle}>
                                SRT {currentTranscriptionData?.has_diarization ? (showSpeakers ? t("overview.withSpeakers") : t("overview.noSpeakers")) : ""}
                              </button>
                              <button className="menu-item" role="menuitem" onClick={() => handleDownload("vtt")} style={menuItemStyle}>
                                VTT {currentTranscriptionData?.has_diarization ? (showSpeakers ? t("overview.withSpeakers") : t("overview.noSpeakers")) : ""}
                              </button>
                              <div style={menuDividerStyle} />
                              <button className="menu-item" role="menuitem" onClick={() => handleDownload("docx")} style={menuItemStyle}>
                                DOCX {showTime ? t("overview.withTimecodes") : ""}
                              </button>
                              <button className="menu-item" role="menuitem" onClick={() => handleDownload("pdf")} style={menuItemStyle}>
                                PDF {showTime ? t("overview.withTimecodes") : ""}
                              </button>
                            </>
                          )}
                        </div>,
                        document.body
                      )}
                    </div>
                  </>
                )
              )}
              {!isErrorModal && <button type="button" className="secondary-button transcript-next-file-button"
                disabled={isUploading || showUploadPopup || isImportingUrl || recordingMode === 'recording'} onClick={() => {
                  trackEvent('transcript_next_file', { offer_variant: 'result_context_v3' });
                  setShowModal(false); setModalShowUpgradeCTA(false); handleClickUpload();
                }}><FiUpload aria-hidden="true" /><span>{conversionCopy.nextFile}</span></button>}
              {isGuest && !isErrorModal && !modalShowUpgradeCTA && currentRecordId
                && currentTranscriptionData && !isTranscriptEmpty(currentTranscriptionData) && !currentTranscriptionData.is_preview && (
                  <GuestSavePrompt compact key={currentRecordId} recordId={currentRecordId} location="completed_transcript"
                    onSave={details => setGuestAccountDialog({ ...details, filename: modalTitle, purpose: 'save' })} />
                )}
            </div>
          </div>
        </div>
      )}

      {guestAccountDialog && <GuestAccountDialog {...guestAccountDialog} onClose={() => setGuestAccountDialog(null)} onComplete={() => {
        const intent = guestAccountDialog;
        setGuestAccountDialog(null);
        if (intent.purpose === 'premium') navigate(intent.redirectPath);
        else toast(conversionCopy.saved);
      }} />}

      {showBulkDownloadModal && (
        <div className="modal-overlay" onClick={() => !bulkDownloading && setShowBulkDownloadModal(false)}>
          <div className="modal-container bulk-download-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title-area">
                <h3>{t("overview.bulkDownload")}</h3>
                <div className="bulk-download-count">
                  {t("overview.bulkCompletedCount", { count: completedBulkCount })}
                </div>
              </div>
              <div className="modal-controls">
                <button
                  className="icon-button"
                  type="button"
                  onClick={() => setShowBulkDownloadModal(false)}
                  disabled={bulkDownloading}
                  aria-label={t("common.close")}
                >
                  ×
                </button>
              </div>
            </div>

            <div className="modal-content bulk-download-content">
              {bulkDownloadError && <div className="bulk-download-error">{bulkDownloadError}</div>}
              {bulkProgress && <div className="bulk-download-progress">{bulkProgress}</div>}
              <div className="bulk-download-grid">
                <label className="bulk-download-field">
                  {t("overview.bulkFormat")}
                  <select
                    value={bulkFormat}
                    onChange={(e) => setBulkFormat(e.target.value)}
                    disabled={bulkDownloading}
                  >
                    <option value="txt">TXT</option>
                    <option value="docx">DOCX</option>
                    <option value="pdf">PDF</option>
                    <option value="srt">SRT</option>
                    <option value="vtt">VTT</option>
                  </select>
                </label>

                <label className="modal-toggle">
                  <input
                    type="checkbox"
                    checked={bulkShowSpeakers}
                    onChange={(e) => setBulkShowSpeakers(e.target.checked)}
                    disabled={bulkDownloading}
                  />
                  {t("overview.showSpeakers")}
                </label>

                <label className="modal-toggle">
                  <input
                    type="checkbox"
                    checked={bulkShowTime}
                    onChange={(e) => setBulkShowTime(e.target.checked)}
                    disabled={bulkDownloading || !bulkFormatUsesTimeOption}
                  />
                  {t("overview.showTimestamps")}
                </label>

                <label className="modal-toggle">
                  <input
                    type="checkbox"
                    checked={bulkUsePostprocessing}
                    onChange={(e) => setBulkUsePostprocessing(e.target.checked)}
                    disabled={bulkDownloading || !bulkShowSpeakers}
                  />
                  {t("overview.useEnhancedSpeakers")}
                </label>

                <label className="modal-toggle">
                  <input
                    type="checkbox"
                    checked={bulkIncludeSummaries}
                    onChange={(e) => setBulkIncludeSummaries(e.target.checked)}
                    disabled={bulkDownloading}
                  />
                  {t("overview.bulkIncludeSummaries")}
                </label>
              </div>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="secondary-button"
                onClick={() => setShowBulkDownloadModal(false)}
                disabled={bulkDownloading}
              >
                {t("common.cancel")}
              </button>
              <button
                type="button"
                className="primary-button"
                onClick={handleBulkDownload}
                disabled={bulkDownloading || completedBulkCount < 2}
              >
                <FiDownload />
                {bulkDownloading ? t("overview.bulkPreparingShort") : t("overview.bulkDownloadZip")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* GLOBAL DND OVERLAY */}
      {isDragging && (
        <div
          className="global-dnd-overlay"
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
        >
          <div className="global-dnd-box">
            <FiUpload className="global-dnd-icon" />
            <div className="global-dnd-title">{t("overview.dropFiles")}</div>
            <div className="global-dnd-sub">{t("overview.dropSub")}</div>
          </div>
        </div>
      )}

      {/* TOAST */}
      {showToast && (
        <div className={`toast-message${showModal ? " toast-message--transcript" : ""}`} role="status">
          <FiCheckCircle />
          <span>{toastMessage}</span>
        </div>
      )}

      {/* UPLOAD POPUP */}
      <UploadPopup
        onUploadInstead={() => { setShowUploadPopup(false); handleClickUpload(); }}
        focused={studio}
        isVisible={showUploadPopup}
        onClose={handleUploadPopupClose}
        onSubmit={handleUploadPopupSubmit}
        file={currentUploadFile}
        files={uploadItems.length ? uploadItems.map(item => item.file) : null}
        uploadItems={uploadItems}
        isUploading={isUploading}
        uploadProgress={uploadPct}
        uploadPreparation={uploadPreparation}
        uploadConnection={uploadConnection}
        jobId={currentJobId}
        mode={recordingMode}
        isRecording={isRecording}
        onRecordingStateChange={setIsRecording}
        isPremium={isPremium}
        onUpgrade={(interval) => handlePremiumSubscription("upload_popup_upgrade_bar", interval)}
        autoStartRecording={autoStartRecording}
        recordingInput={recordingInput}   // 'mic' | 'tab' | 'tab-mic'
        onDone={handlePodcastDone}
        signupFirst={!currentUser || isGuest}
        onSavedImport={async (payload,status) => {
          const {data:{session}} = await supabase.auth.getSession();
          if (!session || session.user?.id!==currentUserId) return;
          resetUploadPopupState();
          await applyImportResult({response:{status,ok:status<400},payload},
            {session,inputMethod:'podcast',url:''});
          await fetchHistory();
        }}
      />
    </AppErrorBoundary>
  );
}


export default Overview;
